/**
 * Database Editor & Modifier
 *
 * DatabaseEditor: wrapper with View/Edit toggle toolbar
 * DatabaseModifier: inner surface with table browser, SQL editor, AG Grid
 *
 * Features:
 * - Cell editing with optimistic locking + conflict popover
 * - Live refresh via file events (updates non-editing cells)
 * - SQL editor with highlight.js syntax highlighting
 * - Schema tree sidebar with column types + foreign keys
 * - Pagination controls
 * - Query history
 * - Export results (CSV/JSON)
 */
import { useTranslation } from '@papan/i18n'
import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle, useId } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { createPortal } from 'react-dom'
import {
  Loader2, Play, Table2, AlertTriangle, Lock, Wifi, WifiOff, Eraser, Filter, Plus, Braces,
  ChevronLeft, ChevronRight, ChevronDown, Download, History, X,
  Eye, Pencil, Key, Check, Copy, CopyPlus, Trash2, MoreVertical, Save,
} from 'lucide-react'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
} from 'ag-grid-community'
import { addToHistory, loadHistory, type HistoryEntry } from './sql-history.js'
import { useTheme, getThemeClass, useConfirm, useToastOptional, useCommandRegistryOptional, useFocusScope, useEditorCovered, useDraftState, useDraftInFlight, isDraftInFlight, useDraftScope, peekSwapStash, claimSwapStash, stashForSwap, onLayoutSwapStart, onLayoutSwapEnd, PasswordInput, formatShortcut, type Command, workspaceIdOf, Tooltip } from '@papan/core-ui/ui'
import { editorActionIsMine, streamPreviewIsMine } from './db-editor-routing.js'
import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'
import { sqliteAdapter, DB_VERSION_UNSAVED_EVENT, type DbVersionUnsavedReason, type DbAdapter, type ColumnInfo, type ForeignKey, type TableInfo, type QueryResult, type TableFilter, type CellConflict, type FilterOperator } from '../client/db-adapter.js'
import { useRealtime, useOnlineRecovery, DB_OPERATION_ENDED_EVENT } from '@papan/realtime/client'
import type { DbOperationEnded } from '@papan/types'
import { dbOperationNoticeKey, dbOperationNoticeTone } from '../client/db-operation-notice.js'
import { classifyRowDelete, staleDeleteMessage } from './rowDeleteOutcome.js'
import { useGridEdits, type GridEditsHandle } from './useGridEdits.js'
import hljs from 'highlight.js/lib/core'
import sql from 'highlight.js/lib/languages/sql'
import { EditorToolbar } from './EditorToolbar.js'
import { ModeToggle, type ModeOption } from './ModeToggle.js'
import { ToolButton, ToolToggle } from './ToolbarControls.js'
import { editCellKey, editKeyRowid, isEditKeyOfRow } from './db-edit-keys.js'
import { withoutFilterColumns } from './db-grid-filters.js'
import { errorText } from '@papan/api-client'
hljs.registerLanguage('sql', sql)

ModuleRegistry.registerModules([AllCommunityModule])

const editableTheme = themeQuartz.withParams({
  backgroundColor: '#1c1c1e',
  foregroundColor: '#e4e4e7',
  headerBackgroundColor: '#252528',
  headerTextColor: '#a1a1aa',
  borderColor: 'rgba(255,255,255,0.08)',
  columnBorder: { style: 'solid', width: 1, color: 'rgba(255,255,255,0.07)' },
  rowHoverColor: 'rgba(255,255,255,0.04)',
  selectedRowBackgroundColor: 'rgba(255,255,255,0.08)',
  cellTextColor: '#e4e4e7',
  oddRowBackgroundColor: '#1c1c1e',
  wrapperBorderRadius: 0,
  headerFontSize: 13,
  fontSize: 13,
})

const readonlyTheme = themeQuartz.withParams({
  backgroundColor: '#2a2a2e',
  foregroundColor: '#d4d4d8',
  headerBackgroundColor: '#303035',
  headerTextColor: '#a1a1aa',
  borderColor: 'rgba(255,255,255,0.07)',
  columnBorder: { style: 'solid', width: 1, color: 'rgba(255,255,255,0.06)' },
  rowHoverColor: 'rgba(255,255,255,0.04)',
  selectedRowBackgroundColor: 'rgba(255,255,255,0.07)',
  cellTextColor: '#d4d4d8',
  oddRowBackgroundColor: '#2a2a2e',
  wrapperBorderRadius: 0,
  headerFontSize: 13,
  fontSize: 13,
})

// Types imported from ../client/db-adapter.js

const FILTER_OPERATORS: { value: FilterOperator; label: string; needsValue: boolean }[] = [
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'not_equals', label: 'not equals', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'starts_with', label: 'starts with', needsValue: true },
  { value: 'ends_with', label: 'ends with', needsValue: true },
  { value: 'greater_than', label: '>', needsValue: true },
  { value: 'less_than', label: '<', needsValue: true },
  { value: 'greater_equal', label: '>=', needsValue: true },
  { value: 'less_equal', label: '<=', needsValue: true },
  { value: 'is_empty', label: 'is empty', needsValue: false },
  { value: 'is_not_empty', label: 'is not empty', needsValue: false },
]

/** Convert one filter to a SQL clause */
function filterToClause(f: TableFilter): string {
  const col = `"${f.column.replace(/"/g, '""')}"`
  const val = f.value.replace(/'/g, "''")
  switch (f.operator) {
    case 'equals': return `${col} = '${val}'`
    case 'not_equals': return `${col} != '${val}'`
    case 'contains': return `${col} LIKE '%${val}%'`
    case 'starts_with': return `${col} LIKE '${val}%'`
    case 'ends_with': return `${col} LIKE '%${val}'`
    case 'greater_than': return `${col} > '${val}'`
    case 'less_than': return `${col} < '${val}'`
    case 'greater_equal': return `${col} >= '${val}'`
    case 'less_equal': return `${col} <= '${val}'`
    case 'is_empty': return `(${col} IS NULL OR ${col} = '')`
    case 'is_not_empty': return `(${col} IS NOT NULL AND ${col} != '')`
    default: return ''
  }
}

/** Convert structured filters to SQL WHERE clause with AND/OR grouping.
 *  Groups consecutive AND clauses, OR breaks groups:
 *  A AND B OR C AND D → (A AND B) OR (C AND D) */
function filtersToWhere(filters: TableFilter[]): string {
  if (filters.length === 0) return ''
  // Split into OR-separated groups of AND-connected clauses
  const groups: string[][] = [[]]
  for (const f of filters) {
    const clause = filterToClause(f)
    if (!clause) continue
    if (f.logic === 'or') {
      groups.push([clause])
    } else {
      groups[groups.length - 1].push(clause)
    }
  }
  const parts = groups
    .map(g => g.length > 1 ? `(${g.join(' AND ')})` : g[0])
    .filter(Boolean)
  return parts.length > 0 ? ` WHERE ${parts.join(' OR ')}` : ''
}

let _filterId = 0
function genFilterId(): string { return `f${++_filterId}` }

// API adapter is passed as prop — see ../client/db-adapter.ts

// ---------------------------------------------------------------------------
// SQL Highlight helper
// ---------------------------------------------------------------------------

function highlightSQL(code: string): string {
  try {
    return hljs.highlight(code, { language: 'sql' }).value
  } catch {
    return code.replace(/&/g, '&amp;').replace(/</g, '&lt;')
  }
}

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------

async function buildParquetBlob(columns: string[], types: string[] | undefined, rows: Record<string, unknown>[]): Promise<Blob> {
  const { parquetWriteBuffer } = await import('hyparquet-writer')
  if (rows.length === 0) {
    // Empty result — produce valid empty Parquet
    const columnData = columns.map(name => ({ name: name.replace(/[^\w]/g, '_'), data: [] }))
    const buffer = parquetWriteBuffer({ columnData, codec: 'SNAPPY' })
    return new Blob([buffer], { type: 'application/octet-stream' })
  }
  // Build columnar data from row objects
  const columnData = columns.map((name, i) => {
    const rawData = rows.map(r => r[name] ?? null)
    const sqlType = (types?.[i] || '').toUpperCase()
    const safeName = name.replace(/[^\w]/g, '_')
    // Check if column has any nulls — if so, avoid typed arrays (null handling issues)
    const hasNulls = rawData.some(v => v === null || v === undefined)
    if (hasNulls) {
      // Let hyparquet auto-detect type — handles nulls gracefully
      return { name: safeName, data: rawData }
    }
    // Map SQL types to hyparquet types for non-null columns
    let type: 'INT32' | 'DOUBLE' | 'BOOLEAN' | undefined
    if (sqlType.includes('INT') || sqlType === 'SMALLINT') type = 'INT32'
    else if (sqlType.includes('REAL') || sqlType.includes('FLOAT') || sqlType.includes('DOUBLE') || sqlType.includes('NUMERIC') || sqlType.includes('DECIMAL')) type = 'DOUBLE'
    else if (sqlType.includes('BOOL')) type = 'BOOLEAN'
    return { name: safeName, data: rawData, ...(type ? { type } : {}) }
  })
  const buffer = parquetWriteBuffer({ columnData, codec: 'SNAPPY' })
  return new Blob([buffer], { type: 'application/octet-stream' })
}

function buildCsvBlob(columns: string[], rows: Record<string, unknown>[]): Blob {
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return ''
    // Handle objects (shouldn't reach here but safety)
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v)
    return s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r') ? `"${s.replace(/"/g, '""')}"` : s
  }
  // BOM for Excel UTF-8 compatibility
  const lines = [columns.map(escape).join(',')]
  for (const row of rows) {
    lines.push(columns.map(c => escape(row[c])).join(','))
  }
  return new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' })
}

function buildJsonBlob(rows: Record<string, unknown>[]): Blob {
  return new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' })
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

/** Export dropdown — Download or Save to Drive for CSV/JSON/Parquet */
function ExportDropdown({ columns, rows, types, onExportToDrive, parentId, baseName = 'export', onExportFullTable }: {
  columns: string[]
  rows: Record<string, unknown>[]
  types?: string[]
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  parentId?: string | null
  baseName?: string
  /** Server-side full table export — if provided, "Full table" section is shown */
  onExportFullTable?: (format: 'csv' | 'json' | 'parquet') => Promise<Blob>
}) {
  const [open, setOpen] = useState(false)
  const [parquetLoading, setParquetLoading] = useState(false)
  const [fullTableLoading, setFullTableLoading] = useState<string | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', handler, true)
    return () => window.removeEventListener('mousedown', handler, true)
  }, [open])

  return (
    <div className="relative">
      <Tooltip always placement="top" className="flex items-center" content="Export">
        <button
          ref={btnRef}
          onClick={() => setOpen(o => !o)}
          className="themed-btn-secondary text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5"
        >
          <Download size={13} />
        </button>
      </Tooltip>
      {open && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full mt-1 themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[220px] rounded"
        >
          <button
            onClick={() => { downloadBlob(buildCsvBlob(columns, rows), `${baseName}.csv`); setOpen(false) }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            <Download size={14} />
            Download <b className="themed-text">CSV</b>
          </button>
          <button
            onClick={() => { downloadBlob(buildJsonBlob(rows), `${baseName}.json`); setOpen(false) }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            <Download size={14} />
            Download <b className="themed-text">JSON</b>
          </button>
          <button
            disabled={parquetLoading}
            onClick={async () => {
              setParquetLoading(true)
              try {
                const blob = await buildParquetBlob(columns, types, rows)
                downloadBlob(blob, `${baseName}.parquet`)
              } catch (err: any) {
                console.error('[Export] Parquet failed:', err)
              } finally {
                setParquetLoading(false)
                setOpen(false)
              }
            }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            {parquetLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download <b className="themed-text">Parquet</b>
          </button>
          {onExportToDrive && (
            <>
              <div className="my-1 border-t themed-border" />
              <button
                onClick={() => { onExportToDrive(buildCsvBlob(columns, rows), `${baseName}.csv`, parentId, 'Save CSV to Drive'); setOpen(false) }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                <Save size={14} />
                Save <b className="themed-text">CSV</b> to Drive
              </button>
              <button
                onClick={() => { onExportToDrive(buildJsonBlob(rows), `${baseName}.json`, parentId, 'Save JSON to Drive'); setOpen(false) }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                <Save size={14} />
                Save <b className="themed-text">JSON</b> to Drive
              </button>
              <button
                disabled={parquetLoading}
                onClick={async () => {
                  setParquetLoading(true)
                  try {
                    const blob = await buildParquetBlob(columns, types, rows)
                    onExportToDrive(blob, `${baseName}.parquet`, parentId, 'Save Parquet to Drive')
                  } catch (err: any) {
                    console.error('[Export] Parquet failed:', err)
                  } finally {
                    setParquetLoading(false)
                    setOpen(false)
                  }
                }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                {parquetLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save <b className="themed-text">Parquet</b> to Drive
              </button>
            </>
          )}
          {onExportFullTable && (
            <>
              <div className="my-1 border-t themed-border" />
              <div className="px-3 py-1 text-[10px] uppercase themed-text-muted font-medium tracking-wider">Full table</div>
              {(['csv', 'json', 'parquet'] as const).map(fmt => (
                <button
                  key={`dl-${fmt}`}
                  disabled={fullTableLoading !== null}
                  onClick={async () => {
                    setFullTableLoading(`dl-${fmt}`)
                    try {
                      const blob = await onExportFullTable(fmt)
                      downloadBlob(blob, `${baseName}_full.${fmt}`)
                    } catch (err: any) {
                      console.error(`[Export] Full table ${fmt} failed:`, err)
                    } finally {
                      setFullTableLoading(null)
                      setOpen(false)
                    }
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                >
                  {fullTableLoading === `dl-${fmt}` ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  Download <b className="themed-text">{fmt.toUpperCase()}</b>
                </button>
              ))}
              {onExportToDrive && (
                <>
                  <div className="my-1 border-t themed-border" />
                  {(['csv', 'json', 'parquet'] as const).map(fmt => (
                    <button
                      key={`save-${fmt}`}
                      disabled={fullTableLoading !== null}
                      onClick={async () => {
                        setFullTableLoading(`save-${fmt}`)
                        try {
                          const blob = await onExportFullTable(fmt)
                          onExportToDrive(blob, `${baseName}_full.${fmt}`, parentId, `Save ${fmt.toUpperCase()} to Drive`)
                        } catch (err: any) {
                          console.error(`[Export] Full table ${fmt} save failed:`, err)
                        } finally {
                          setFullTableLoading(null)
                          setOpen(false)
                        }
                      }}
                      className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                    >
                      {fullTableLoading === `save-${fmt}` ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                      Save <b className="themed-text">{fmt.toUpperCase()}</b> to Drive
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared hook: load schema for a database file
// ---------------------------------------------------------------------------

/**
 * The grid's command scope. A VERSION PREVIEW of the file is its own scope, parented on the workspace rather than the
 * live editor: it shares the live grid's file id, and with one id the registry kept whichever registered last and the
 * first to unmount deleted the other's — so closing a preview left the live grid with no commands. And under
 * 'editor' its read-only Copy commands were greyed by the very cover its overlay puts on the live editor (#929).
 */
function dbGridScope(fileId: string, versionId?: string): { scopeId: string; parentScopeId: string } {
  return versionId
    ? { scopeId: `editor:db:grid:${fileId}@${versionId}`, parentScopeId: 'workspace' }
    : { scopeId: `editor:db:grid:${fileId}`, parentScopeId: 'editor' }
}

function useDbSchema(fileId: string, adapter: DbAdapter, versionId?: string, authenticated?: boolean) {
  const recovery = useOnlineRecovery()
  const [loaded, setLoaded] = useState<{ key: string; schema: TableInfo[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Skip fetch if adapter requires auth but not authenticated yet
  const canFetch = !adapter.requiresAuth || !!authenticated

  /**
   * WHAT the held schema describes.
   *
   * Keeping a schema across a refresh is the whole point of this hook, but it
   * must only be kept for the SAME thing. Holding it unconditionally meant a
   * Firebird Logout → different user sign-in rendered the editor immediately
   * against the previous user's table list: `initialLoading` was false
   * because a schema existed, so the grid mounted on a table the new user may
   * have no grant on.
   *
   * The key covers file and version. IDENTITY is not in it and cannot be —
   * `authenticated` is a boolean, so "user A signed in" and "user B signed
   * in" produce the same string. (That was this fix's first attempt, and it
   * did not work: sign out, sign in as someone else, and the key comes back
   * to exactly what it was.) Identity is handled by dropping the schema the
   * moment the session ends, below.
   */
  const key = `${fileId}:${versionId ?? ''}`
  const schema = loaded && loaded.key === key ? loaded.schema : null

  /**
   * TWO counters, because two different things are being ordered and one
   * counter doing both jobs hangs the editor.
   *
   * `writeSeq` decides WHO MAY WRITE THE SCHEMA. Everything that fetches one
   * bumps it — this effect, `refreshSchema`, and the sign-out branch — and a
   * response may only land if its stamp is still the newest. Without it, a
   * `file:updated` refresh and a reconnect refetch race, and the older answer
   * can win: a pre-DDL table list back in the sidebar after the post-DDL one
   * had already arrived.
   *
   * `loadSeq` decides WHO OWNS THE `loading` FLAG, and only this effect ever
   * bumps it. One shared counter looked simpler and was wrong: a
   * `refreshSchema` call during a refetch superseded the effect's own
   * response, so neither its `.then` nor its `.catch` ran — and `refreshSchema`
   * has no `loading` of its own to put back. The flag stuck on for the rest of
   * the session, leaving the connection dot spinning "Connecting…" over a
   * perfectly working editor, and in any future spot where `schema` is still
   * null it would be a permanent full-pane spinner instead.
   */
  const writeSeq = useRef(0)
  const loadSeq = useRef(0)
  // #1316: a schema load whose answer was dropped (its session ended) is asked again as the session now here. Review
  // round 2: in the share view it waits until a moved share session lands (`useDroppedLoadReask`), never as the ended one
  const { reask: schemaReask, begin: beginSchemaLoad } = useDroppedLoadReask()

  useEffect(() => {
    if (!canFetch) {
      // The session ended. The schema belonged to it, so it goes too —
      // otherwise the next person to sign in at this same dialog sees the
      // previous one's tables before their own fetch has answered.
      //
      // The bump is what makes that stick. Logging out while a fetch is in
      // flight and NOT bumping meant the old response still counted as
      // current, so it put the previous user's tables straight back and the
      // next person walked into them — which is the whole failure this is
      // here to prevent.
      writeSeq.current++
      loadSeq.current++
      setLoaded(null)
      setError(null)
      setLoading(false)
      return
    }
    const write = ++writeSeq.current
    const load = ++loadSeq.current
    const asked = beginSchemaLoad()   // the share session this load goes out as
    setLoading(true)
    setError(null)
    adapter.getSchema(fileId, { versionId })
      .then((res: { schema: TableInfo[] }) => {
        if (write === writeSeq.current) setLoaded({ key, schema: res.schema })
        if (load === loadSeq.current) setLoading(false)
      })
      .catch((err: any) => {
        const text = errorText(err, 'Failed to load database')
        if (text === null) { if (load === loadSeq.current) asked.dropped(); return }
        if (write === writeSeq.current) setError(text)
        if (load === loadSeq.current) setLoading(false)
      })
  }, [fileId, versionId, recovery, adapter, canFetch, authenticated, key, schemaReask, beginSchemaLoad])

  const refreshSchema = useCallback(() => {
    // Bumps `writeSeq` and NOT `loadSeq`: it competes for the right to write
    // the schema, but it never set `loading` and so has no business clearing
    // it. Taking both was the bug — see the note on the two counters.
    const write = ++writeSeq.current
    adapter.getSchema(fileId, { versionId })
      .then((res: { schema: TableInfo[] }) => {
        if (write !== writeSeq.current) return
        setLoaded({ key, schema: res.schema })
        // Clears the error as well as setting the schema. Nothing else does:
        // only the effect resets `error`, and it re-runs on file, version,
        // recovery or sign-in — so one failed wake-from-sleep refetch left
        // the connection dot reading "Error" and the version preview's amber
        // "could not refresh" strip on screen for the rest of the session,
        // over an editor that had been working again for an hour. Keeping
        // the old schema is this branch's fix; never taking the sign down
        // afterwards was the half of it that was missing.
        setError(null)
      })
      .catch((err: unknown) => {
        // Not surfaced: a background refresh that fails leaves the schema it
        // already had, which is the point, and the effect's own path owns the
        // visible error state. Logged rather than swallowed in silence.
        console.warn('[db] schema refresh failed', err)
      })
  }, [fileId, versionId, adapter, key])

  return {
    schema,
    loading,
    error,
    refreshSchema,
    /**
     * A load with NOTHING to show yet — the only state that justifies
     * replacing the editor with a spinner.
     *
     * `loading` alone does not: this effect re-runs on `useOnlineRecovery`,
     * so a Wi-Fi blink or waking the laptop set it, the editor was swapped
     * for a spinner, and both panes UNMOUNTED. Everything they held went with
     * them — the table you were on (back to the first one), the rows marked
     * for deletion, the rows added but not saved, the cell you were typing
     * in — and the grid then announced that unsaved changes had been
     * discarded "when the table changed", which the user had not done.
     *
     * A refresh that already has a schema to fall back on happens behind the
     * screen. The connection dot keeps showing `loading`, which is the honest
     * place for it.
     */
    initialLoading: loading && !schema,
  }
}

// ---------------------------------------------------------------------------
// DatabaseTableModifier — View/Edit modes (table browser + grid)
// ---------------------------------------------------------------------------

/** Handle exposed by DatabaseTableModifier for Edit menu integration */
interface DatabaseTableHandle {
  copy: () => void
  delete: () => void
  /** Get current editor state for AI inspector */
  getState?: () => {
    table: string
    columns: string[]
    where: string
    limit: number
    page: number
    selectedRange?: { startRow: number; startCol: number; endRow: number; endCol: number }
    /** What is on screen but NOT in the database yet — see useGridEdits. */
    staged: { deleteRowIds: number[]; insertCount: number }
  }
  /** Set visible columns programmatically */
  setColumns?: (columns: string[]) => void
}

interface DatabaseTableModifierProps {
  fileId: string
  fileName?: string
  schema: TableInfo[]
  editable: boolean
  versionId?: string
  refreshSchema: () => void
  onSendToSql?: (sql: string) => void
  filterOpen?: boolean
  advancedFilter?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.RefObject<(() => void) | null>
  onDiscardRef?: React.RefObject<(() => void) | null>
  sqlMode?: boolean
  parentId?: string | null
  canCopy?: boolean
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  /** Shared view sync state — syncs filters, selected table, etc. between editor and version preview */
  viewSync?: import('./viewSync.js').ViewSyncRef
  viewSyncId?: string
  adapter: DbAdapter
  onRequestEdit?: () => void
  /** Everything the user has changed that has NOT been sent — rows marked for
   *  deletion, rows added. Owned by the outer DatabaseEditor, because this
   *  component remounts on a collab reconnect and a staged set living inside
   *  it would go with it.
   *
   *  The reason used to be `useDbSchema` flipping `loading`, which both
   *  modifiers were gated on — that is the teardown this branch removed, and
   *  they are gated on `initialLoading` now. The hoist is still right: the
   *  grid remounts for other reasons (the realtime session key), and the
   *  staged set has to outlive all of them. */
  edits: GridEditsHandle
  /** #897 — where this grid's unsaved cells are kept across a phone/desktop
   *  layout swap (`db:<place>:<file>:<version>`); absent = not kept. */
  draftKey?: string | null
  /** The grid's tab is the one on screen (default true). Its own Cmd+S listener answers only then — see below. */
  isActive?: boolean
  /** #900 — the grid's keyboard REGION when it is wider than the grid: the database editor around it (its toolbar),
   *  so Cmd+C after a toolbar click still copies the selection. Absent = the grid itself (the version preview has no
   *  toolbar). Safe in SQL mode: the grid's commands are disabled there. */
  keyboardRegion?: HTMLElement | null
}

/** What the grid snapshots when a layout swap unmounts it (#897): the typed
 *  cells and the open edit rows, and the VIEW they sit in — an edit row only
 *  renders for a row on the current page and filter, so restoring the cells
 *  without the page would hide them. */
interface GridSwapStash {
  table: string
  page: number
  limit: number
  appliedFilters: TableFilter[]
  appliedRawWhere: string
  selectedColumns: string[]
  cells: Array<[string, { column: string; value: string; dirty: boolean; isNull: boolean }]>
  rowIds: number[]
  heights: Array<[number, number]>
}

/** Thrown to skip the rest of a save whose table is no longer on screen,
 *  while still letting the cleanup after the try/catch run. */
class StaleTableSave extends Error {}

/**
 * Table renames seen by the grids at one editor place (#897), `old → new`,
 * kept beside the drafts so a Save handed over from a grid a layout swap
 * replaced — logged under the name it had then — still finds its table after
 * a rename on the new grid. Keyed by the Save's in-flight key (file, version,
 * place); written only by a rename while a Save is out, emptied when a Save
 * starts, and folded into its log (then emptied) as it ends.
 */
const tableRenames = new Map<string, Map<string, string>>()
function recordTableRename(place: string, from: string, to: string): void {
  let m = tableRenames.get(place)
  if (!m) { m = new Map(); tableRenames.set(place, m) }
  for (const [k, v] of m) if (v === from) m.set(k, to)
  m.set(from, to)
}
function currentTableName(place: string, table: string): string {
  return tableRenames.get(place)?.get(table) ?? table
}

/** What one Save sent (#897): the table, the staged deletes and inserts it
 *  took out, and the cells it wrote (`rowid:column` + the value sent). */
interface SavePurged {
  table: string
  deleteRowIds: number[]
  insertTempIds: string[]
  savedCells: { key: string; value: unknown; isNull: boolean }[]
}

/** What a grid read covers, as far as WHICH ROWS come back. */
interface ReadWindow { table: string; page: number; limit: number; where: string }
function readWindow(table: string, page: number, limit: number, filters: TableFilter[], rawWhere: string): ReadWindow {
  return { table, page, limit, where: JSON.stringify([filters, rawWhere]) }
}
function sameReadWindow(a: ReadWindow, b: ReadWindow): boolean {
  return a.table === b.table && a.page === b.page && a.limit === b.limit && a.where === b.where
}

const DatabaseTableModifier = forwardRef<DatabaseTableHandle, DatabaseTableModifierProps>(function DatabaseTableModifier({ fileId, fileName, schema, editable, versionId, refreshSchema, onSendToSql, filterOpen: filterOpenProp, advancedFilter: advancedFilterProp, onDirtyChange, onSaveRef, onDiscardRef, sqlMode, parentId, canCopy = true, onExportToDrive, viewSync, viewSyncId, adapter, onRequestEdit, edits, draftKey = null, isActive = true, keyboardRegion }, ref) {
  const { onFileEvent } = useRealtime()
  // Reconnect revalidation. The effect that uses this is further down, next
  // to `loadTable`'s own — NOT in `loadTable`'s deps, deliberately: only a
  // revalidating load closes cell editors, and making every paging click one
  // threw away what the user had typed. See that effect.
  const recovery = useOnlineRecovery()
  // CommandRegistryProvider (e.g., share view chrome). Command
  // registration is a no-op in that case — the editor still browses
  // schema + tables and runs queries; only the global keyboard-shortcut
  // surface is unavailable, which is fine for read-only share recipients.
  const cmdRegistry = useCommandRegistryOptional()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Read initial state from viewSync (for version preview inheriting main editor's filters)
  const _vs = viewSync && viewSyncId ? viewSync[viewSyncId] : undefined
  // #897 — a grid a layout swap unmounted left its unsaved cells and their view
  // here. Read while building the FIRST render (every initializer below starts
  // from it), so the table-change effect sees no change and the staged rows'
  // scope matches; claimed after mount. A table that is gone takes the cells
  // with it (reported once the toast host is known, below).
  const stashKey = draftKey ? `${draftKey}:grid` : null
  const [restored] = useState<GridSwapStash | null>(() => {
    const st = stashKey ? peekSwapStash<GridSwapStash>(stashKey) : undefined
    return st && schema.some(t => t.name === st.table) ? st : null
  })
  const [restoreLost] = useState(() => !!(stashKey && peekSwapStash<GridSwapStash>(stashKey) && !restored))
  const [selectedTable, setSelectedTable] = useState<string>(() => {
    if (restored) return restored.table
    const synced = _vs?.selectedTable as string | undefined
    return synced && schema.some(t => t.name === synced) ? synced : schema[0]?.name || ''
  })
  const [schemaOpen, setSchemaOpen] = useState(() => typeof _vs?.schemaOpen === 'boolean' ? _vs.schemaOpen : true)
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<QueryResult | null>(null)
  const [queryLoading, setQueryLoading] = useState(false)
  const [statusMsg, setStatusMsg] = useState('')
  const { t } = useTranslation('collab')
  /**
   * Applied filters the grid had to drop because their column left the table
   * (#899, D1) — said on the status line, since fewer filters show MORE rows
   * and nothing else on screen would explain why. Column-subset removals stay
   * silent: they only stop showing a column that is gone. Cleared when the
   * user (or the AI) applies or clears filters, or the table changes.
   */
  const [droppedFilterColumns, setDroppedFilterColumns] = useState<string[]>([])
  const [conflict, setConflict] = useState<CellConflict | null>(null)
  const confirm = useConfirm()
  // Optional: this grid also mounts under hosts with no ToastProvider
  // (isolated tests, preview shells), where a refused delete is reported by
  // the row simply staying put.
  const toast = useToastOptional()
  const pendingDeleteRowIds = edits.deleteRowIds
  const pendingInserts = edits.inserts
  const editsRef = useRef(edits)
  editsRef.current = edits
  // schemaBanner removed — unified diff auto-reloads on schema changes
  // Column selection: empty = SELECT *, otherwise SELECT specific columns
  const [draftColumns, setDraftColumns] = useState<string[]>(() => restored ? restored.selectedColumns : Array.isArray(_vs?.selectedColumns) ? _vs.selectedColumns as string[] : [])
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => restored ? restored.selectedColumns : Array.isArray(_vs?.selectedColumns) ? _vs.selectedColumns as string[] : [])
  const selectedColumnsRef = useRef(selectedColumns)
  selectedColumnsRef.current = selectedColumns
  const [colInput, setColInput] = useState('')
  const [colInputFocused, setColInputFocused] = useState(false)
  const [colHighlight, setColHighlight] = useState(0)
  const [draggingCol, setDraggingCol] = useState<string | null>(null)
  const dragColRef = useRef<string | null>(null)
  const gridRef = useRef<AgGridReact>(null)
  const flashRowsRef = useRef<Map<string, 'updated' | 'added' | 'deleted'>>(new Map())
  const pendingEditCellRef = useRef<{ rowid: number | string; column: string } | null>(null)

  // Edit row state
  interface EditCell { column: string; value: string; dirty: boolean; isNull: boolean }
  // Multi-row editing: keys are "rowid:column" for cells/errors
  const [editRowIds, setEditRowIds] = useState<Set<number>>(() => new Set(restored?.rowIds ?? []))
  const editRowIdsRef = useRef(editRowIds)
  editRowIdsRef.current = editRowIds
  // Stable ref for postSortRows — AG Grid breaks double-click if this prop changes reference
  const postSortRowsRef = useRef((params: any) => {
    if (editRowIdsRef.current.size === 0) return
    const rows = params.nodes
    const editNodes: Array<[string, any]> = []
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].data?.__editRow) {
        editNodes.push([String(rows[i].data.__sourceRowId), rows[i]])
        rows.splice(i, 1)
      }
    }
    if (editNodes.length === 0) return
    const editMap = new Map(editNodes)
    for (let i = rows.length - 1; i >= 0; i--) {
      const editNode = editMap.get(String(rows[i].data?.__rowid))
      if (editNode) rows.splice(i + 1, 0, editNode)
    }
  })
  const [editCells, setEditCells] = useState<Map<string, EditCell>>(() => new Map(restored?.cells ?? []))
  const [editSaving, setEditSaving] = useState(false)
  const [editErrors, setEditErrors] = useState<Map<string, string>>(new Map())
  // editCellsRef is the source of truth for cell values (onInput updates it directly).
  // editCells state is only for dirty tracking via useEffect. Do NOT sync ref←state on render.
  const editCellsRef = useRef(editCells)
  const editErrorsRef = useRef(editErrors)
  const savingCellsRef = useRef<Set<string>>(new Set())
  const [editRowHeights, setEditRowHeights] = useState<Map<number, number>>(() => new Map(restored?.heights ?? []))
  const cornerDraggingRef = useRef(false)
  // Stored column pixel widths — prevents flex:1 from resetting manual resizes on re-render
  const columnWidthsRef = useRef(new Map<string, number>())
  // Bumped after sizeColumnsToFit captures widths — triggers columnDefs recomputation so AG Grid gets fixed widths
  const [colWidthsVersion, setColWidthsVersion] = useState(0)
  /** The table the rows currently on screen came from — lags `selectedTable`
   *  by one round trip, which is the point. */
  const [loadedTable, setLoadedTable] = useState('')
  // Result ref for closures that need current data without triggering memo recomputation
  const resultRef = useRef(result)
  resultRef.current = result

  // Cell selection for copy (click-drag)
  interface CellRange { startRow: number; startCol: number; endRow: number; endCol: number }
  const selectionRef = useRef<CellRange | null>(null)
  const [, setSelectionVersion] = useState(0) // bump to re-render cellClass
  const getCellRange = useCallback((): { rowStart: number; rowEnd: number; colStart: number; colEnd: number } | null => {
    const sel = selectionRef.current
    if (!sel) return null
    return {
      rowStart: Math.min(sel.startRow, sel.endRow),
      rowEnd: Math.max(sel.startRow, sel.endRow),
      colStart: Math.min(sel.startCol, sel.endCol),
      colEnd: Math.max(sel.startCol, sel.endCol),
    }
  }, [])

  // Track dirty state — any dirty cell means the document is "unsaved"
  const hasDirtyRef = useRef(false)
  useEffect(() => {
    // A row marked for deletion is unsaved work too — without this the tab
    // shows no unsaved marker and closing it would drop the marks silently.
    //
    // Cell values from the REF: it is the source of truth (typing writes it
    // directly), and `editCells` is only this effect's trigger. Reading the
    // state let a cell typed back to its original value count as dirty again
    // on the next re-run, where Save — which reads the ref — had nothing to
    // send and so never cleared it.
    const hasDirty = [...editCellsRef.current.values()].some(c => c.dirty) || pendingDeleteRowIds.size > 0 || pendingInserts.length > 0
    if (hasDirty !== hasDirtyRef.current) {
      hasDirtyRef.current = hasDirty
      onDirtyChange?.(hasDirty)
    }
  }, [editCells, pendingDeleteRowIds, pendingInserts, onDirtyChange])

  // Wire up save/discard refs for the workspace toolbar
  useEffect(() => {
    if (onSaveRef) onSaveRef.current = async () => {
      // One at a time. Save is reachable from the toolbar, the Edit menu and
      // Cmd+S, and the staged set is only cleared once the server answers —
      // so a second Save starting inside that window sends the same inserts
      // again and the table gets duplicate rows.
      // #897: and not while a Save started by the screen a layout swap
      // replaced is still running — its staged inserts came back with the
      // restored draft and are only purged once it lands.
      if (savingRef.current || (saveKeyRef.current && isDraftInFlight(saveKeyRef.current))) return
      savingRef.current = true
      let issue = ''
      // The log of what this Save sends — handed to a grid that replaces this
      // one mid-Save (#897) whether the Save ends well or throws (a flush that
      // fails AFTER inserts landed must still purge them there, or the next
      // Save inserts them twice). Bound to the table it saved.
      const log: SavePurged = { table: selectedTableRef.current, deleteRowIds: [], insertTempIds: [], savedCells: [] }
      savePurgeLogRef.current = log
      let failure: { error: unknown } | null = null
      // The rename map describes THIS Save only (one Save per place is out at
      // a time): started empty, folded into the log and dropped as it ends.
      const place = saveKeyRef.current
      if (place) tableRenames.delete(place)
      await trackSaveRef.current(async (): Promise<SavePurged> => { try {
      // Deletions first, and they can refuse. Backing out of the confirmation
      // stops the whole Save on purpose — the cell edits belong to the same
      // gesture — but it has to SAY so: silence made Save look like a dead
      // button, with the marks and the typed values all still sitting there.
      const deletesOk = await applyPendingDeletesRef.current?.()
      if (deletesOk === false) return log
      // A REFUSED insert does not stop the rest. The deletions have already
      // gone, so the save is partial whatever happens now, and stopping here
      // left the cell edits unsaveable behind one mistyped row. The refused
      // rows stay green and the message names them.
      insertIssueRef.current = ''
      await applyPendingInsertsRef.current?.()
      issue = insertIssueRef.current
      await saveAllEditCellsRef.current?.()
      // Flush to VFS — catches debounced edits not yet versioned
      await adapter.flush?.(fileId)
      return log
      } catch (error) {
        failure = { error }
        return log
      } finally {
        // The table under the name it has NOW (renames seen while this Save
        // was out): the hand-over compares it as it stands at settle time,
        // whether delivered at once or parked for a later grid.
        if (place) { log.table = currentTableName(place, log.table); tableRenames.delete(place) }
        savePurgeLogRef.current = null
        savingRef.current = false
        // LAST. `loadTable` and the refresh that follow a save both rewrite
        // the status line with a row count, so a message set while they were
        // still running never survived to be read.
        if (issue) setStatusMsg(issue)
      } })
      if (failure) throw (failure as { error: unknown }).error
    }
    if (onDiscardRef) onDiscardRef.current = () => {
      // Discard all edits — close all edit rows (edits are client-side only)
      // Only the EDIT rows here. The delete marks repaint themselves — the
      // effect on the marked set covers every route that changes it, this one
      // included.
      const oldRowIds = new Set(editRowIdsRef.current)
      edits.reset()
      editCellsRef.current = new Map()
      setEditCells(new Map())
      editErrorsRef.current = new Map()
      setEditRowIds(new Set())
      setEditRowHeights(new Map())
      hasDirtyRef.current = false
      onDirtyChange?.(false)
      // Cancel any pending debounced flush
      adapter.discard?.(fileId)
      // Redraw source rows to drop db-editing-source / db-pending-delete
      requestAnimationFrame(() => {
        const api = gridRef.current?.api
        if (!api) return
        const nodes = [...oldRowIds].map(id => api.getRowNode(String(id))).filter(Boolean)
        if (nodes.length) api.redrawRows({ rowNodes: nodes as any })
      })
    }
    return () => {
      if (onSaveRef) onSaveRef.current = null
      if (onDiscardRef) onDiscardRef.current = null
    }
  }, [onSaveRef, onDiscardRef, adapter, fileId])
  const savingRef = useRef(false)
  const saveKey = draftKey ? `${draftKey}:save` : null
  /** What a running Save took out of the staged set (sent deletes, landed
   *  inserts) — its result, for a grid that replaced this one mid-Save. */
  const savePurgeLogRef = useRef<SavePurged | null>(null)
  const logSavePurge = (g: Partial<Pick<SavePurged, 'deleteRowIds' | 'insertTempIds' | 'savedCells'>>) => {
    const log = savePurgeLogRef.current
    if (!log) return
    log.deleteRowIds.push(...(g.deleteRowIds ?? []))
    log.insertTempIds.push(...(g.insertTempIds ?? []))
    log.savedCells.push(...(g.savedCells ?? []))
  }
  // A Save that finished on the grid a layout swap replaced purged THAT grid's
  // staged set and closed THAT grid's saved cells; this one came back from the
  // draft with them all still in it — a second Save would insert the rows
  // twice and re-send the cells over anything changed since. Done here, only
  // on the table the Save was for — followed through renames, which keep the
  // staged set (its rowids mean nothing on another table, and the staged set
  // was reset when this grid moved to one).
  const [, trackSave] = useDraftInFlight<SavePurged>(saveKey, (o) => {
    if (!o.ok || o.value.table !== selectedTableRef.current) return
    closeEditRows(o.value.deleteRowIds)
    dropSavedCells(o.value.savedCells)
    edits.purge(o.value)
    void loadTable()
  })
  /** Close cells a Save already wrote — the ones still holding exactly the
   *  value it sent (a cell typed into since keeps the new text). */
  const dropSavedCells = (saved: SavePurged['savedCells']) => {
    const next = new Map(editCellsRef.current)
    let changed = false
    for (const s of saved) {
      const c = next.get(s.key)
      if (c && c.value === s.value && !!c.isNull === s.isNull) { next.delete(s.key); changed = true }
    }
    if (!changed) return
    editCellsRef.current = next
    setEditCells(new Map(next))
    if (![...next.values()].some(c => c.dirty)) { hasDirtyRef.current = false; onDirtyChange?.(false) }
    const rowsToClose = [...editRowIdsRef.current].filter(rowid => ![...next.keys()].some(k => isEditKeyOfRow(k, rowid)))
    if (rowsToClose.length > 0) {
      setEditRowIds(prev => { const r = new Set(prev); for (const x of rowsToClose) r.delete(x); return r })
      setEditRowHeights(prev => { const m = new Map(prev); for (const x of rowsToClose) m.delete(x); return m })
    }
  }
  const trackSaveRef = useRef(trackSave)
  trackSaveRef.current = trackSave
  const saveKeyRef = useRef(saveKey)
  saveKeyRef.current = saveKey
  /** What an insert refused with, carried past the refreshes that follow a
   *  Save — `loadTable` and the post-save refresh both rewrite the status line
   *  with a row count, so a message written while they ran was never read. */
  const insertIssueRef = useRef('')
  const saveAllEditCellsRef = useRef<(() => void) | null>(null)

  // Filter & pagination
  // Draft filters = what the user is editing in the panel
  // Applied filters = what the query actually uses (set on Apply/Enter)
  const [draftFilters, setDraftFilters] = useState<TableFilter[]>(() => restored ? restored.appliedFilters : Array.isArray(_vs?.appliedFilters) ? _vs.appliedFilters as TableFilter[] : [])
  const [appliedFilters, setAppliedFilters] = useState<TableFilter[]>(() => restored ? restored.appliedFilters : Array.isArray(_vs?.appliedFilters) ? _vs.appliedFilters as TableFilter[] : [])
  const appliedFiltersRef = useRef(appliedFilters)
  appliedFiltersRef.current = appliedFilters
  // Filter panel state: use props from parent (DatabaseEditor) if provided, otherwise local
  const [filterOpenLocal, setFilterOpenLocal] = useState(false)
  const [advancedFilterLocal, setAdvancedFilterLocal] = useState(() => typeof _vs?.advancedFilter === 'boolean' ? _vs.advancedFilter : false)
  const filterOpen = filterOpenProp ?? filterOpenLocal
  const advancedFilter = advancedFilterProp ?? advancedFilterLocal
  const [rawWhere, setRawWhere] = useState(() => restored ? restored.appliedRawWhere : typeof _vs?.appliedRawWhere === 'string' ? _vs.appliedRawWhere : '')
  const [appliedRawWhere, setAppliedRawWhere] = useState(() => restored ? restored.appliedRawWhere : typeof _vs?.appliedRawWhere === 'string' ? _vs.appliedRawWhere : '')
  const [limit, setLimit] = useState(() => restored ? restored.limit : typeof _vs?.limit === 'number' ? _vs.limit as number : 100)
  const [page, setPage] = useState(() => restored?.page ?? 0)

  // #897 — claim the stash this grid was built from, and snapshot the unsaved
  // cells (with their view) if a layout swap is what unmounts it. Read through
  // a ref the cleanup sees at unmount time; StrictMode's dev unmount → remount
  // re-stashes and re-claims inside the same commit.
  const swapSnapshotRef = useRef<() => GridSwapStash | null>(() => null)
  useEffect(() => {
    if (!stashKey) return
    claimSwapStash(stashKey)
    const stash = () => {
      const snap = swapSnapshotRef.current()
      if (snap) stashForSwap(stashKey, snap)
    }
    // Snapshot the moment the swap STARTS — before React renders the new tree,
    // so the new grid's first render finds it however the commit is ordered
    // (a grid that mounted in the same commit as this one's cleanup would
    // otherwise peek before the stash exists, then claim it away).
    const off = onLayoutSwapStart(stash)
    // Still mounted when the window closes: this grid was NOT swapped (the chat
    // home's 950px swap, say) — drop its start snapshot, or a later reopen of
    // the same file would restore cells the user has since saved or discarded.
    const offEnd = onLayoutSwapEnd(() => claimSwapStash(stashKey))
    return () => {
      off()
      offEnd()
      // …and again on the way out, for the StrictMode dev remount inside a swap.
      stash()
    }
  }, [stashKey])
  // Kept current every render. Snapshots only when something is unsaved: typed
  // cells, or staged rows (whose scope needs the table and page back too — on
  // another table `scopeTo` would reset them).
  swapSnapshotRef.current = () => {
    const cells = [...editCellsRef.current.entries()]
    const dirtyCells = cells.some(([, c]) => c.dirty)
    const staged = editsRef.current.deleteRowIds.size > 0 || editsRef.current.inserts.length > 0
    if (!dirtyCells && !staged) return null
    return {
      table: selectedTable, page, limit, appliedFilters, appliedRawWhere, selectedColumns,
      cells: dirtyCells ? cells : [],
      rowIds: dirtyCells ? [...editRowIdsRef.current] : [],
      heights: dirtyCells ? [...editRowHeights.entries()] : [],
    }
  }
  useEffect(() => {
    // The table the swap left unsaved cells in is gone (dropped or renamed meanwhile).
    if (restoreLost) toast?.error('Unsaved grid changes were discarded — their table no longer exists')
  }, [restoreLost, toast])

  // --- View sync: write state changes ---
  useEffect(() => {
    if (!viewSync || !viewSyncId) return
    viewSync[viewSyncId] = {
      selectedTable,
      appliedFilters,
      appliedRawWhere,
      advancedFilter,
      selectedColumns,
      limit,
      schemaOpen,
    }
  }, [viewSync, viewSyncId, selectedTable, appliedFilters, appliedRawWhere, advancedFilter, selectedColumns, limit, schemaOpen])

  const applyFilters = useCallback(() => {
    if (advancedFilter) {
      setAppliedRawWhere(rawWhere.trim())
      setAppliedFilters([])
    } else {
      setAppliedFilters(draftFilters)
      setAppliedRawWhere('')
    }
    setSelectedColumns(draftColumns)
    setPage(0)
    setDroppedFilterColumns([])
  }, [draftFilters, draftColumns, advancedFilter, rawWhere])

  const clearFilters = useCallback(() => {
    if (advancedFilter) {
      setRawWhere('')
      setAppliedRawWhere('')
    } else {
      const table = enrichedSchema.find(t => t.name === selectedTable)
      const columns = table?.columns || []
      const defaultFilter = columns.length > 0
        ? [{ id: genFilterId(), column: columns[0].name, operator: 'equals' as FilterOperator, value: '' }]
        : []
      setDraftFilters(defaultFilter)
      setAppliedFilters([])
    }
    setPage(0)
    setDroppedFilterColumns([])
  }, [schema, selectedTable, advancedFilter])

  // Load table data
  /**
   * The window a revalidation (reconnect, a file event) was checking, held
   * from the moment it STARTS until something prunes against fresh rows of
   * that window. One that is refused (a column dropped in the same change),
   * superseded (a reload the change caused lands first) or fails has pruned
   * nothing — and an editor left on a row deleted meanwhile is an UPDATE
   * aimed at a rowid the database may reuse.
   *
   * The next read that lands pays it if it read the SAME window
   * (`sameReadWindow` — table, page, page size, filters; the column subset
   * changes columns, not rows). Any other window is "elsewhere", not
   * "deleted" — the reason an ordinary load never prunes — so there the debt
   * is dropped. That residual (a revalidation lost to a page or filter
   * change, including the filter on a column the same change dropped) is
   * what main did with every lost revalidation.
   */
  const revalidateDebtRef = useRef<ReadWindow | null>(null)
  // Read through a ref so `loadTable`'s identity — which reloads the table —
  // does not change with it.
  const refreshSchemaRef = useRef(refreshSchema)
  refreshSchemaRef.current = refreshSchema
  /**
   * `revalidate` — this load is checking the table against the SERVER, not
   * just moving the window over it.
   *
   * Only a revalidating load closes cell editors for rows that are no longer
   * there, and the distinction is the whole reason the flag exists. An
   * ordinary load cannot tell "this row was deleted" from "this row is on
   * another page / excluded by the filter", and it took a review round to
   * notice that pruning on every load meant paging away from a cell you had
   * typed into, or hiding its column, threw the keystrokes away with no
   * warning. Delete MARKS accept that cost (see `pruneMarksTo`) because
   * re-placing one is a click; retyping a value is not.
   */
  const loadTable = useCallback(async (table?: string, pageNum?: number, retriesLeft = 3, opts?: { revalidate?: boolean }) => {
    const tbl = table ?? selectedTable
    const pg = pageNum ?? page
    if (!tbl) return
    const seq = ++tableReadSeqRef.current
    const loadSeq = ++loadTableSeqRef.current
    const readWin = readWindow(tbl, pg, limit, appliedFilters, appliedRawWhere)
    if (opts?.revalidate) revalidateDebtRef.current = readWin
    const writeSeq = writeSeqRef.current
    setQueryLoading(true)
    try {
      const res = await adapter.getTable(fileId, tbl, limit, pg * limit, {
        versionId, editMode: editable,
        filters: appliedFilters.length > 0 ? appliedFilters : undefined,
        rawWhere: appliedRawWhere || undefined,
        columns: selectedColumns.length > 0 ? selectedColumns : undefined,
      })
      // A newer read started while this one was out: it describes the table
      // as it was BEFORE whatever that read is about to show. Skipped, not
      // returned — an early return here would leave the spinner running for
      // good, because the line that stops it is below the catch.
      if (seq === tableReadSeqRef.current) {
        setResult(res)
        setLoadedTable(tbl)
        const debt = revalidateDebtRef.current
        revalidateDebtRef.current = null
        // A revalidating read armed the debt with its own window, so this is
        // also how a revalidation prunes; a discard since has left nothing.
        if (debt && sameReadWindow(debt, readWin)) pruneEditsToRef.current(res.rows, selectedColumns.length === 0 ? res.columns : undefined)
        pruneMarksToRef.current(res.rows)
        const total = res.totalCount ?? 0
        const isFiltered = appliedFilters.length > 0 || !!appliedRawWhere
        setStatusMsg(`${res.rows.length} of ${total.toLocaleString()} rows${isFiltered ? ' (filtered)' : ''}`)
      }
    } catch (err: any) {
      // The server refused columns this grid still names (#899) — dropped
      // since they were picked, or never there (the AI's `setColumns` takes
      // any name). The server is the authority on what the table can answer
      // (generated and hidden columns, identifier case — the schema list is
      // not), and it names what it refused: forget exactly those, and the
      // state change reloads. Nothing to forget (a name the grid does not
      // hold as given) shows the refusal. Not in a version preview: its
      // view-sync entry is the live grid's (see `forgetColumns`).
      //
      // A refused revalidation stays owed (`revalidateDebtRef`) to the
      // reload that follows, which carries no `opts`.
      const current = seq === tableReadSeqRef.current
      if (current && err?.code === 'unknown_column' && !versionId && Array.isArray(err.unknownColumns)
          && forgetColumnsRef.current(err.unknownColumns)) {
        // The sidebar's column list catches up; the reload does not wait.
        refreshSchemaRef.current()
      } else if (current) {
        // A failed OLD read must not wipe out what a newer one is showing. #1316: nor does a dropped answer (its
        // session ended — nothing failed for the person now here): the grid keeps what it shows.
        const text = errorText(err)
        if (text !== null) {
          setResult({ columns: [], rows: [], error: text })
          setStatusMsg('')
        }
      }
    }
    // Only the newest LOAD owns the spinner. An older one turning it off made
    // the table look finished while the real data was still on its way, and
    // then the rows changed under the user a moment later.
    if (loadSeq === loadTableSeqRef.current) {
      setQueryLoading(false)
      // OUR OWN save landed while this load was out, so its rows were thrown
      // away as pre-write — and nothing else is coming to replace them. Go to
      // page 2, save a cell before it arrives, and the grid sat on page 1's
      // rows with the pager saying page 2. A file event usually rescues it,
      // but a PDApp's `_appdata/data.db` publishes none at all.
      // Only for OUR write, and under a BUDGET rather than a single retry: a
      // second save landing during the re-read discards that one too, and a
      // hard "one retry" cap left the grid on pre-write rows with nothing
      // else coming — a PDApp's `_appdata/data.db` publishes no file event to
      // rescue it. Three is enough for a burst of saves and still cannot run
      // away.
      // No table/page arguments: the re-read must go to WHATEVER the grid is
      // showing now, not to what this load was started for. Replaying the
      // captured `tbl`/`pg` re-read the old table and, because it takes the
      // newest read token on the way, outranked the load for the table the
      // user had just switched to — one table's rows under another's header.
      if (retriesLeft > 0 && writeSeqRef.current !== writeSeq) {
        // `opts` travels with the retry. A discarded read has done NOTHING —
        // including the prune — so a revalidating load that loses the race to
        // one of our own writes must come back as a revalidating load. It
        // dropped the flag before, on the one path most likely to need it:
        // come back online with a dirty cell editor open, our queued save
        // lands while the revalidation is out, and the editor stays open on a
        // row that is gone.
        void loadTableRef.current?.(undefined, undefined, retriesLeft - 1, opts)
      }
    }
  }, [fileId, selectedTable, page, limit, appliedFilters, appliedRawWhere, selectedColumns, versionId, editable])

  // Undo entries outlive the render that made them, so they refresh through
  // a ref rather than closing over one generation of `loadTable`.
  const loadTableRef = useRef(loadTable)
  loadTableRef.current = loadTable

  /**
   * What the grid was last set up for: file, version and table.
   *
   * The table-change effect resets everything keyed by row or column when
   * this changes. A rename writes the new value here BEFORE moving
   * `selectedTable`, which is the whole of how a rename avoids being read as
   * a move — see that effect.
   */
  // A grid restored after a layout swap (#897) starts AT its scope: the
  // table-change effect below must see no change, or it would clear the
  // restored cells and reset the staged rows on the first run.
  const prevScopeRef = useRef<string>(restored ? `${fileId}:${versionId ?? ''}:${restored.table}` : '')
  const prevTableRef = useRef<string>(restored ? restored.table : '')
  /** The table on screen NOW, readable after an await. `onModify` captures
   *  `selectedTable` when the user clicks Apply and then waits on the server;
   *  by the time it resolves the AI may have moved the grid elsewhere, and
   *  acting on the captured value then acts on a table nobody is looking at. */
  const selectedTableRef = useRef<string>('')
  selectedTableRef.current = selectedTable

  /**
   * Which table is on screen, as a NUMBER that only changes when the table
   * really does.
   *
   * A cell save captures this and compares after its await: the user can
   * switch tables while a save is in flight, and an error message written
   * afterwards would land under the new table's row 7 / `name`, which is a
   * different cell entirely.
   *
   * It was the table NAME, and a rename broke it in both directions at once.
   * After a rename the captured name and the current one differ while being
   * the same physical table, so the guard fired on a transition that was not
   * stale: a single-cell save left its spinner running with no check and no
   * cancel button to dismiss it, and a Save-all reported nothing at all —
   * neither that it had succeeded nor that a constraint had rejected it.
   * Comparing a token the rename does not touch makes that correct by
   * construction rather than by another condition.
   *
   * Bumped in a LAYOUT EFFECT, and there are two wrong places it has been.
   *
   * In a PASSIVE effect it moved too late: React flushes those after paint
   * for anything that is not discrete input — the AI's `papan:db-set-table`
   * is exactly that — so a response resolving in between read an epoch that
   * had not moved, and the guard passed on a genuinely stale save.
   *
   * DURING RENDER it moved on trees that never existed. A render React starts
   * and then throws away — a concurrent interruption, a Suspense retry — had
   * still bumped it, so the epoch could move with no commit and therefore no
   * reset behind it: the save came back to a cell that was still on screen
   * and it spun for good, with no cancel and no error. This repo already had
   * that rule written down, in `ShareViewerChrome.tsx` — stamp in a layout
   * effect, never during render — and it is right.
   *
   * A layout effect runs synchronously inside the commit, so no promise
   * continuation can interleave: it beats the microtask as completely as a
   * render-phase write did, while only ever running on a tree that committed.
   * It is the same effect that does the reset, so the epoch cannot move
   * without the reset that goes with it — which is what makes the saves'
   * plain `return` safe.
   */
  const tableEpochRef = useRef(0)
  useEffect(() => { loadTable() }, [loadTable])

  /**
   * Come back online → check the table against the server.
   *
   * This used to happen by accident: an `online` event made `useDbSchema` set
   * `loading`, the spinner unmounted this component, and the remount re-ran
   * the load. Not tearing the editor down any more — the point of this branch
   * — took that revalidation with it, and the revalidation is what drops a
   * delete mark, or a cell editor, left pointing at a row somebody else
   * removed during the gap. A rowid the database has since reassigned belongs
   * to another row now.
   *
   * Its own effect rather than a dep of `loadTable`, so that the revalidating
   * behaviour belongs to the reconnect and not to every paging click.
   */
  const firstRecoveryRef = useRef(recovery)
  useEffect(() => {
    if (recovery === firstRecoveryRef.current) return
    void loadTableRef.current?.(undefined, undefined, undefined, { revalidate: true })
  }, [recovery])


  /** Ordering token for table reads.
   *
   *  Bumped by every read that will REPLACE what is on screen, and by every
   *  write. A background refetch answering file event N can come back after
   *  write N+1 has landed; applied blindly it puts the rows back to their
   *  pre-N+1 values, and nothing would be coming to correct them. */
  const tableReadSeqRef = useRef(0)

  /** Which `loadTable` call owns the spinner — SEPARATE from the staleness
   *  token above, deliberately. That one is bumped by background refetches
   *  and by every write, so keying the spinner on it meant a save during a
   *  load turned the load's data away AND left the spinner running with
   *  nothing on the way to stop it. Only a newer LOAD takes the spinner. */
  const loadTableSeqRef = useRef(0)

  /** Counts OUR OWN writes. The re-read below is armed by this and not by the
   *  staleness token, which every background refetch also bumps: a steady
   *  stream of a colleague's writes to the same .db kept re-arming the retry
   *  and the spinner never settled. A refetch already puts its own fresh rows
   *  on screen, so only a write needs the re-read. */
  const writeSeqRef = useRef(0)

  /** Run a write and invalidate the table reads it makes stale.
   *
   *  Any read already in flight was issued against the rows as they were
   *  BEFORE this write, so letting it land would put the old values back on
   *  screen — and then nothing would be coming to correct them. The counters
   *  make a stale response discardable and tell `loadTable` to re-read.
   *
   *  It no longer tries to work out whether an incoming file event was ours.
   *  That question existed only to decide whether to throw away an undo
   *  history of already-saved writes, and there is no such history any more. */
  const trackedWrite = useCallback(async <T,>(op: () => Promise<T>): Promise<T> => {
    tableReadSeqRef.current++
    writeSeqRef.current++
    return op()
  }, [])

  // Live refresh: background fetch + cell-level diff (no full reload)
  useEffect(() => {
    if (versionId) return
    return onFileEvent((event) => {
      if (event.fileId !== fileId) return
      if (event.type !== 'file:updated' && event.type !== 'file:restored') return

      // Refresh schema counts
      refreshSchemaRef.current()
      // Clear cached table info so edit rows pick up column changes
      setTableInfoCache(new Map())
      if (selectedTable) loadTableInfo(selectedTable)

      if (!selectedTable || !gridRef.current?.api) return

      // Unified diff: re-fetch data, prune stale edit cells, update grid
      const seq = ++tableReadSeqRef.current
      const epoch = tableEpochRef.current
      // A revalidation: owed from here until something prunes this window
      // (see `revalidateDebtRef`) — a refused or superseded read prunes nothing.
      revalidateDebtRef.current = readWindow(selectedTable, page, limit, appliedFilters, appliedRawWhere)
      adapter.getTable(fileId, selectedTable, limit, page * limit, {
        editMode: editable,
        filters: appliedFilters.length > 0 ? appliedFilters : undefined,
        rawWhere: appliedRawWhere || undefined,
        columns: selectedColumns.length > 0 ? selectedColumns : undefined,
      }).then(fresh => {
        // Stale: a later read (another file event, or a full reload) started
        // while this was in flight, so these rows predate what is coming.
        // Writing them into the grid would put the old values BACK, with
        // nothing on the way to correct them.
        if (seq !== tableReadSeqRef.current) return
        // Stale in the other direction: these rows are a different TABLE's.
        // `tableReadSeqRef` cannot see that: it moves on reads and writes (see
        // its own note above), and the table change is neither. The reset and
        // the epoch bump happen in a LAYOUT effect, while the load that would
        // move the sequence is dispatched from a passive one — later in the
        // same commit. Everything below is keyed by
        // `__rowid`, including the green flash, so the old table's ids would
        // land on the new table's rows and light them up as just saved; the
        // row count at the end would be the old table's too.
        if (epoch !== tableEpochRef.current) return
        const api = gridRef.current?.api
        if (!api) return

        const freshById = new Map<string | number, Record<string, unknown>>()
        for (const row of fresh.rows) freshById.set(row.__rowid as string | number, row)

        pruneEditsToRef.current(fresh.rows, selectedColumns.length === 0 ? fresh.columns : undefined)
        // That was a full revalidation of what is on screen.
        revalidateDebtRef.current = null

        pruneMarksToRef.current(fresh.rows)

        // Detect structural changes (columns or row set changed)
        const currentRows = new Map<string | number, Record<string, unknown>>()
        api.forEachNode(node => {
          // Staged inserts are ours, not the server's. Counting them made
          // `hasStructuralChanges` true for as long as anything was staged, so
          // every remote event took the full-reload path instead of the
          // targeted cell diff.
          if (node.data && !node.data.__editRow && !node.data.__pendingInsert) {
            currentRows.set(node.data.__rowid, node.data)
          }
        })
        // Get current columns from grid state (not React state — avoids stale closure)
        const currentCols = api.getColumns()?.map(c => c.getColId()).filter(c => c !== '__rownum') || []
        const currentIds = new Set(currentRows.keys())
        const freshIds = new Set(freshById.keys())
        const hasStructuralChanges =
          fresh.columns.length !== currentCols.length ||
          fresh.columns.some((c, i) => c !== currentCols[i]) ||
          currentIds.size !== freshIds.size ||
          [...currentIds].some(id => !freshIds.has(id)) ||
          [...freshIds].some(id => !currentIds.has(id))

        if (hasStructuralChanges) {
          // Structural change — reload via loadTable for proper grid remount.
          // loadTable retires the memo when ITS rows land; doing it here would
          // strip the bridge for the whole of that second round trip.
          void loadTable()
        } else {
          // Same structure — targeted cell update with flash. These rows are
          // the same objects `result` holds, so mutating them below is what
          // puts the new values on screen.
          const toUpdate: Record<string, unknown>[] = []
          for (const [rowid, currentRow] of currentRows) {
            const freshRow = freshById.get(rowid)
            if (!freshRow) continue
            let changed = false
            for (const col of fresh.columns) {
              if (currentRow[col] !== freshRow[col]) {
                currentRow[col] = freshRow[col]
                changed = true
              }
            }
            if (changed) toUpdate.push(currentRow)
          }
          if (toUpdate.length) {
            for (const r of toUpdate) flashRowsRef.current.set(String(r.__rowid), 'updated')
            api.applyTransaction({ update: toUpdate })
            const nodes = toUpdate.map(r => api.getRowNode(String(r.__rowid))).filter(Boolean)
            api.redrawRows({ rowNodes: nodes as any })
            setTimeout(() => { flashRowsRef.current.clear() }, 1500)
          }
        }

        // Update total count
        if (fresh.totalCount !== undefined) {
          setStatusMsg(`${fresh.rows.length} of ${fresh.totalCount.toLocaleString()} rows${appliedFilters.length > 0 ? ' (filtered)' : ''}`)
        }
      }, (err: any) => {
        // Refused for naming columns the change removed: forget them, as
        // `loadTable` does, and that reload reads the table again. Only for
        // the newest read of this table (its success is guarded the same
        // way). Anything else stays as quiet as it always was.
        if (seq === tableReadSeqRef.current && epoch === tableEpochRef.current
            && err?.code === 'unknown_column' && Array.isArray(err.unknownColumns)) {
          forgetColumnsRef.current(err.unknownColumns)
        }
      }).catch(() => {})
    })
  }, [fileId, versionId, onFileEvent, selectedTable, page, limit, appliedFilters, appliedRawWhere, selectedColumns, editable])

  // What the staged set belongs to. Declared rather than reset: the grid
  // remounts on a collab reconnect, and an effect that reset on mount threw
  // the staged rows away every time the socket blinked. The hook compares
  // against the key it already holds, which a remount cannot fake.
  //
  // Only the TABLE and the version. Paging, filtering and hiding columns move
  // the window over the same table — the rowids stay valid and a staged insert
  // is still headed for the same place.
  useLayoutEffect(() => {
    // A RENAME is not a move. `rename_table` changes what the table is called
    // and nothing else — same rows, same rowids, same columns — but it
    // arrives here looking exactly like switching to a different table, and
    // everything below would then throw away the user's open cell editors,
    // what they had typed in them, and their staged rows. For a rename they
    // asked for, and with no message.
    //
    // It is told so by the rename itself, which writes the new scope here
    // before it moves `selectedTable`. There is no flag, no from/to pair and
    // nothing to disarm: what arrives is simply a scope that has not changed.
    //
    // That is deliberately the SECOND version of this. The first compared a
    // `{from, to}` pair against the previous table, and a flag that has to be
    // armed has a state where it is armed and never fires — a rename whose
    // table moved under it mid-request stayed armed and then matched a later,
    // coincidental a→b navigation, carrying `a`'s staged delete marks into
    // `b` where they pointed at `b`'s rowids. The fix for that was a third
    // rule about disarming. Three pieces of mutable state, two thousand lines
    // apart, and a bug that only the seventh review round found: the shape
    // was wrong, not the rules. Last-write-wins is marginally less precise if
    // the table changes mid-rename, and it cannot get stuck.
    const scope = `${fileId}:${versionId ?? ''}:${selectedTable}`
    const unchanged = prevScopeRef.current === scope
    prevScopeRef.current = scope
    const prevTable = prevTableRef.current
    prevTableRef.current = selectedTable
    if (unchanged) {
      // The staged set is the same set, now under a new name.
      editsRef.current.rekey(scope)
      // #897 — and a Save handed over from a grid a swap replaced, logged
      // under the OLD name, is still for this table.
      // Recorded only while a Save is out (the map is dropped when it lands).
      if (saveKeyRef.current && prevTable && prevTable !== selectedTable && isDraftInFlight(saveKeyRef.current)) {
        recordTableRename(saveKeyRef.current, prevTable, selectedTable)
      }
      return
    }

    // Told by `scopeTo` whether it actually reset. Reading the staged set here
    // instead said "discarded" on a plain remount, where nothing was — and the
    // user could see their marks still on screen while being told they were
    // gone. A toast rather than the status line: `loadTable` writes a row
    // count there as soon as the new table arrives. The hosts with no
    // ToastProvider are the read-only preview shells, where nothing can be
    // staged in the first place.
    const discarded = editsRef.current.scopeTo(`${fileId}:${versionId ?? ''}:${selectedTable}`)
    // Typed cell values too. They are keyed `rowid:column` with no table, and
    // Save sends every dirty one to the CURRENT table — so a value typed in
    // the old table landed on whichever row of the new one shared its rowid.
    // Every way the table changes passes here (a click, the AI's
    // `papan:db-set-table`, dropping the open table); the state is the dirty
    // effect's trigger, which then reports the grid clean.
    const discardedCells = [...editCellsRef.current.values()].some(c => c.dirty)
    setDroppedFilterColumns([])
    // Every editor goes below; nothing is left for an owed revalidation.
    revalidateDebtRef.current = null
    if (editCellsRef.current.size > 0) {
      editCellsRef.current = new Map()
      setEditCells(new Map())
      setEditRowIds(new Set())
      editErrorsRef.current = new Map()
      setEditErrors(new Map())
      setEditRowHeights(new Map())
    }
    if (discarded || discardedCells) toast?.error('Unsaved grid changes were discarded when the table changed')
    // Everything that POINTS AT A ROW goes with them. `__rowid` is per-table,
    // so a remembered row means nothing here — and Duplicate keys its prefill
    // by COLUMN NAME, so any name the two tables share would carry a value
    // across without a word. The open context menu too: the AI can switch
    // tables with no user mousedown (`papan:db-set-table`), so the
    // close-on-outside-click never fires.
    lastClickedRowRef.current = null
    selectionRef.current = null
    setSelectionVersion(v => v + 1)
    setCtxMenu(null)
    // The save flash too. It is keyed by `__rowid` and cleared only by its own
    // 1.5s timer, so a table changed inside that window carried the green
    // "just saved" highlight onto whichever row of the NEW table shares the
    // id — telling the user they had just written a row they have never
    // touched.
    //
    // Untested, like the two guards that go with it (the deferred flash and
    // the live-refresh `.then`): deleting any of the three leaves the whole
    // suite green. Verified by hand, same window as the delete path below.
    flashRowsRef.current.clear()
    // And the Insert Row dialog. A form opened for one table does not belong to
    // the next one: left open it re-seeds to the new table's columns, so what
    // had been typed silently became that table's defaults while the dialog
    // still looked like it was holding the user's work.
    //
    // HERE rather than in its own effect keyed on `selectedTable`: a RENAME
    // changes the name without changing anything the dialog cares about — same
    // columns, same rows — and this effect is the one place that already knows
    // the difference (it returns above).
    //
    // Not because that was observed. A rename needs the Modify Table dialog,
    // which is opened from the schema sidebar — and this dialog's backdrop
    // covers the sidebar, so one cannot be reached while the other is up, and
    // there is no window event for a rename the way there is for a table
    // change. So keyed on the name it would have been wrong and unreachable;
    // here it is right by construction, for whenever a rename does become
    // scriptable. (If that happens, note the dialog would then live through
    // `refreshSchema`'s fetch with `columns: []` — no fields, Insert still
    // enabled, staging an empty row. Typed values survive it; the button does
    // not need a guard until the window exists.)
    //
    // Reachable only when the table moves with no click: clicking the sidebar
    // dismisses this dialog through its backdrop first, but the AI's
    // `papan:db-set-table` does not.
    //
    // Untested, like the resets around it: deleting these two lines leaves the
    // whole suite green.
    setShowInsertDialog(false)
    setInsertPrefill(null)

    // The open cell editors, their errors, and their spinners. Per-table,
    // every one of them: `__rowid` restarts at 1 in each table and column
    // names repeat, so anything keyed by row and column means something
    // different the moment the table changes.
    //
    // HERE and not in `selectTable`, because `selectTable` is only the
    // sidebar click. The AI switches tables through `papan:db-set-table`, and
    // dropping the table you are viewing moves you to whatever is left —
    // neither goes near it. Doing this on the effect that watches the table
    // itself is the only version that does not need every future caller to
    // remember.
    //
    // What it looked like: leave a failed save on row 7's `name`, ask the AI
    // for another table, and row 7 of THAT table opened with the previous
    // table's "NOT NULL constraint failed" under a cell that had never been
    // saved — or, for a save still in flight, a spinner that never stopped.
    //
    // The refs matter more than the state. `editCells` state exists for dirty
    // tracking; `editErrors` state is write-only — every read of either goes
    // through the ref.
    editCellsRef.current = new Map()
    editErrorsRef.current = new Map()
    savingCellsRef.current = new Set()
    setEditCells(new Map())
    setEditErrors(new Map())
    setEditRowIds(new Set())
    // Row heights are keyed by `__rowid` too — drag row 3's editor taller in
    // one table and row 3 of the next one opened at that height.
    setEditRowHeights(new Map())

    // LAST. A real change of table, file or version means anything captured
    // before it — a cell save still in flight — belongs to a database that is
    // no longer on screen, and the two saves' staleness guard reads this.
    //
    // At the end rather than the top so that "the epoch never moves without
    // the reset that goes with it" is true by construction instead of by
    // reading the twenty lines above and confirming none of them returns.
    // Identical on every reachable path today; the difference is that it
    // stays identical when somebody adds a condition up there.
    tableEpochRef.current++
  }, [selectedTable, versionId, fileId, toast])

  /**
   * The cell selection is POSITIONAL — `startRow`/`endRow` are offsets into
   * what is currently drawn, not `__rowid`s. So anything that changes which
   * rows are on screen leaves the same blue box over different data: the user
   * sees their selection apparently survive a page turn, and Copy takes the
   * rows now sitting in those positions.
   *
   * The dep list is what `loadTable` keys on, minus the three the table-change
   * effect above already covers (`selectedTable`, `fileId`, `versionId`) —
   * `editable` is in here because the row ARRAY changes shape with it: the
   * `rowData` memo interleaves an `__editRow` pseudo-row after each open
   * editor only while editable, so turning it off removes rows from the
   * MIDDLE and shifts every position after them. (It is also in `loadTable`'s
   * deps and rides along as `editMode` on the query — but nothing on the
   * server reads that, so the re-query returns the same bytes and is not the
   * reason.) With an editor open the clear is simply correct — the pseudo-rows
   * really do come and go and everything below them shifts. The cost is only
   * the flips with NO editor open, where nothing moves and the selection goes
   * anyway: correctness first. (A View↔SQL trip is not one of them at all —
   * `activeMode` makes `editable` false for both.) That effect
   * also resets the per-table state a page turn must NOT touch (open
   * cell editors stay: a `__rowid` still means the same row on the next page,
   * so the typing in them is still aimed at the row it was aimed at).
   *
   * Untested: deleting this effect leaves the whole suite green. Verified by
   * hand — select a range, turn the page, it is gone.
   *
   * What this does NOT cover: `result.rows` changing underneath without any of
   * these moving — a background file-event refresh, or `loadTable`'s own
   * post-write retry. Another writer deleting a row above the selection shifts
   * the rest up, and the box then spans different rows. Same bug, different
   * trigger; clearing on `setResult` would catch it, and would also wipe the
   * selection on every unrelated refresh, so it is left for its own change
   * rather than smuggled in here.
   *
   * The context menu goes too. It is placed in screen coordinates over the row
   * that was clicked, and `papan:db-set-filter` / `papan:db-set-columns` let
   * the AI move the rows under it with no click to dismiss it.
   */
  useEffect(() => {
    selectionRef.current = null
    setSelectionVersion(v => v + 1)
    setCtxMenu(null)
  }, [page, limit, appliedFilters, appliedRawWhere, selectedColumns, editable])

  // Table selection
  const selectTable = useCallback((name: string) => {
    if (name === selectedTable) {
      setExpandedTables(prev => {
        const next = new Set(prev)
        next.has(name) ? next.delete(name) : next.add(name)
        return next
      })
      return
    }
    setSelectedTable(name)
    setPage(0)
    setDraftFilters([])
    setAppliedFilters([])
    setRawWhere('')
    setAppliedRawWhere('')
    setDraftColumns([])
    setSelectedColumns([])
    setColInput('')
  }, [selectedTable])

  // Pagination
  const totalPages = Math.max(1, Math.ceil((result?.totalCount ?? 0) / limit))
  const goPage = useCallback((p: number) => {
    const clamped = Math.max(0, Math.min(p, totalPages - 1))
    setPage(clamped)
  }, [totalPages])

  // Edit row handlers
  const openEditCell = useCallback((rowid: number, column: string) => {
    if (!editable) return
    const row = result?.rows.find(r => r.__rowid === rowid)
    if (!row) return
    setEditRowIds(prev => new Set(prev).add(rowid))
    // Redraw source row after state update so getRowClass applies the gradient,
    // then focus the textarea in the newly opened cell
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const api = gridRef.current?.api
        if (!api) return
        const node = api.getRowNode(String(rowid))
        if (node) api.redrawRows({ rowNodes: [node] })
        // Focus the textarea for the opened cell
        setTimeout(() => {
          const editRowId = `edit_${rowid}`
          const editRow = Array.from(document.querySelectorAll('.ag-full-width-row')).find(el => el.getAttribute('row-id') === editRowId)
          const cellEl = editRow?.querySelector(`[data-col="${column}"]`)
          const textarea = cellEl?.querySelector('textarea') as HTMLTextAreaElement | null
          if (textarea) { textarea.focus(); textarea.setSelectionRange(textarea.value.length, textarea.value.length) }
        }, 50)
      })
    })
    const key = editCellKey(rowid, column)
    if (!editCellsRef.current.has(key)) {
      const rawVal = row[column]
      const isNull = rawVal === null || rawVal === undefined
      const cell = { column, value: isNull ? '' : String(rawVal), dirty: false, isNull }
      editCellsRef.current = new Map(editCellsRef.current).set(key, cell)
    }
    setEditCells(prev => {
      const next = new Map(prev)
      if (!next.has(key)) {
        const rawVal = row[column]
        const isNull = rawVal === null || rawVal === undefined
        next.set(key, { column, value: isNull ? '' : String(rawVal), dirty: false, isNull })
      }
      return next
    })
  }, [editable, result])

  // Open pending edit cell when switching from View → Edit mode via double-click
  useEffect(() => {
    if (editable && pendingEditCellRef.current) {
      const { rowid, column } = pendingEditCellRef.current
      pendingEditCellRef.current = null
      openEditCell(rowid as number, column)
    }
  }, [editable, openEditCell])

  /**
   * The last clicked row, for Duplicate to copy values from.
   *
   * Only ONE case has to be refused: a STAGED row the user has since taken
   * back. They removed it on purpose — offering it as a template afterwards
   * hands back the thing they just discarded. It leaves by four routes (its ×,
   * the context menu, Ctrl+Z, Discard), so this is asked on read rather than
   * cleared at each of them, which is the shape that keeps missing one.
   *
   * A REAL row needs no such check, and an earlier version that looked for it
   * in the rows on screen was wrong twice over: `__rowid` is per-table, so
   * after a table switch the new table almost always has that id and the check
   * passed on the wrong row; and the rows on screen are one page, so paging
   * away silently emptied the form. Neither matters. Duplicate makes a NEW row
   * out of values we are already holding — it does not need the original to
   * still be in front of the user.
   */
  // `[]` deps on purpose: it reads only refs, and the command registration
  // closes over the first instance for the life of the component. Adding a dep
  // here would make the menu item go stale with no visible symptom.
  const rowIfUsable = useCallback((row: Record<string, unknown> | null | undefined) => {
    if (!row) return null
    if (!row.__pendingInsert) return row
    return editsRef.current.inserts.some(i => i.tempId === String(row.__rowid)) ? row : null
  }, [])

  const clickedRowIfPresent = useCallback(
    () => rowIfUsable(lastClickedRowRef.current),
    [rowIfUsable],
  )

  /** Close the edit UI for these rows — cells, row set, heights and errors,
   *  the same four the other close paths clear. */
  const closeEditRows = useCallback((rowIds: Iterable<number>) => {
    const ids = [...rowIds]
    if (ids.length === 0) return
    const next = new Map(editCellsRef.current)
    const errs = new Map(editErrorsRef.current)
    for (const id of ids) {
      for (const key of [...next.keys()]) if (isEditKeyOfRow(key, id)) next.delete(key)
      for (const key of [...errs.keys()]) if (isEditKeyOfRow(key, id)) errs.delete(key)
    }
    editCellsRef.current = next
    editErrorsRef.current = errs
    setEditCells(new Map(next))
    setEditRowIds(prev => { const s = new Set(prev); for (const id of ids) s.delete(id); return s })
    setEditRowHeights(prev => { const m = new Map(prev); for (const id of ids) m.delete(id); return m })
  }, [])

  const redrawEditRow = useCallback((rowid: number) => {
    const api = gridRef.current?.api
    if (!api) return
    const node = api.getRowNode(`edit_${rowid}`)
    if (node) api.redrawRows({ rowNodes: [node] })
  }, [])

  const saveEditCell = useCallback(async (rowid: number, column: string) => {
    const key = editCellKey(rowid, column)
    const cell = editCellsRef.current.get(key)
    if (!cell || !cell.dirty) return

    // Mark saving → redraw to show spinner
    savingCellsRef.current = new Set(savingCellsRef.current).add(key)
    editErrorsRef.current = new Map(editErrorsRef.current)
    editErrorsRef.current.delete(key)
    redrawEditRow(rowid)

    const table = selectedTable
    const epoch = tableEpochRef.current
    const newValue = cell.isNull ? null : cell.value

    try {
      const res = await trackedWrite(
        () => adapter.cellSave(fileId, table, [{ column, rowid, newValue }]),
      )
      // Switched away while this was out. The refs have been cleared for the
      // new table, so anything written now belongs to the wrong one.
      //
      // No cleanup of our own saving key here, deliberately. The epoch only
      // moves on a real table change, and that change's effect clears the
      // whole set — while `key` is just `rowid:column`, with nothing in it to
      // say which table it was for. Deleting it would be a no-op at best, and
      // at worst would take out the key of a LIVE save on the same row and
      // column in the new table, dropping its spinner while the write was
      // still out.
      if (epoch !== tableEpochRef.current) return
      const cellResult = res.results?.find(r => r.column === column)
      if (cellResult?.ok) {
        // Nothing is recorded for undo here, deliberately — a saved write is
        // final in this grid, exactly as it is in every other database
        // manager. Undo reaches only what is still pending. See useGridEdits.
        // Remove this cell from editing (sync both ref and state)
        const next = new Map(editCellsRef.current)
        next.delete(key)
        editCellsRef.current = next
        setEditCells(new Map(next))
        // Clear dirty if no more dirty cells remain
        const stillDirty = [...next.values()].some(c => c.dirty)
        if (!stillDirty) { hasDirtyRef.current = false; onDirtyChange?.(false) }
        // If no cells remain for this row, close the edit row entirely
        const hasRowCells = [...next.keys()].some(k => isEditKeyOfRow(k, rowid))
        if (!hasRowCells) {
          setEditRowIds(prev => { const s = new Set(prev); s.delete(rowid); return s })
          setEditRowHeights(prev => { const m = new Map(prev); m.delete(rowid); return m })
          // Delay redraw to let React update editRowIds, then flash + clear purple
          setTimeout(() => {
            // Not if the table moved in those 50ms. The flash is keyed by
            // `__rowid`, and the table-change effect's clear has already run by
            // now — so writing it here puts the green "just saved" highlight on
            // whichever row of the NEW table shares the id. (The live-refresh
            // `.then` writes the same map after that clear too, and carries the
            // same guard for the same reason.)
            if (epoch !== tableEpochRef.current) return
            const api = gridRef.current?.api
            const node = api?.getRowNode(String(rowid))
            if (node && api) {
              flashRowsRef.current.set(String(rowid), 'updated')
              api.redrawRows({ rowNodes: [node] })
              setTimeout(() => { flashRowsRef.current.delete(String(rowid)) }, 1500)
            }
          }, 50)
        }
        savingCellsRef.current = new Set(savingCellsRef.current)
        savingCellsRef.current.delete(key)
        redrawEditRow(rowid)
        return
      } else {
        const errMsg = cellResult?.error || (res as any).error || 'Save failed'
        editErrorsRef.current = new Map(editErrorsRef.current).set(key, errMsg)
      }
    } catch (err: any) {
      // #1316 round 3: a dropped late answer is no failure for the person now here (`errorText` → null)
      const msg = errorText(err, 'Save failed')
      if (msg !== null) editErrorsRef.current = new Map(editErrorsRef.current).set(key, msg)
    }

    // Done saving → redraw to show result
    savingCellsRef.current = new Set(savingCellsRef.current)
    savingCellsRef.current.delete(key)
    redrawEditRow(rowid)
  }, [fileId, selectedTable, redrawEditRow, adapter, trackedWrite])

  const saveAllEditCells = useCallback(async () => {
    if (editRowIds.size === 0) return
    const cells = editCellsRef.current
    const dirty = [...cells.entries()].filter(([, c]) => c.dirty)
    if (dirty.length === 0) return
    setEditSaving(true)

    // Mark all dirty cells as saving → redraw
    for (const [key] of dirty) savingCellsRef.current.add(key)
    savingCellsRef.current = new Set(savingCellsRef.current)
    for (const rowid of editRowIds) redrawEditRow(rowid)

    const table = selectedTable
    const epoch = tableEpochRef.current
    let stale = false

    try {
      const updates = dirty.map(([key, c]) => {
        // Never `key.split(':')` — a Firebird rowid carries colons (#898).
        // Every writer of the map keys a cell by its own `column`, so this
        // always parses.
        const rowid = editKeyRowid(key, c.column)!
        return { column: c.column, rowid, newValue: c.isNull ? null : c.value }
      })
      const res = await trackedWrite(() => adapter.cellSave(fileId, table, updates))
      // Switched away while this was out. Not an early return: `setEditSaving`
      // below still has to run, or the Save button stays spinning forever.
      // (The rest of that cleanup is skipped on this path — see it.)
      // Everything inside belongs to `table`, and the refs now hold the new
      // one's cells — an error written here would land under whatever
      // occupies the same row and column there.
      if (epoch !== tableEpochRef.current) throw new StaleTableSave()
      if (res.ok) {
        logSavePurge({ savedCells: dirty.map(([key, c]) => ({ key, value: c.value, isNull: !!c.isNull })) })
        // Saved is final — see saveEditCell.
        // Transaction succeeded — close all editing cells/rows
        const next = new Map(editCellsRef.current)
        for (const [key] of dirty) next.delete(key)
        editCellsRef.current = next
        setEditCells(new Map(next))
        const stillDirty = [...next.values()].some(c => c.dirty)
        if (!stillDirty) { hasDirtyRef.current = false; onDirtyChange?.(false) }
        // Close rows that have no remaining cells
        const rowsToClose = new Set<number>()
        for (const rowid of editRowIds) {
          const hasRemaining = [...next.keys()].some(k => isEditKeyOfRow(k, rowid))
          if (!hasRemaining) rowsToClose.add(rowid)
        }
        if (rowsToClose.size > 0) {
          setEditRowIds(prev => { const s = new Set(prev); for (const r of rowsToClose) s.delete(r); return s })
          setEditRowHeights(prev => { const m = new Map(prev); for (const r of rowsToClose) m.delete(r); return m })
        }
      } else {
        // Transaction failed — mark the culprit cells with errors
        const nextErrors = new Map(editErrorsRef.current)
        let hasSpecificError = false
        for (const r of (res.results || [])) {
          if (r.error) {
            hasSpecificError = true
            // Match by column name — rowid may not be returned by server
            const matchKey = dirty.find(([, c]) => c.column === r.column)?.[0]
            if (matchKey) nextErrors.set(matchKey, r.error)
          }
        }
        // If no specific cell was identified, show generic error on all
        if (!hasSpecificError) {
          const errMsg = res.error || 'Save failed'
          for (const [key] of dirty) nextErrors.set(key, errMsg)
        }
        editErrorsRef.current = nextErrors
      }
    } catch (err: any) {
      if (err instanceof StaleTableSave) stale = true
      if (!(err instanceof StaleTableSave)) {
        // Network/parse error — show on all cells
        const errMsg = errorText(err, 'Save failed')
        if (errMsg !== null) {
          const nextErrors = new Map(editErrorsRef.current)
          for (const [key] of dirty) nextErrors.set(key, errMsg)
          editErrorsRef.current = nextErrors
        }
      }
    }

    // Clear saving state → redraw.
    //
    // NOT on the stale path, and this is the same rule `saveEditCell` states
    // where it declines to clean up its own key. These keys are
    // `rowid:column` with nothing in them to say which table they were for,
    // the table change has already emptied the set, and the one case where
    // the delete is not a no-op is the bad one: it takes out a LIVE save on
    // the same row and column in the new table and drops its spinner while
    // the write is still out. The two functions disagreed about this, which
    // is how it was found.
    if (!stale) {
      for (const [key] of dirty) savingCellsRef.current.delete(key)
      savingCellsRef.current = new Set(savingCellsRef.current)
      for (const rowid of editRowIds) redrawEditRow(rowid)
    }
    setEditSaving(false)
  }, [fileId, selectedTable, editRowIds, redrawEditRow, adapter, trackedWrite])
  saveAllEditCellsRef.current = saveAllEditCells

  /**
   * A delete mark is only good while its row is ON SCREEN.
   *
   * Called wherever rows land — a full load, a background refresh. Anything
   * marked that the new rows do not contain is dropped, from the staged set
   * and from the history with it.
   *
   * One rule instead of the four it replaces. The mark means "delete this row
   * I am looking at", so a row that is not in front of the user is not
   * something Save should act on, and it does not matter WHY it left: a
   * colleague deleted it, a filter excluded it, it moved to another page. The
   * version that tried to tell those apart needed to know whether the read had
   * seen the whole table, and grew a condition per case while the dangerous
   * one — a rowid SQLite has since handed to somebody else's row — stayed live
   * on any table too big for one page.
   *
   * The cost is plain and worth naming: page away from marked rows and the
   * marks are gone when you come back. Staged INSERTS are untouched — they
   * belong to the table, not to the window over it.
   */
  const pruneMarksTo = useCallback((rows: Array<Record<string, unknown>>) => {
    const marks = editsRef.current.deleteRowIds
    if (marks.size === 0) return
    const onScreen = new Set(rows.map(r => r.__rowid))
    const gone = [...marks].filter(id => !onScreen.has(id))
    if (gone.length > 0) editsRef.current.purge({ deleteRowIds: gone })
  }, [])
  const pruneMarksToRef = useRef(pruneMarksTo)
  pruneMarksToRef.current = pruneMarksTo

  /**
   * Close cell editors whose row is no longer in the table.
   *
   * The same rule as `pruneMarksTo`, for the other half of what the user has
   * staged. A cell editor open on a row somebody else has deleted is an
   * UPDATE aimed at a rowid the database is free to hand to the next row
   * inserted — the value lands in a stranger's row and the save reports
   * success.
   *
   * Lived inline in the file-event refresh until the reconnect reload needed
   * it too. Pruning marks there but not edits would have left exactly this
   * hole open on the path that reconnect takes.
   *
   * By column only when `columns` is given — the caller passes them only for
   * an UNRESTRICTED read (no subset), whose columns are the whole table. A
   * narrowed read returns only the SUBSET, and judging editors by it closed
   * one on a column merely hidden, keystrokes and all, on the next file
   * event. A subset column that is really gone is `forgetColumns`' (#899).
   */
  const pruneEditsTo = useCallback((rows: Array<Record<string, unknown>>, columns?: string[]) => {
    const editCells = editCellsRef.current
    if (editCells.size === 0) return
    // Compared as strings: the rowid a key yields and the `__rowid` a row
    // carries are the same value, but one may be a number and the other its
    // text.
    const freshById = new Set(rows.map(r => String(r.__rowid)))
    // An empty list is a zero-row read describing nothing — not a table with
    // no columns (every row is pruned by its rowid then anyway).
    const freshCols = columns && columns.length > 0 ? new Set(columns) : null
    let pruned = false
    const next = new Map(editCells)
    const activeRowIds = new Set<string>()
    for (const [key, cell] of next) {
      // The column is the cell's own, and the rowid is what is left — never
      // the first colon, which on a Firebird TIMESTAMP/TIME key is inside the
      // rowid (#898).
      const rowid = editKeyRowid(key, cell.column)
      // Exact, as `forgetColumns` is: Firebird's quoted names are case-sensitive.
      if (rowid === null || !freshById.has(String(rowid)) || (freshCols && !freshCols.has(cell.column))) {
        next.delete(key)
        pruned = true
      } else {
        activeRowIds.add(String(rowid))
      }
    }
    if (!pruned) return
    // Their errors go with them — keyed `rowid:column`, one left behind would
    // show on the next editor opened at that key.
    if (editErrorsRef.current.size > 0) {
      const errs = new Map(editErrorsRef.current)
      for (const key of errs.keys()) if (!next.has(key)) errs.delete(key)
      if (errs.size !== editErrorsRef.current.size) editErrorsRef.current = errs
    }
    editCellsRef.current = next
    setEditCells(new Map(next))
    // Close edit rows with no remaining cells
    setEditRowIds(prev => {
      const s = new Set(prev)
      for (const id of prev) { if (!activeRowIds.has(String(id))) s.delete(id) }
      return s.size !== prev.size ? s : prev
    })
    setEditRowHeights(prev => {
      const m = new Map(prev)
      for (const id of prev.keys()) { if (!activeRowIds.has(String(id))) m.delete(id) }
      return m.size !== prev.size ? m : prev
    })
    const stillDirty = [...next.values()].some(c => c.dirty)
    if (!stillDirty && hasDirtyRef.current) { hasDirtyRef.current = false; onDirtyChange?.(false) }
  }, [onDirtyChange])
  const pruneEditsToRef = useRef(pruneEditsTo)
  pruneEditsToRef.current = pruneEditsTo

  /** Redraw the given source rows so getRowClass re-runs for them. */
  const redrawRows = useCallback((rowIds: Iterable<number | string>) => {
    const api = gridRef.current?.api
    if (!api) return
    const nodes = [...rowIds].map(id => api.getRowNode(String(id))).filter(Boolean)
    if (nodes.length) api.redrawRows({ rowNodes: nodes as any })
  }, [])

  /**
   * Redraw whenever the set of rows marked for deletion changes.
   *
   * Marking a row changes only its CLASS — the row data is untouched — so AG
   * Grid has no reason to repaint it on its own. Doing this at the one place
   * the set can change covers every route into it: the delete key, Ctrl+Z,
   * redo, Discard, and the clearing that follows a save. Redrawing the union
   * of before and after is what makes the marks come OFF; redrawing only the
   * current set leaves a row that was just unmarked still painted red, which
   * is what Ctrl+Z looked like — the state had reverted, the screen had not.
   */
  const prevDeleteMarksRef = useRef<Set<number>>(new Set())
  useEffect(() => {
    const prev = prevDeleteMarksRef.current
    prevDeleteMarksRef.current = pendingDeleteRowIds
    const touched = new Set<number>([...prev, ...pendingDeleteRowIds])
    if (touched.size === 0) return
    // After the render that owns the new class, or getRowClass reads the old
    // value and paints exactly what we are trying to change.
    requestAnimationFrame(() => redrawRows(touched))
  }, [pendingDeleteRowIds, redrawRows])


  /**
   * Mark the selected rows for deletion. Nothing is sent yet.
   *
   * No confirmation here, deliberately: the mark is free to take back, and a
   * dialog in front of a reversible action trains people to dismiss dialogs.
   * The question is asked once, at Save, where the rows actually go.
   *
   * Marking an already-marked row unmarks it, so the same key both stages and
   * cancels — there is no separate "undelete" control to find.
   */
  const deleteSelectedRows = useCallback(async () => {
    if (!result || !selectedTable) return

    const targetRowIds = new Set<number>()

    // Rows from cell selection range
    const range = getCellRange()
    if (range) {
      for (let i = range.rowStart; i <= range.rowEnd; i++) {
        const rowid = result.rows[i]?.__rowid as number
        if (rowid != null) targetRowIds.add(rowid)
      }
    }

    // Rows currently being edited (source rows)
    for (const rowid of editRowIdsRef.current) targetRowIds.add(rowid)

    if (targetRowIds.size === 0) return

    edits.stage(current => {
      const allMarked = [...targetRowIds].every(id => current.deleteRowIds.has(id))
      const nextIds = new Set(current.deleteRowIds)
      for (const id of targetRowIds) {
        if (allMarked) nextIds.delete(id)
        else nextIds.add(id)
      }
      return { ...current, deleteRowIds: nextIds }
    })
  }, [result, selectedTable, getCellRange, edits])

  /**
   * Send the marked deletions. Called by Save, never on its own.
   *
   * Returns false when the user backs out at the confirmation, so Save can
   * stop before writing the cell edits too — half-applying a Save would be
   * worse than not applying it.
   */
  const applyPendingDeletes = useCallback(async (): Promise<boolean> => {
    const ids = [...editsRef.current.deleteRowIds]
    if (ids.length === 0 || !selectedTable) return true
    // BEFORE the confirmation, not after it. `confirm` is a real modal and the
    // wait is a person's, while `papan:db-set-table` keeps listening right
    // through it — so reading the epoch after the click reads the value the
    // table change has ALREADY bumped, and the guard below compares a number
    // with itself. This is the widest window in the function, not the request.
    const epoch = tableEpochRef.current

    const confirmed = await confirm({
      title: 'Delete Rows',
      message: `Delete ${ids.length} row${ids.length > 1 ? 's' : ''}? This is applied to the database and cannot be undone here — restore from a previous version if you need them back.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
    if (!confirmed) {
      setStatusMsg('Save cancelled — nothing was written')
      return false
    }

    const table = selectedTable
    // Reported in BOTH places on purpose. A toast is easy to miss and this
    // grid also mounts where there is no ToastProvider at all, and then a
    // refused delete looked like Save doing nothing. The status line is what
    // this screen used before and it stays put.
    const reportFailure = (msg: string) => {
      setStatusMsg(`Delete failed: ${msg}`)
      toast?.error(msg)
    }
    try {
      const res = await trackedWrite(() => adapter.rowDelete(fileId, table, ids))
      if (!res.ok) {
        reportFailure(res.error || 'Could not delete the selected rows')
        return false
      }
      // Switched away while this was out — the guard the two cell-save paths
      // already carry, which this one was missing. Not quite the same shape:
      // they capture the epoch with no await between the capture and the
      // write, while this one has the confirmation modal in between, so its
      // capture has to sit further up (see it).
      //
      // Everything below is keyed by `__rowid` alone, with nothing in it to
      // say which table it was for. On the old table none of it is needed any
      // more: the table-change effect has already closed its edit rows and
      // dropped its staged marks. So the only state left for it to reach is
      // the NEW table's, where `closeEditRows` would shut an editor the user
      // is typing in and `purge` would quietly un-mark a row they had just
      // marked — both on rows that merely share an id with the deleted ones.
      //
      // `loadTable` is skipped with them: the effect that moved the epoch has
      // started its own load already. The epoch tracks the whole scope — file,
      // version and table — but where this runs only the table can move: a
      // grid showing a version is forced out of edit mode (`activeMode`
      // below), so nothing can be staged and this never runs there, and
      // `fileId` is fixed per mount. Note the reason is that gate, NOT the
      // registry: `.fdb` has no preview registration, so a version preview of
      // one mounts THIS component with a `versionId` set.
      //
      // Returns false like every other partial outcome, so the staged inserts
      // and cell edits do not run. They were staged against the table that is
      // no longer open; the one that IS open has its own staged work, which
      // this Save was never asked to write.
      //
      // SAY what happened, because on this path nothing else does and one
      // thing already said the opposite.
      //
      // Everywhere else a delete needs no announcement: the rows vanish from
      // the grid in front of the user. Here they are looking at another table,
      // so the only evidence is off screen — and the scope change has already
      // told them "Unsaved grid changes were discarded", which is the reverse
      // of what became of these marks. They were applied.
      //
      // Not a failure report — the delete did exactly what was asked, and
      // dressing that up as a problem is what an earlier version of this got
      // wrong. A plain confirmation, naming the table they are no longer on.
      //
      // Both channels on purpose, and neither is reliable alone. The toast is
      // the one that persists; the status line here can be the last writer or
      // can be overwritten by whichever read lands next — the new table's own
      // load is NOT guaranteed to be that read, because this Save's write
      // bumped the read sequence past it.
      if (epoch !== tableEpochRef.current) {
        // The wording lives in `staleDeleteMessage`, with its own tests. It is
        // the one part of this branch that is pure, and the only part the
        // suite can reach: nothing here can be exercised without a rendered
        // grid and a faked scope change.
        const said = staleDeleteMessage(res, ids, table)
        // Not through `reportFailure`: it prefixes "Delete failed", and on this
        // branch the delete did not fail — the hedged wording is about what the
        // server could confirm, not about the rows surviving.
        setStatusMsg(said.text)
        if (said.tone === 'success') toast?.success(said.text)
        else toast?.error(said.text)
        // `false` STOPS the Save. Returning true here does not merely report
        // wrongly: `onSaveRef` falls through to the staged inserts and the cell
        // edits, and runs them against the table that is now on screen.
        //
        // Untested, and not for want of a fixture.
        // `e2e/drafts-survive-swap.spec.ts` already seeds a second table,
        // switches to it and edits a cell there; `db-grid-survives-reconnect`
        // stages delete marks through the real UI. Both were used.
        //
        // What is missing is a way to drive the UI INSIDE the window. Holding
        // the row-delete response open and switching tables, then trying to
        // stage work in the new table, was attempted two ways: a cell editor
        // (double click waits for a stability that never arrives; forced past
        // the check it lands elsewhere and opens nothing) and the Insert Row
        // dialog, chosen because a modal is not a grid cell — the click on the
        // Edit menu timed out the same way. So it is not the control and not
        // the fixture: while that response is held, Playwright's actionability
        // never settles.
        //
        // What would be worth trying next, since the harm IS reachable by hand
        // (Season reproduced it with the response delayed): drive the staging
        // through the command registry from `page.evaluate`, which needs no
        // actionable element at all.
        //
        // `staleDeleteMessage` is unit-tested; the wiring around it is not.
        // Flipping this return, or passing `selectedTableRef.current` instead
        // of `table`, both leave the whole suite green.
        return false
      }
      // `ok` is not "all of them". Firebird deletes row by row and commits
      // regardless, so a row somebody else removed first comes back as two
      // out of three. (SQLite's route is one transaction that throws on the
      // first missing row, so it never lands here.)
      //
      // The server now says WHICH ones went, so the rest of this is exact:
      // close the edit rows for those and only those, clear their marks and
      // only theirs, and leave the survivors marked for the user to retry or
      // unmark. Before, the count was all we had — the edit rows were left
      // open on rows that no longer existed, and the next Save wrote a cell
      // to a rowid the database may have reassigned.
      //
      // `deletedIds` absent or null means the backend could not tell (the
      // Firebird driver can answer "not applicable" for the affected count).
      // The COUNT still is read in that case — see `classifyRowDelete`.
      const outcome = classifyRowDelete(res, ids)
      if (outcome.kind === 'partial') {
        const gone = outcome.gone as number[]
        closeEditRows(gone)
        edits.purge({ deleteRowIds: gone })
        logSavePurge({ deleteRowIds: gone })
        await loadTable()
        // "Nothing else was saved" belongs here as much as on the branch
        // below. This one returns false too, so the staged inserts and the
        // cell edits in the same Save never ran — and saying only how many
        // rows went left people believing the rest had been written. It was
        // added to the sibling branch and missed here, and this is the branch
        // that actually happens: any Firebird table somebody else has deleted
        // from reaches it.
        reportFailure(`Deleted ${outcome.gone.length} of ${ids.length} rows — the rest were already gone. Nothing else was saved.`)
        return false
      }
      if (outcome.kind === 'unknown') {
        // The backend ran the deletes and cannot say what happened. Marks
        // stay — we have no idea which rows are still there — but the edit
        // rows close, because any of them may be gone and an editor on a
        // deleted row writes to a rowid the database can reassign.
        closeEditRows(ids)
        await loadTable()
        reportFailure(`Could not confirm which of the ${ids.length} rows were deleted — check the table. Nothing else was saved.`)
        return false
      }
      if (outcome.kind === 'short-unknown') {
        // No MARK is cleared: we know some rows survived but not which, and
        // clearing a mark for a survivor would tell the user it was deleted
        // when it was not.
        //
        // Every edit ROW is closed, though, and the asymmetry is the point.
        // A mark that stays is a delete the user can still see and take
        // back; a cell editor that stays is an UPDATE aimed at a rowid that
        // may already belong to somebody else's row. Some of these rows are
        // definitely gone, and the user asked for all of them to be deleted,
        // so an open editor on any of them has nothing left to write to.
        closeEditRows(ids)
        await loadTable()
        // Says outright that the rest of the Save did not run. Reporting only
        // the delete count left people believing their new rows and typed
        // cells had been written.
        reportFailure(`Deleted ${outcome.deleted} of ${ids.length} rows — check which are left. Nothing else was saved.`)
        return false
      }
    } catch (err: any) {
      reportFailure(err?.message || 'Could not delete the selected rows')
      return false
    }
    // Clear only the ids we SENT. A mark added while the request was in flight
    // is not covered by it, and blanking the whole set would drop that row's
    // deletion without ever asking the server — the user marked it, the mark
    // vanished, the row stayed.
    //
    // Only cleared once the server has taken them at all: a failed delete
    // leaves everything marked so the user can retry or unmark, rather than
    // silently forgetting what they asked for.
    // The rows are gone, so any cell edit still open on one is moot — and
    // writing it would land on a rowid the database has reassigned.
    //
    // HERE, not when the row was marked: marking is undoable and the staged
    // snapshot does not carry edit cells, so dropping them at mark time loses
    // work Ctrl+Z cannot restore. By this point the delete has succeeded.
    closeEditRows(ids)
    edits.purge({ deleteRowIds: ids })
    logSavePurge({ deleteRowIds: ids })
    await loadTable()
    return true
  }, [selectedTable, fileId, confirm, loadTable, adapter, trackedWrite, toast, closeEditRows, edits])
  const applyPendingDeletesRef = useRef(applyPendingDeletes)
  applyPendingDeletesRef.current = applyPendingDeletes

  /**
   * Send the staged inserts. Called by Save, after the deletions.
   *
   * One at a time because that is what the route takes, and it stops at the
   * first refusal: the rows still staged are exactly the ones that did not
   * land, so the grid keeps showing them and the user can fix or drop them.
   * Silently skipping a bad row and carrying on would leave them believing
   * everything saved.
   *
   * Reports through `insertIssueRef` rather than a return value — the caller
   * has to write the message AFTER the refreshes that follow a Save, which
   * would otherwise overwrite it with a row count.
   */
  const applyPendingInserts = useCallback(async (): Promise<void> => {
    const staged = editsRef.current.inserts
    if (staged.length === 0 || !selectedTable) return
    const table = selectedTable

    const fail = (msg: string) => { insertIssueRef.current = `Insert failed: ${msg}`; toast?.error(msg) }
    let landed: string[] = []
    // One request for the whole Save where the adapter can (SQLite, #295 step 2) — all or nothing, and a large db is
    // copied ONCE for the Save, not once per row. Firebird has no batch, so it falls back to a row at a time.
    if (adapter.rowInsertMany) {
      try {
        const res = await trackedWrite(() => adapter.rowInsertMany!(fileId, table, staged.map(i => ({ values: i.values, functions: i.functions }))))
        if (!res.ok) fail(res.error || 'Could not add the new rows')
        else landed = staged.map(i => i.tempId)
      } catch (err: any) {
        fail(err?.message || 'Could not add the new rows')
      }
    } else {
      for (const ins of staged) {
        try {
          const res = await trackedWrite(() => adapter.rowInsert(fileId, table, ins.values, ins.functions))
          if (!res.ok) { fail(res.error || 'Could not add the new row'); break }
          landed.push(ins.tempId)
        } catch (err: any) {
          fail(err?.message || 'Could not add the new row')
          break
        }
      }
    }

    if (landed.length > 0) {
      edits.purge({ insertTempIds: landed })
      logSavePurge({ insertTempIds: landed })
      await loadTable()
    }
  }, [selectedTable, fileId, adapter, trackedWrite, loadTable, toast])
  const applyPendingInsertsRef = useRef(applyPendingInserts)
  applyPendingInsertsRef.current = applyPendingInserts

  // Insert row dialog
  const [showInsertDialog, setShowInsertDialog] = useState(false)
  const [insertPrefill, setInsertPrefill] = useState<Record<string, unknown> | null>(null)

  // Schema context menu + dialogs
  const [schemaCtxMenu, setSchemaCtxMenu] = useState<{ x: number; y: number; table: string } | null>(null)
  const schemaCtxMenuRef = useRef<HTMLDivElement>(null)
  const [showCreateTable, setShowCreateTable] = useState(false)
  const [showModifyTable, setShowModifyTable] = useState<string | null>(null)
  /** The table ModifyTableDialog actually changed, if any — see its `onClose`. */
  const modifiedTableRef = useRef<string | null>(null)

  // Close schema context menu on outside click
  useEffect(() => {
    if (!schemaCtxMenu) return
    const onMouseDown = (e: MouseEvent) => {
      if (schemaCtxMenuRef.current && !schemaCtxMenuRef.current.contains(e.target as Node)) setSchemaCtxMenu(null)
    }
    window.addEventListener('mousedown', onMouseDown, true)
    return () => window.removeEventListener('mousedown', onMouseDown, true)
  }, [schemaCtxMenu])

  // Table operations
  const handleDropTable = useCallback(async (tableName: string) => {
    const confirmed = await confirm({
      title: 'Drop Table',
      message: `This will permanently drop table "${tableName}" and all its data. You can restore from a previous version.`,
      type: 'danger',
      confirmText: 'Drop',
      cancelText: 'Cancel',
      requiredInput: tableName,
    })
    if (!confirmed) return
    try {
      // Nothing to clear by hand: dropping the selected table moves the
      // selection, and the staged set is scoped to it (see scopeTo).
      const res = await adapter.tableDrop(fileId, tableName)
      if (res.ok) {
        if (selectedTable === tableName) {
          const remaining = schema.filter(t => t.name !== tableName)
          setSelectedTable(remaining[0]?.name || '')
        }
        refreshSchema()
      } else {
        setStatusMsg(`Drop failed: ${res.error}`)
      }
    } catch (err: any) {
      const text = errorText(err)
      if (text !== null) setStatusMsg(`Drop failed: ${text}`)
    }
  }, [fileId, selectedTable, schema, confirm, refreshSchema])

  const handleDuplicateTable = useCallback(async (tableName: string) => {
    let newName = `${tableName}_copy`
    const existing = schema.map(t => t.name)
    let i = 1
    while (existing.includes(newName)) { newName = `${tableName}_copy_${++i}` }
    try {
      const res = await adapter.tableDuplicate(fileId, tableName, newName)
      if (res.ok) {
        refreshSchema()
        setTimeout(() => selectTable(newName), 100)
      } else {
        setStatusMsg(`Duplicate failed: ${res.error}`)
      }
    } catch (err: any) {
      const text = errorText(err)
      if (text !== null) setStatusMsg(`Duplicate failed: ${text}`)
    }
  }, [fileId, schema, refreshSchema, selectTable])

  // Wire Ctrl+S to save all edits — this grid's own `window` listener, outside the command registry, so it must ask
  // for itself: only for the tab on screen (it listens on `window`, so every mounted grid answered every Cmd+S, in any
  // tab or workspace), and never while something COVERS the grid (#929 — a version preview over it: the shell refuses
  // the save with its notice, and this listener wrote the hidden staged cells anyway).
  const editorCovered = useEditorCovered()
  const saveGateRef = useRef({ isActive, editorCovered })
  saveGateRef.current = { isActive, editorCovered }
  useEffect(() => {
    if (!editable) return
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        // On screen = the active tab AND actually displayed: a workspace kept mounted behind `display:none`
        // (WorkspaceCache), or the chat home's hidden file view, still has an "active" tab — and no client rects.
        if (!saveGateRef.current.isActive || !dbModifierRef.current?.getClientRects().length) return
        e.preventDefault()
        if (saveGateRef.current.editorCovered) return
        saveAllEditCells()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [editable, saveAllEditCells])

  // Conflict resolution
  const resolveConflict = useCallback(async (action: 'mine' | 'theirs') => {
    if (!conflict) return
    if (action === 'mine') {
      // Force update: pass currentValue as oldValue
      // Writes straight through the conflict dialog, outside the recorded
      // paths. Leaving the previous entry on top would make the next Ctrl+Z
      // revert an OLDER edit while the user believes they are taking this one
      // back. (The dialog is unreachable today — nothing calls setConflict
      // with a value — but the guarantee should not depend on that.)
      await adapter.cellUpdate(fileId, conflict.table, conflict.column, conflict.rowid, conflict.newValue, conflict.currentValue)
    }
    setConflict(null)
    loadTable()
  }, [conflict, fileId, loadTable])

  // Lazy column loading — fetch columns/PKs/FKs on demand when adapter supports it
  const [tableInfoCache, setTableInfoCache] = useState<Map<string, { columns: ColumnInfo[]; foreignKeys: ForeignKey[] }>>(new Map())
  const [tableInfoLoading, setTableInfoLoading] = useState<Set<string>>(new Set())
  const loadTableInfo = useCallback((tableName: string) => {
    if (!adapter.getTableInfo || !tableName) return
    if (tableInfoCache.has(tableName)) return
    const schemaEntry = schema.find(t => t.name === tableName)
    if (schemaEntry?.columns && schemaEntry.columns.length > 0) return
    setTableInfoLoading(prev => new Set(prev).add(tableName))
    adapter.getTableInfo(fileId, tableName)
      .then(info => {
        setTableInfoCache(prev => new Map(prev).set(tableName, info))
      })
      .catch(() => {})
      .finally(() => {
        setTableInfoLoading(prev => { const next = new Set(prev); next.delete(tableName); return next })
      })
  }, [adapter, fileId, schema, tableInfoCache])

  // Auto-load table info when selecting a table
  useEffect(() => {
    if (selectedTable) loadTableInfo(selectedTable)
  }, [selectedTable, loadTableInfo])

  // Listen for AI-dispatched table/filter changes
  useEffect(() => {
    const tableHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      if (detail?.table && schema.some(t => t.name === detail.table)) {
        setSelectedTable(detail.table)
      }
    }
    const filterHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      const filter = detail?.filter ?? ''
      setRawWhere(filter)
      setAdvancedFilterLocal(true)
      // Open the filter panel so user can see what was applied
      setFilterOpenLocal(true)
      setTimeout(() => {
        setAppliedRawWhere(filter)
        setPage(0)
        setDroppedFilterColumns([])
      }, 0)
    }
    const columnsHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      const cols = detail?.columns as string[] ?? []
      setDraftColumns(cols)
      setSelectedColumns(cols)
    }
    window.addEventListener('papan:db-set-table', tableHandler)
    window.addEventListener('papan:db-set-filter', filterHandler)
    window.addEventListener('papan:db-set-columns', columnsHandler)
    return () => {
      window.removeEventListener('papan:db-set-table', tableHandler)
      window.removeEventListener('papan:db-set-filter', filterHandler)
      window.removeEventListener('papan:db-set-columns', columnsHandler)
    }
  }, [fileId, schema])

  // Enriched schema — merges lazy-loaded columns into schema for sidebar + grid
  const enrichedSchema = useMemo(() => {
    if (tableInfoCache.size === 0) return schema
    return schema.map(t => {
      if (t.columns.length > 0) return t
      const cached = tableInfoCache.get(t.name)
      if (!cached) return t
      return { ...t, columns: cached.columns, foreignKeys: cached.foreignKeys }
    })
  }, [schema, tableInfoCache])

  // Build column defs — use enriched schema
  const tableInfo = useMemo(() => enrichedSchema.find(t => t.name === selectedTable), [enrichedSchema, selectedTable])

  /**
   * Forget columns the table no longer has (#899) — from the column subset,
   * the filters (draft and applied) and the open cell editors.
   *
   * The subset and the filters name columns, and nothing else drops a name
   * when its column stops existing. Drop one that is in the subset, or still
   * filtered on, and every load after asks for it — the server refuses a
   * column the table does not have, and the grid showed the error in place
   * of the whole table. An editor on such a column rendered nowhere, kept the
   * tab "unsaved", and Save sent an UPDATE against the dropped column.
   *
   * Two sources call it, each with names that are really gone: the schema
   * effect below (a column that WAS in the table's list and left it) and
   * `loadTable` (columns the server refused). Names match EXACTLY: Firebird's
   * quoted identifiers are case-sensitive (`NAME` and `"name"` can be two
   * columns), and the server reports a refused name as it was asked for.
   *
   * Returns whether the subset or the filters changed — the change that
   * reloads the table. Never in a version preview: its grid writes the same
   * view-sync entry as the live editor, so forgetting what an OLD version
   * lacks would strip the user's subset and filters from the live grid too.
   */
  const forgetColumns = useCallback((names: Iterable<string>): boolean => {
    if (versionId) return false
    const gone = new Set(names)
    if (gone.size === 0) return false
    const keepCols = (prev: string[]) => prev.some(c => gone.has(c)) ? prev.filter(c => !gone.has(c)) : prev
    const keepFilters = (prev: TableFilter[]) => withoutFilterColumns(prev, gone)
    const hitCols = selectedColumnsRef.current.some(c => gone.has(c))
    const droppedFilters = appliedFiltersRef.current.filter(f => gone.has(f.column)).map(f => f.column)
    const hitFilters = droppedFilters.length > 0
    if (hitFilters) setDroppedFilterColumns(prev => [...new Set([...prev, ...droppedFilters])])
    setDraftColumns(keepCols)
    setSelectedColumns(keepCols)
    setDraftFilters(keepFilters)
    setAppliedFilters(keepFilters)
    // Fewer filters can mean more rows: start from the first page, as
    // applying filters does.
    if (hitFilters) setPage(0)

    const cells = editCellsRef.current
    const closing = [...cells.entries()].filter(([, c]) => gone.has(c.column)).map(([k]) => k)
    if (closing.length > 0) {
      const next = new Map(cells)
      const errs = new Map(editErrorsRef.current)
      for (const k of closing) { next.delete(k); errs.delete(k) }
      editCellsRef.current = next
      editErrorsRef.current = errs
      setEditCells(new Map(next))
      const emptied = [...editRowIdsRef.current].filter(id => ![...next.keys()].some(k => isEditKeyOfRow(k, id)))
      if (emptied.length > 0) {
        setEditRowIds(prev => { const s = new Set(prev); for (const id of emptied) s.delete(id); return s })
        setEditRowHeights(prev => { const m = new Map(prev); for (const id of emptied) m.delete(id); return m })
      }
      // The unsaved marker follows from `setEditCells` — the dirty effect
      // counts staged deletes and inserts too, which clearing it here would not.
    }
    return hitCols || hitFilters
  }, [versionId])
  const forgetColumnsRef = useRef(forgetColumns)
  forgetColumnsRef.current = forgetColumns

  /**
   * Columns that LEFT the selected table's list are forgotten — keyed on the
   * list, so it covers every route that changes a schema: the Modify Table
   * dialog, the SQL pane, the AI, a collaborator.
   *
   * Only names that were in the previous list and are not in this one: the
   * list is the schema's (`PRAGMA table_info`), which omits generated and
   * hidden columns and knows one spelling, while the server answers those
   * too — a name the list never had is the server's call (`loadTable`'s
   * refusal), not this effect's. No list (a table's info still loading,
   * Firebird's lazily fetched columns being re-fetched) compares nothing and
   * keeps the last one, so a drop seen across that gap still counts.
   */
  const tableColumnNames = tableInfo?.columns.map(c => c.name).join('\u0000') ?? ''
  const lastColumnListRef = useRef<{ table: string; names: Set<string> } | null>(null)
  useEffect(() => {
    if (!tableColumnNames || !selectedTable) return
    const names = new Set(tableColumnNames.split('\u0000'))
    const last = lastColumnListRef.current
    lastColumnListRef.current = { table: selectedTable, names }
    if (!last || last.table !== selectedTable) return
    const left = [...last.names].filter(n => !names.has(n))
    if (left.length > 0) forgetColumnsRef.current(left)
  }, [tableColumnNames, selectedTable])
  /**
   * The columns to show.
   *
   * `result.columns` is EMPTY for a zero-row read — the server has no row to
   * describe — so an empty table with a staged insert rendered a grid with
   * nothing but the row-number column, and the values the user had just typed
   * were invisible. The table's own schema knows the columns whether or not
   * any row exists.
   */
  const displayColumns = useMemo(() => {
    if (result?.columns?.length) return result.columns
    // Only once the rows on screen belong to the table this schema describes.
    // `tableInfo` follows the SELECTION, so switching away from an empty table
    // swapped in the incoming table's headers a render early and showed the
    // staged row under them as all-NULL for a frame.
    if (loadedTable !== selectedTable) return []
    const all = tableInfo?.columns.map(c => c.name) ?? []
    // Respect a column subset — otherwise a filtered view with no rows falls
    // back to showing every column in the table, which is not what the user
    // asked to see.
    // Filtered the user's list, not the schema's: `result.columns` follows the
    // order they picked, and falling back to schema order would reshuffle the
    // grid the moment a filtered view happened to return no rows.
    return selectedColumns.length > 0 ? selectedColumns.filter(c => all.includes(c)) : all
  }, [result?.columns, tableInfo, selectedColumns, loadedTable, selectedTable])

  const columnDefs: ColDef[] = useMemo(() => {
    const storedWidths = columnWidthsRef.current
    const dataCols = displayColumns.map(col => {
      const colInfo = tableInfo?.columns.find(c => c.name === col)
      const isPk = colInfo?.pk
      const colType = colInfo?.type?.toUpperCase() || ''
      const isBinaryBlob = colType === 'BLOB' // binary BLOB — not editable
      return {
        field: col,
        headerName: col,
        sortable: !isBinaryBlob,
        resizable: true,
        minWidth: 80,
        width: storedWidths.get(col) || 150,
        editable: false,
        headerClass: isPk ? 'ag-header-pk' : undefined,
        cellRenderer: (params: any) => {
          const val = params.value
          if (val === null) return <span className="text-zinc-500 italic">NULL</span>
          if (isBinaryBlob) return <span className="text-zinc-500 italic text-[10px]">(BLOB)</span>
          if (typeof val === 'object') return JSON.stringify(val)
          return String(val)
        },
      }
    })
    // Row number column — dynamic width based on max number
    const maxRowNum = (resultRef.current?.totalCount ?? resultRef.current?.rows.length ?? 0)
    const digits = Math.max(2, String(maxRowNum).length)
    const rowNumWidth = digits * 8 + 10
    const rowNumCol: ColDef = {
      colId: '__rownum',
      headerName: '',
      width: rowNumWidth,
      minWidth: rowNumWidth,
      maxWidth: rowNumWidth,
      sortable: false,
      resizable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      cellStyle: { textAlign: 'center', cursor: 'pointer', userSelect: 'none', overflow: 'visible', textOverflow: 'clip', padding: '0 4px' },
      headerClass: 'ag-header-row-num',
      valueGetter: (params: any) => {
        if (params.data?.__editRow) return ''
        const rows = resultRef.current?.rows
        const idx = rows?.findIndex((r: any) => r.__rowid === params.data?.__rowid)
        return idx != null && idx >= 0 ? idx + 1 + (page * limit) : ''
      },
      cellRenderer: (params: any) => {
        // A staged row gets its own control instead of a number — it has no
        // position in the table yet, and Delete is the wrong key for it: there
        // is nothing in the database to delete.
        //
        // Its OWN control, deliberately. Routing this through Delete meant one
        // action reconciling five inputs — the selection range, the open edit
        // rows, the clicked row, the focused cell and the context menu — and
        // every round of review found another pair that disagreed. A button on
        // the row needs none of them.
        if (params.data?.__pendingInsert) {
          const tempId = String(params.data.__rowid)
          return (
            <button
              type="button"
              aria-label="Remove this unsaved row"
              title="Remove this unsaved row"
              className="text-[11px] leading-none themed-text-muted hover:text-red-400"
              onClick={(e) => {
                e.stopPropagation()
                editsRef.current.stage(current => ({
                  ...current,
                  inserts: current.inserts.filter(i => i.tempId !== tempId),
                }))
              }}
            >
              ×
            </button>
          )
        }
        const val = params.value
        return val ? <span className="text-[10px] themed-text-muted">{val}</span> : null
      },
    }
    return [rowNumCol, ...dataCols]
  }, [displayColumns, tableInfo, page, limit, colWidthsVersion])

  // Force AG Grid remount when column set changes (AG Grid doesn't reliably add/remove columns via props)
  //
  // Derived from `displayColumns`, which is what the grid actually renders.
  // Keyed on `result.columns` it never changed when the schema fallback kicked
  // in, so the empty-table case could keep a grid built for no columns at all —
  // and every zero-row table shared the key '', so switching between two of
  // them reused one grid.
  //
  // The table part is the one the DATA came from, not the one just selected.
  // `selectedTable` flips a render before its rows arrive, which remounted the
  // grid and re-fitted the OLD table's columns — manual widths lost, a visible
  // jump — and then remounted again when the new result landed.
  // NUL-joined throughout: a table or column name may contain `:` or `,` (I2).
  const columnListKey = [loadedTable, ...displayColumns].join('\u0000')

  // Clear stored column widths when column set changes (new table or schema change) — flex:1 re-applies
  const prevColumnListKeyRef = useRef(columnListKey)
  if (prevColumnListKeyRef.current !== columnListKey) {
    columnWidthsRef.current = new Map()
    prevColumnListKeyRef.current = columnListKey
  }

  // After column set changes, sync edit row widths once grid settles
  useEffect(() => {
    if (editRowIds.size === 0) return
    const timer = setTimeout(() => {
      const api = gridRef.current?.api
      if (!api) return
      const widthMap = new Map<string, number>()
      for (const gc of (api.getColumns?.() || [])) widthMap.set(gc.getColId(), gc.getActualWidth())
      const editRowIds_ = new Set([...editRowIds].map(id => `edit_${id}`))
      document.querySelectorAll('.ag-full-width-row').forEach(el => {
        if (!editRowIds_.has(el.getAttribute('row-id') || '')) return
        el.querySelectorAll('[data-col]').forEach(cell => {
          const w = widthMap.get((cell as HTMLElement).dataset.col || '')
          if (w) (cell as HTMLElement).style.width = `${w}px`
        })
      })
    }, 100)
    return () => clearTimeout(timer)
  }, [columnListKey, editRowIds])

  // Copy selection to clipboard
  const copySelection = useCallback(() => {
    const range = getCellRange()
    if (!range || !result) return false
    const cols = result.columns.filter((_: string, i: number) => i >= range.colStart && i <= range.colEnd)
    const rows = result.rows.filter((_: any, i: number) => i >= range.rowStart && i <= range.rowEnd)
    if (rows.length === 0 || cols.length === 0) return false
    const text = rows.map((row: any) => cols.map((col: string) => {
      const val = row[col]
      if (val === null || val === undefined) return ''
      return String(val)
    }).join('\t')).join('\n')
    navigator.clipboard.writeText(text)
    return true
  }, [getCellRange, result])

  const hasSelection = selectionRef.current !== null

  // Context menu
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; row?: Record<string, unknown> } | null>(null)
  const lastClickedRowRef = useRef<Record<string, unknown> | null>(null)
  const ctxMenuRef = useRef<HTMLDivElement>(null)

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return
    const onMouseDown = (e: MouseEvent) => {
      if (ctxMenuRef.current && !ctxMenuRef.current.contains(e.target as Node)) setCtxMenu(null)
    }
    window.addEventListener('mousedown', onMouseDown, true)
    return () => window.removeEventListener('mousedown', onMouseDown, true)
  }, [ctxMenu])

  // Viewport clamp context menu
  useEffect(() => {
    if (!ctxMenu || !ctxMenuRef.current) return
    const el = ctxMenuRef.current
    const rect = el.getBoundingClientRect()
    let { x, y } = ctxMenu
    if (x + rect.width > window.innerWidth - 4) x = window.innerWidth - rect.width - 4
    if (y + rect.height > window.innerHeight - 4) y = window.innerHeight - rect.height - 4
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }, [ctxMenu])

  // Expose copy/delete for Edit menu integration
  useImperativeHandle(ref, () => ({
    copy: () => { copySelection() },
    delete: () => { deleteSelectedRows() },
    getState: () => ({
      // Staged changes are part of what is on screen, and an agent describing
      // the table without them describes something the user is not looking at:
      // rows struck through for deletion, rows added that the server has never
      // heard of.
      staged: {
        deleteRowIds: [...editsRef.current.deleteRowIds],
        insertCount: editsRef.current.inserts.length,
      },
      table: selectedTable,
      columns: selectedColumns,
      where: appliedRawWhere,
      limit,
      page,
      selectedRange: selectionRef.current ?? undefined,
    }),
    setColumns: (cols: string[]) => {
      setDraftColumns(cols)
      setSelectedColumns(cols)
    },
  }), [copySelection, deleteSelectedRows, selectedTable, selectedColumns, appliedRawWhere, limit, page])

  // Focus scope — clicking anywhere in the DB table modifier activates db:grid commands.
  // Unique per file so multiple DB tabs don't clash.
  const { scopeId: dbScopeId, parentScopeId: dbParentScopeId } = dbGridScope(fileId, versionId)
  const dbModifierRef = useRef<HTMLDivElement>(null)
  const keyboardRegionRef = useMemo(() => ({ current: keyboardRegion ?? null }), [keyboardRegion])
  useFocusScope(dbScopeId, keyboardRegion ? keyboardRegionRef : dbModifierRef)

  // Register DB grid commands — single stable registration, reads ALL state from refs.
  const copySelectionRef = useRef(copySelection)
  copySelectionRef.current = copySelection
  const deleteSelectedRowsRef = useRef(deleteSelectedRows)
  deleteSelectedRowsRef.current = deleteSelectedRows
  const editableRef = useRef(editable)
  editableRef.current = editable
  const canCopyRef = useRef(canCopy)
  canCopyRef.current = canCopy
  const sqlModeRef = useRef(!!sqlMode)
  sqlModeRef.current = !!sqlMode

  useEffect(() => {
    if (!cmdRegistry) return
    return cmdRegistry.registerScope({
      scopeId: dbScopeId,
      parentScopeId: dbParentScopeId,
      get commands(): Command[] {
        // SQL mode: no grid commands (future: SQL-specific commands)
        if (sqlModeRef.current) return [
          { id: 'edit.cut', label: 'Cut', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true },
          { id: 'edit.paste', label: 'Paste', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true },
          { id: 'edit.delete', label: 'Delete', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true },
        ]
        const hasSel = selectionRef.current !== null
        const hasEditRows = editRowIdsRef.current.size > 0
        const isEditable = editableRef.current
        const cmds: Command[] = [
          { id: 'edit.copy', label: 'Copy', icon: Copy, shortcut: { key: 'c', mod: true }, execute: () => copySelectionRef.current(), enabled: hasSel && canCopyRef.current, menuGroup: 'edit', menuOrder: 10 },
          { id: 'edit.cut', label: 'Cut', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true },
          { id: 'edit.paste', label: 'Paste', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true },
        ]
        if (isEditable) {
          cmds.push(
            { id: 'db.insertRow', label: 'Insert Row', icon: Plus, shortcut: { key: 'i', mod: true }, execute: () => { setInsertPrefill(null); setShowInsertDialog(true) }, enabled: true, menuGroup: 'edit', menuOrder: 30, separator: 'before' },
            { id: 'db.duplicateRow', label: 'Duplicate Row', icon: CopyPlus, execute: () => { const row = clickedRowIfPresent(); if (row) { const p: Record<string, unknown> = {}; for (const [k, v] of Object.entries(row)) { if (k !== '__rowid') p[k] = v }; setInsertPrefill(p) } else { setInsertPrefill(null) }; setShowInsertDialog(true) }, enabled: hasSel || hasEditRows, menuGroup: 'edit', menuOrder: 31 },
            { id: 'edit.delete', label: 'Delete Rows', icon: Trash2, shortcut: { key: 'Backspace' }, execute: () => deleteSelectedRowsRef.current(), enabled: hasSel || hasEditRows, menuGroup: 'edit', menuOrder: 50, danger: true, separator: 'before' },
          )
        } else {
          cmds.push({ id: 'edit.delete', label: 'Delete', execute: () => {}, enabled: false, menuGroup: 'edit', hidden: true })
        }
        return cmds
      },
    })
  }, [cmdRegistry]) // eslint-disable-line react-hooks/exhaustive-deps
  // Single registration on mount — getter reads all state from refs, never stale


  // Ctrl+C copy handler
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // #900 — where a command registry dispatches keys (the workspace, the single-file view on screen), Cmd+C is its
      // `edit.copy` (this same copySelection) with all its rules, and this listener stands aside. Elsewhere (the share
      // view, mobile) it answers as before, with two additions: never while this grid is not DISPLAYED (a keep-alive
      // workspace behind display:none — its provider dispatches nothing — would otherwise take the visible pane's
      // copy), and never a keystroke something else already handled.
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && canCopyRef.current && !cmdRegistry?.dispatchesKeys
        && !e.defaultPrevented && !!dbModifierRef.current?.getClientRects().length) {
        if (copySelection()) e.preventDefault()
      }
      // Escape clears selection
      if (e.key === 'Escape' && selectionRef.current) {
        selectionRef.current = null
        setSelectionVersion(v => v + 1)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [copySelection])

  // Memoize rowData so typing in edit cells doesn't trigger AG Grid re-render
  const gridRowData = useMemo(() => {
    if (!result) return []
    // Staged rows render in VIEW mode as well. They are still staged, still
    // keep the tab dirty and are still sent by Save, and the delete marks stay
    // painted — hiding only half the staged set on a mode flip told the user
    // their new rows were gone while Save was still going to write them.
    if (!editable && pendingInserts.length === 0) return result.rows
    // Always build array in edit mode so removing the last edit row
    // is a diff (remove one pseudo-row) instead of a full array swap
    const rows: Record<string, unknown>[] = []
    for (const row of result.rows) {
      rows.push(row)
      if (editable && editRowIds.has(row.__rowid as number)) {
        rows.push({ __rowid: `edit_${row.__rowid}`, __editRow: true, __sourceRowId: row.__rowid })
      }
    }
    // Staged inserts go last, where a new row would land anyway. Every column
    // is present (null when the dialog left it out) so the cell renderers see
    // the same shape they see for a real row.
    for (const ins of pendingInserts) {
      const row: Record<string, unknown> = { __rowid: ins.tempId, __pendingInsert: true }
      for (const col of displayColumns) row[col] = ins.values[col] ?? null
      rows.push(row)
    }
    return rows
  }, [result, editable, editRowIds, pendingInserts, displayColumns])

  return (
    <div ref={dbModifierRef} className="flex-1 flex overflow-hidden">
      {/* Schema sidebar */}
      {schemaOpen && (
        <div className="w-52 shrink-0 themed-bg-secondary themed-border border-r flex flex-col overflow-hidden">
          <div className="h-9 flex items-center px-3 text-xs font-medium themed-text-muted border-b themed-border shrink-0">
            <span className="flex-1">Tables ({schema.length})</span>
            {editable && (
              <button onClick={() => setShowCreateTable(true)} className="themed-text-muted hover:themed-text" title="Create table">
                <Plus size={14} />
              </button>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {enrichedSchema.map(t => {
              const isExpanded = expandedTables.has(t.name)
              const isSelected = selectedTable === t.name
              return (
                <div
                  key={t.name}
                  // The table's name also appears as a toolbar button, so a
                  // test looking it up by text can land on the wrong one — and
                  // right-clicking the toolbar does not open this menu.
                  data-db-schema-table={t.name}
                  onContextMenu={editable ? (e) => { e.preventDefault(); setSchemaCtxMenu({ x: e.clientX, y: e.clientY, table: t.name }) } : undefined}
                >
                  <div className={`flex items-center hover:themed-bg-primary transition-colors group ${isSelected ? 'themed-bg-primary' : ''}`}>
                    <button
                      onClick={() => { loadTableInfo(t.name); setExpandedTables(prev => {
                        const next = new Set(prev)
                        next.has(t.name) ? next.delete(t.name) : next.add(t.name)
                        return next
                      }) }}
                      className="pl-2 py-1.5 shrink-0 themed-text-muted"
                    >
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                    <button
                      onClick={() => selectTable(t.name)}
                      className={`flex-1 text-left pr-1 py-1.5 text-sm flex items-center gap-1.5 min-w-0 ${isSelected ? 'text-blue-400' : 'themed-text'}`}
                    >
                      <Table2 size={12} className="shrink-0" />
                      <span className="truncate flex-1">{t.name}</span>
                      <span className="text-xs opacity-50">{t.rowCount < 0 ? '…' : t.rowCount}</span>
                    </button>
                    {editable && (
                      <button
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect()
                          setSchemaCtxMenu({ x: rect.right, y: rect.top, table: t.name })
                        }}
                        className="pr-2 py-1.5 shrink-0 themed-text-muted opacity-50 group-hover:opacity-70 hover:!opacity-100 transition-opacity"
                      >
                        <MoreVertical size={12} />
                      </button>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="pl-8 pr-2 pb-1">
                      {t.columns.length === 0 && tableInfoLoading.has(t.name) && (
                        <div className="flex items-center gap-1.5 py-1 themed-text-muted">
                          <Loader2 size={10} className="animate-spin" />
                          <span className="text-[10px]">Loading columns...</span>
                        </div>
                      )}
                      {t.columns.map(col => {
                        const isColSelected = draftColumns.includes(col.name)
                        const isCurrentTable = t.name === selectedTable
                        return (
                          <button
                            key={col.name}
                            onClick={() => {
                              if (!isCurrentTable) return
                              setDraftColumns(prev => {
                                if (prev.length === 0) {
                                  // First click: select all except this one? No — select just this one
                                  return [col.name]
                                }
                                if (prev.includes(col.name)) {
                                  return prev.filter(c => c !== col.name)
                                }
                                return [...prev, col.name]
                              })
                            }}
                            className={`w-full flex items-center gap-1 text-xs py-0.5 transition-colors ${
                              isCurrentTable ? 'cursor-pointer hover:themed-text' : 'cursor-default'
                            } ${isColSelected ? 'text-amber-300' : 'themed-text-muted'}`}
                          >
                            {col.pk && <Key size={9} className="text-amber-400 shrink-0" />}
                            <span className="truncate">{col.name}</span>
                            <span className="ml-auto text-[10px] opacity-50">{col.type || 'any'}</span>
                          </button>
                        )
                      })}
                      {t.foreignKeys.length > 0 && (
                        <div className="mt-0.5 pt-0.5 border-t themed-border">
                          {t.foreignKeys.map((fk, i) => (
                            <div key={i} className="text-[10px] themed-text-muted opacity-60">
                              {fk.from} → {fk.table}.{fk.to}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="h-9 flex items-center gap-2 px-3 themed-bg-secondary themed-border border-b shrink-0">
          <Tooltip always placement="top" className="flex items-center" content={schemaOpen ? 'Hide schema' : 'Show schema'}>
            <button
              onClick={() => setSchemaOpen(!schemaOpen)}
              className="text-xs themed-btn-secondary px-1.5 py-0.5 rounded"
            >
              <Table2 size={13} />
            </button>
          </Tooltip>

          {/* Filter/WHERE buttons moved to top bar — kept here as hidden for standalone use */}

          {canCopy && (
            <Tooltip always placement="top" className="flex items-center" content={`Copy selection (${formatShortcut({ key: 'c', mod: true })})`}>
              <button
                onClick={() => { if (copySelection()) { /* visual feedback could go here */ } }}
                disabled={!hasSelection}
                className="text-xs themed-btn-secondary px-1.5 py-0.5 rounded disabled:opacity-30 flex items-center gap-1"
              >
                <Copy size={13} />
              </button>
            </Tooltip>
          )}

          {editable && (
            <>
              <Tooltip always placement="top" className="flex items-center" content="Insert new row">
                <button
                  onClick={() => { setInsertPrefill(null); setShowInsertDialog(true) }}
                  className="text-xs themed-btn-secondary px-1.5 py-0.5 rounded flex items-center gap-1"
                >
                  <Plus size={13} />
                </button>
              </Tooltip>
              <Tooltip always placement="top" className="flex items-center" content="Duplicate selected row">
                <button
                  onClick={() => {
                    const row = clickedRowIfPresent()
                    if (row) {
                      const p: Record<string, unknown> = {}
                      for (const [k, v] of Object.entries(row)) { if (k !== '__rowid') p[k] = v }
                      setInsertPrefill(p)
                    } else {
                      setInsertPrefill(null)
                    }
                    setShowInsertDialog(true)
                  }}
                  disabled={!hasSelection && editRowIds.size === 0}
                  className="text-xs themed-btn-secondary px-1.5 py-0.5 rounded disabled:opacity-30 flex items-center gap-1"
                >
                  <CopyPlus size={13} />
                </button>
              </Tooltip>
              <Tooltip always placement="top" className="flex items-center" content="Delete selected rows">
                <button
                  onClick={deleteSelectedRows}
                  disabled={!hasSelection && editRowIds.size === 0}
                  className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${!hasSelection && editRowIds.size === 0 ? 'themed-btn-secondary opacity-30' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}
                >
                  <Trash2 size={13} />
                </button>
              </Tooltip>
            </>
          )}

          <div className="flex-1" />

          {/* Always mounted: a live region created together with its text is
              usually not announced. Says "no longer in the table", not
              "dropped" — a rename and a name that was never there look the same. */}
          {(() => {
            const note = droppedFilterColumns.length > 0
              ? t('database.filterDropped', { count: droppedFilterColumns.length, columns: droppedFilterColumns.join(', ') })
              : ''
            return <span className="text-xs themed-text-muted truncate max-w-72" role="status" title={note || undefined}>{note}</span>
          })()}
          {statusMsg && <span className="text-xs themed-text-muted truncate max-w-48">{statusMsg}</span>}

          {/* Pagination */}
          {result && (result.totalCount ?? 0) > limit && (
            <div className="flex items-center gap-1 text-xs">
              <Tooltip always placement="top" className="flex items-center" content="Previous page">
                <button onClick={() => goPage(page - 1)} disabled={page === 0} className="themed-btn-secondary px-1 py-0.5 rounded disabled:opacity-30">
                  <ChevronLeft size={14} />
                </button>
              </Tooltip>
              <span className="themed-text-muted px-1">{page + 1}/{totalPages}</span>
              <Tooltip always placement="top" className="flex items-center" content="Next page">
                <button onClick={() => goPage(page + 1)} disabled={page >= totalPages - 1} className="themed-btn-secondary px-1 py-0.5 rounded disabled:opacity-30">
                  <ChevronRight size={14} />
                </button>
              </Tooltip>
            </div>
          )}

          {/* Limit */}
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(0) }}
            className="text-xs themed-bg-primary themed-text themed-border border rounded px-1.5 py-0.5 outline-none"
          >
            {[50, 100, 200, 500, 1000].map(n => (
              <option key={n} value={n}>{n} rows</option>
            ))}
          </select>

          {/* Export */}
          {canCopy && result && result.rows.length > 0 && (
            <ExportDropdown
              columns={result.columns}
              rows={result.rows}
              types={result.types}
              onExportToDrive={onExportToDrive}
              parentId={parentId}
              baseName={`${fileName?.replace(/\.[^.]+$/, '') || 'db'}_${selectedTable}`}
              onExportFullTable={adapter.exportTable ? (fmt) => adapter.exportTable!(fileId, selectedTable, fmt, { columns: selectedColumns.length > 0 ? selectedColumns : undefined, where: appliedRawWhere || undefined }) : undefined}
            />
          )}
        </div>

        {/* Filter panel */}
        {filterOpen && (() => {
          const safeTbl = `"${selectedTable.replace(/"/g, '""')}"`
          const allColumns = tableInfo?.columns || []

          // Column tags — shared between basic and advanced modes
          const available = allColumns.filter(c => !draftColumns.includes(c.name))
          const colSuggestions = colInput.trim()
            ? available.filter(c => c.name.toLowerCase().includes(colInput.toLowerCase()))
            : available

          const renderColumnTags = (showSelectLabel: boolean) => (
              <div className="px-3 py-1.5 themed-bg-secondary themed-border border-b">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-xs font-mono themed-text-muted mr-1 shrink-0 opacity-50">
                    {showSelectLabel
                      ? `SELECT${draftColumns.length === 0 ? ' *' : ''}`
                      : 'COLUMNS'}
                  </span>
                  {draftColumns.map((col) => (
                    <span
                      key={col}
                      draggable
                      onDragStart={e => {
                        dragColRef.current = col
                        setDraggingCol(col)
                        e.dataTransfer.effectAllowed = 'move'
                      }}
                      onDragOver={e => e.preventDefault()}
                      onDragEnter={() => {
                        const from = dragColRef.current
                        if (!from || from === col) return
                        setDraftColumns(prev => {
                          const next = prev.filter(c => c !== from)
                          const targetIdx = next.indexOf(col)
                          if (targetIdx === -1) return prev
                          next.splice(targetIdx, 0, from)
                          return next
                        })
                      }}
                      onDragEnd={() => { dragColRef.current = null; setDraggingCol(null) }}
                      className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border cursor-grab active:cursor-grabbing select-none ${
                        draggingCol === col ? 'border-amber-400 font-bold opacity-70' : 'border-amber-500/20'
                      }`}
                    >
                      {col}
                      <button
                        onClick={() => setDraftColumns(prev => prev.filter(c => c !== col))}
                        className="hover:text-red-400"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <div
                    className="relative flex-1 min-w-[60px]"
                    onDragOver={e => e.preventDefault()}
                    onDragEnter={() => {
                      if (!dragColRef.current) return
                      setDraftColumns(prev => {
                        const next = prev.filter(c => c !== dragColRef.current!)
                        next.push(dragColRef.current!)
                        return next
                      })
                    }}
                  >
                    <input
                      value={colInput}
                      onChange={e => { setColInput(e.target.value); setColHighlight(0) }}
                      onKeyDown={e => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault()
                          setColHighlight(prev => Math.min(prev + 1, colSuggestions.length - 1))
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault()
                          setColHighlight(prev => Math.max(prev - 1, 0))
                        } else if (e.key === 'Enter' && colSuggestions.length > 0) {
                          e.preventDefault()
                          const pick = colSuggestions[colHighlight] || colSuggestions[0]
                          setDraftColumns(prev => [...prev, pick.name])
                          setColInput('')
                          setColHighlight(0)
                        } else if (e.key === 'Escape') {
                          (e.target as HTMLInputElement).blur()
                        } else if (e.key === 'Backspace' && !colInput && draftColumns.length > 0) {
                          setDraftColumns(prev => prev.slice(0, -1))
                        }
                      }}
                      placeholder={draftColumns.length === 0 ? 'Type column name or click in sidebar' : ''}
                      className="w-full text-xs bg-transparent themed-text outline-none placeholder:themed-text-muted"
                      onFocus={() => setColInputFocused(true)}
                      onBlur={() => setTimeout(() => setColInputFocused(false), 150)}
                    />
                    {colInputFocused && colSuggestions.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 z-20 rounded border themed-border shadow-lg overflow-hidden" style={{ backgroundColor: '#2a2a2e' }}>
                        {colSuggestions.slice(0, 8).map((c, ci) => (
                          <button
                            key={c.name}
                            onMouseDown={e => {
                              e.preventDefault()
                              setDraftColumns(prev => [...prev, c.name])
                              setColInput('')
                              setColHighlight(0)
                            }}
                            className={`block w-full text-left text-xs px-2 py-1 text-amber-300 font-mono ${ci === colHighlight ? 'bg-amber-500/20' : 'hover:bg-amber-500/10'}`}
                          >
                            {c.name} <span className="opacity-40">{c.type}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {draftColumns.length > 0 && (
                    <button
                      onClick={() => { setDraftColumns([]); setSelectedColumns([]); setColInput(''); setPage(0) }}
                      className="text-[10px] themed-text-muted hover:text-red-400"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
          )

          // Advanced mode: raw WHERE input
          if (advancedFilter) {
            return (
              <>
                {renderColumnTags(true)}
                <div className="px-3 py-2 themed-bg-secondary themed-border border-b">
                  <div className="flex items-center gap-1 text-xs font-mono themed-text-muted">
                    <span className="opacity-50">FROM {safeTbl} WHERE</span>
                </div>
                <div className="relative mt-1 themed-bg-primary rounded">
                  <pre
                    className="absolute inset-0 text-xs font-mono px-2 py-1.5 overflow-hidden pointer-events-none whitespace-pre-wrap break-words rounded"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: highlightSQL(rawWhere) + '\n' }}
                  />
                  <input
                    value={rawWhere}
                    onChange={e => setRawWhere(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
                    className="w-full text-xs font-mono themed-border border rounded px-2 py-1.5 outline-none focus:border-brand-500 bg-transparent relative z-10"
                    style={{ color: rawWhere ? 'transparent' : undefined, caretColor: '#e4e4e7' }}
                    placeholder="e.g. created_at > '2026-01-01' AND status IN ('active', 'pending')"
                  />
                </div>
                <div className="flex items-center gap-1 text-xs font-mono themed-text-muted mt-1">
                  <span className="opacity-50">LIMIT {limit}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={applyFilters}
                    disabled={!rawWhere.trim() && draftColumns.length === 0}
                    className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
                  >
                    Apply
                  </button>
                  <button
                    onClick={clearFilters}
                    className="text-xs themed-text-muted hover:text-red-400 transition-colors"
                  >
                    Clear all
                  </button>
                  {onSendToSql && (
                    <button
                      onClick={() => {
                        const cols = draftColumns.length > 0 ? draftColumns.join(', ') : '*'
                        onSendToSql(`SELECT ${cols} FROM ${safeTbl} WHERE ${rawWhere.trim()} LIMIT ${limit};`)
                      }}
                      className="text-xs text-brand-400 hover:text-brand-300 transition-colors ml-auto"
                    >
                      Send to SQL
                    </button>
                  )}
                </div>
              </div>
              </>
            )
          }

          // Group filters by AND — OR breaks groups
          const groups: TableFilter[][] = []
          draftFilters.forEach((f, i) => {
            if (i === 0 || f.logic === 'or') groups.push([f])
            else groups[groups.length - 1].push(f)
          })
          const columns = tableInfo?.columns || []

          return (
            <>
              {renderColumnTags(false)}
              <div className="px-3 py-2 themed-bg-secondary themed-border border-b space-y-2">
              {groups.map((group, gi) => (
                <div key={gi}>
                  {/* OR separator between groups */}
                  {gi > 0 && (
                    <div className="flex items-center h-5 mb-2">
                      <select
                        value="or"
                        onChange={e => {
                          if (e.target.value === 'and') {
                            // Merge this group into previous by changing first item's logic
                            setDraftFilters(prev => prev.map(p => p.id === group[0].id ? { ...p, logic: 'and' } : p))
                          }
                        }}
                        className="text-[10px] text-brand-400 bg-transparent border-none outline-none cursor-pointer"
                      >
                        <option value="and">AND</option>
                        <option value="or">OR</option>
                      </select>
                    </div>
                  )}
                  {/* AND group box */}
                  <div className={`${group.length > 1 ? 'rounded border border-brand-500/20 bg-brand-500/5 p-2 space-y-1' : ''}`}>
                    {group.map((f, fi) => {
                      const opInfo = FILTER_OPERATORS.find(o => o.value === f.operator)
                      return (
                        <div key={f.id}>
                          {/* AND connector inside group */}
                          {fi > 0 && (
                            <div className="flex items-center h-5 my-0.5">
                              <select
                                value="and"
                                onChange={e => {
                                  if (e.target.value === 'or') {
                                    setDraftFilters(prev => prev.map(p => p.id === f.id ? { ...p, logic: 'or' } : p))
                                  }
                                }}
                                className="text-[10px] text-brand-400 bg-transparent border-none outline-none cursor-pointer"
                              >
                                <option value="and">AND</option>
                                <option value="or">OR</option>
                              </select>
                            </div>
                          )}
                          {/* Filter row */}
                          <div className="flex items-center gap-1.5">
                            <select
                              value={f.column}
                              onChange={e => setDraftFilters(prev => prev.map(p => p.id === f.id ? { ...p, column: e.target.value } : p))}
                              className="text-xs themed-bg-primary themed-text themed-border border rounded px-1.5 py-1 outline-none min-w-[100px]"
                            >
                              {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                            </select>
                            <select
                              value={f.operator}
                              onChange={e => setDraftFilters(prev => prev.map(p => p.id === f.id ? { ...p, operator: e.target.value as FilterOperator } : p))}
                              className="text-xs themed-bg-primary themed-text themed-border border rounded px-1.5 py-1 outline-none"
                            >
                              {FILTER_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                            </select>
                            {opInfo?.needsValue !== false && (
                              <input
                                value={f.value}
                                onChange={e => setDraftFilters(prev => prev.map(p => p.id === f.id ? { ...p, value: e.target.value } : p))}
                                onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
                                className="text-xs themed-bg-primary themed-text themed-border border rounded px-2 py-1 outline-none flex-1 min-w-[80px]"
                                placeholder="value"
                              />
                            )}
                            <button
                              onClick={() => setDraftFilters(prev => prev.filter(p => p.id !== f.id))}
                              className="themed-text-muted hover:text-red-400 p-0.5"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
              <div className="mt-1.5">
                <button
                  onClick={() => {
                    if (columns.length === 0) return
                    setDraftFilters(prev => [...prev, { id: genFilterId(), column: columns[0].name, operator: 'equals', value: '', logic: prev.length > 0 ? 'and' : undefined }])
                  }}
                  className="flex items-center gap-1 text-xs themed-text-muted hover:themed-text transition-colors"
                >
                  <Plus size={12} /> Add filter
                </button>
                {(draftFilters.length > 0 || draftColumns.length > 0) && (
                  <div className="flex items-center gap-2 pt-1.5">
                    <button
                      onClick={applyFilters}
                      className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-500"
                    >
                      Apply
                    </button>
                    <button
                      onClick={clearFilters}
                      className="text-xs themed-text-muted hover:text-red-400 transition-colors"
                    >
                      Clear all
                    </button>
                    {onSendToSql && (
                      <button
                        onClick={() => {
                          const tbl = `"${selectedTable.replace(/"/g, '""')}"`
                          const cols = draftColumns.length > 0 ? draftColumns.join(', ') : '*'
                          const where = filtersToWhere(draftFilters)
                          onSendToSql(`SELECT ${cols} FROM ${tbl}${where} LIMIT ${limit};`)
                        }}
                        className="text-xs text-brand-400 hover:text-brand-300 transition-colors ml-auto"
                      >
                        Send to SQL
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            </>
          )
        })()}

        {/* Schema changes auto-reload via unified diff in onFileEvent */}

        {/* Conflict popover */}
        {conflict && (
          <div className="mx-3 my-2 p-3 rounded border border-amber-500/50 bg-amber-500/10 text-sm">
            <div className="font-medium text-amber-400 mb-2">
              {conflict.reason === 'row_deleted' ? 'Row was deleted externally' : 'Cell was modified externally'}
            </div>
            {conflict.reason === 'value_changed' && (
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs mb-3 font-mono">
                <span className="themed-text-muted">Was:</span>
                <span>{String(conflict.oldValue ?? 'NULL')}</span>
                <span className="themed-text-muted">Now:</span>
                <span className="text-amber-300">{String(conflict.currentValue ?? 'NULL')}</span>
                <span className="themed-text-muted">Yours:</span>
                <span className="text-blue-300">{String(conflict.newValue ?? 'NULL')}</span>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => resolveConflict('mine')} className="text-xs px-3 py-1 rounded bg-blue-600 text-white">
                Use mine
              </button>
              <button onClick={() => resolveConflict('theirs')} className="text-xs px-3 py-1 rounded themed-btn-secondary">
                Use theirs
              </button>
              <button onClick={() => setConflict(null)} className="text-xs px-3 py-1 rounded themed-btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Results grid */}
        <div className="flex-1 relative">
          {queryLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}

          {result?.error ? (
            <div className="p-4 text-red-400 text-sm font-mono whitespace-pre-wrap">{result.error}</div>
          ) : result && (result.rows.length > 0 || pendingInserts.length > 0) ? (
            <AgGridReact
              key={columnListKey}
              ref={gridRef}
              theme={editable ? editableTheme : readonlyTheme}
              columnDefs={columnDefs}
              rowData={gridRowData}
              headerHeight={32}
              rowHeight={28}
              suppressMovableColumns
              suppressCellFocus={!editable}
              preventDefaultOnContextMenu
              postSortRows={postSortRowsRef.current}
              onCellContextMenu={(e) => {
                if (e.data?.__editRow) return
                lastClickedRowRef.current = e.data
                const ev = e.event as MouseEvent
                ev.preventDefault()
                setCtxMenu({ x: ev.clientX, y: ev.clientY, row: e.data })
              }}
              onCellMouseDown={(e) => {
                if (e.data?.__editRow) return
                lastClickedRowRef.current = e.data
                if (e.data?.__pendingInsert) {
                  // A staged row can never be part of a range — it is not in
                  // `result.rows`. Leaving the PREVIOUS range standing meant
                  // Backspace afterwards marked rows the user had stopped
                  // pointing at.
                  //
                  // Both buttons. Keeping the range for the right button was
                  // the wrong way to make the context menu open: the menu
                  // resolves its own row now, so it no longer needs a leftover
                  // selection — and a Delete taken from a menu opened on a
                  // green row must not act on whatever was highlighted before.
                  selectionRef.current = null
                  setSelectionVersion(v => v + 1)
                  return
                }
                if ((e.event as MouseEvent)?.button === 2) return
                const rowIdx = result?.rows.findIndex(r => r.__rowid === e.data?.__rowid) ?? -1
                const colId = e.column.getColId()
                const isShift = (e.event as MouseEvent)?.shiftKey
                // Click on row number column → select entire row (shift extends)
                if (colId === '__rownum' || !result?.columns.includes(colId)) {
                  if (rowIdx < 0 || !result) return
                  if (isShift && selectionRef.current) {
                    selectionRef.current = { ...selectionRef.current, startCol: 0, endRow: rowIdx, endCol: result.columns.length - 1 }
                  } else {
                    selectionRef.current = { startRow: rowIdx, startCol: 0, endRow: rowIdx, endCol: result.columns.length - 1 }
                  }
                  setSelectionVersion(v => v + 1)
                  return
                }
                const colIdx = result?.columns.indexOf(colId) ?? -1
                if (rowIdx < 0 || colIdx < 0) return
                // Shift-click on data cell extends selection
                if (isShift && selectionRef.current) {
                  selectionRef.current = { ...selectionRef.current, endRow: rowIdx, endCol: colIdx }
                } else {
                  selectionRef.current = { startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx }
                  console.log('[DB CELL CLICK] Set selectionRef.current:', selectionRef.current, 'selectionRef identity:', selectionRef)
                }
                setSelectionVersion(v => v + 1)
              }}
              onCellMouseOver={(e) => {
                if (!selectionRef.current || e.data?.__editRow || !(e.event as MouseEvent)?.buttons) return
                const rowIdx = result?.rows.findIndex(r => r.__rowid === e.data?.__rowid) ?? -1
                if (rowIdx < 0) return
                const colId = e.column.getColId()
                // Dragging over row number column → extend full-row selection
                if (colId === '__rownum' || !result?.columns.includes(colId)) {
                  selectionRef.current = { ...selectionRef.current, startCol: 0, endRow: rowIdx, endCol: result!.columns.length - 1 }
                } else {
                  const colIdx = result?.columns.indexOf(colId) ?? -1
                  if (colIdx < 0) return
                  selectionRef.current = { ...selectionRef.current, endRow: rowIdx, endCol: colIdx }
                }
                setSelectionVersion(v => v + 1)
              }}
              defaultColDef={{
                cellClass: (params: any) => {
                  if (params.data?.__editRow) return ''
                  const range = getCellRange()
                  if (!range || !result) return ''
                  const rowIdx = result.rows.findIndex((r: any) => r.__rowid === params.data?.__rowid)
                  const colIdx = result.columns.indexOf(params.column.getColId())
                  if (rowIdx >= range.rowStart && rowIdx <= range.rowEnd && colIdx >= range.colStart && colIdx <= range.colEnd) {
                    return 'db-cell-selected'
                  }
                  return ''
                },
              }}
              onFirstDataRendered={(e) => {
                // One-time fit columns to grid width (replaces flex:1 which resets on re-render)
                if (columnWidthsRef.current.size > 0) return
                e.api.sizeColumnsToFit()
                const allCols = e.api.getColumns?.() || []
                for (const gc of allCols) {
                  const id = gc.getColId()
                  if (id !== '__rownum') columnWidthsRef.current.set(id, gc.getActualWidth())
                }
                setColWidthsVersion(v => v + 1)
              }}
              onColumnResized={(e) => {
                // Store all column widths on resize finish so columnDefs uses them instead of defaults
                if (e.finished !== false) {
                  const allCols = e.api.getColumns?.() || []
                  for (const gc of allCols) {
                    const id = gc.getColId()
                    if (id !== '__rownum') columnWidthsRef.current.set(id, gc.getActualWidth())
                  }
                  if (e.source === 'uiColumnResized' || e.source === 'uiColumnDragged') {
                    setColWidthsVersion(v => v + 1)
                  }
                }
                // Sync ALL full-width (edit) row cell widths via DOM — no rowId matching needed
                const fullWidthRows = document.querySelectorAll('.ag-full-width-row')
                if (fullWidthRows.length > 0) {
                  const widthMap = new Map<string, number>()
                  for (const gc of (e.api.getColumns?.() || [])) widthMap.set(gc.getColId(), gc.getActualWidth())
                  fullWidthRows.forEach(el => {
                    el.querySelectorAll('[data-col]').forEach(cell => {
                      const w = widthMap.get((cell as HTMLElement).dataset.col || '')
                      if (w) (cell as HTMLElement).style.width = `${w}px`
                    })
                  })
                }
              }}
              onCellDoubleClicked={(e) => {
                if (e.data?.__editRow) return
                // A staged row has no rowid in the database, so `openEditCell`
                // has nothing to open — without this, double-clicking one in
                // View mode flipped the whole tab to Edit and then did
                // nothing. Its values are corrected by removing it with the ×
                // and adding it again.
                if (e.data?.__pendingInsert) return
                const col = e.column.getColId()
                const rowid = e.data?.__rowid
                if (rowid == null || col === '__rowid') return
                if (editable) {
                  openEditCell(rowid, col)
                } else if (onRequestEdit) {
                  // View mode with write permission — switch to Edit and queue cell open
                  pendingEditCellRef.current = { rowid, column: col }
                  onRequestEdit()
                }
              }}
              isFullWidthRow={(params) => !!params.rowNode.data?.__editRow}
              getRowHeight={(params) => {
                if (params.data?.__editRow) return editRowHeights.get(params.data.__sourceRowId) || 80
                return 28
              }}
              context={{ result, tableInfo, editCellsRef, editErrorsRef, savingCellsRef, editRowHeights, editRowIds, openEditCell, saveEditCell, saveAllEditCells, setEditCells, setEditRowIds, setEditRowHeights, cornerDraggingRef, gridRef, editSaving, onDirtyChange, hasDirtyRef, selectedColumns, columnWidthsRef, setColWidthsVersion }}
              fullWidthCellRenderer={EditRowRenderer}
              getRowId={(params) => String(params.data.__rowid)}
              getRowClass={(params) => {
                const rowid = params.data?.__rowid
                if (params.data?.__editRow) return ''
                const isPendingDelete = pendingDeleteRowIds.has(rowid)
                const flash = flashRowsRef.current.get(String(rowid))
                const isEditing = editRowIds.has(rowid)
                const deleteClass = isPendingDelete ? 'db-pending-delete' : ''
                if (params.data?.__pendingInsert) return 'db-pending-insert'
                const editClass = isEditing ? 'db-editing-source' : ''
                if (flash === 'updated') return `db-flash-updated ${editClass} ${deleteClass}`
                if (flash === 'added') return `db-flash-added ${editClass} ${deleteClass}`
                if (flash === 'deleted') return 'db-flash-deleted'
                return `${editClass} ${deleteClass}`
              }}
            />
          ) : result ? (
            <div className="p-4 themed-text-muted text-sm">No rows returned</div>
          ) : (
            <div className="p-4 themed-text-muted text-sm">Select a table to view data</div>
          )}

          {/* Context menu */}
          {ctxMenu && (hasSelection || (editable && !!ctxMenu.row?.__pendingInsert)) && createPortal(
            <div
              ref={ctxMenuRef}
              // Rendered in a portal on document.body, so a test cannot reach
              // it through the viewer. This is what it narrows to instead of
              // looking the label up page-wide.
              data-db-ctx-menu=""
              // A popup menu: while it is open the command registry's shortcuts stand aside (#900).
              role="menu"
              className={`fixed themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[160px] ${themeClass}`}
              style={{ left: ctxMenu.x, top: ctxMenu.y }}
            >
              {/* Copy works off the selection RANGE, and a staged row never
                  makes one — the item would sit there doing nothing. */}
              {canCopy && hasSelection && (
                <button
                  onClick={() => { copySelection(); setCtxMenu(null) }}
                  className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                >
                  <Copy size={14} />
                  Copy
                  <span className="ml-auto text-[11px] themed-text-muted">{formatShortcut({ key: 'c', mod: true })}</span>
                </button>
              )}
              {editable && (
                <>
                  <div className="my-1 border-t themed-border" />
                  <button
                    onClick={() => { setCtxMenu(null); setInsertPrefill(null); setShowInsertDialog(true) }}
                    className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                  >
                    <Plus size={14} />
                    New Row
                  </button>
                  <button
                    onClick={() => {
                      // Through the same guard as everywhere else: the menu
                      // can outlive the row it was opened on — right-click a
                      // staged row, press Ctrl+Z, then choose Duplicate.
                      const row = rowIfUsable(ctxMenu?.row)
                      setCtxMenu(null)
                      if (row) {
                        const p: Record<string, unknown> = {}
                        for (const [k, v] of Object.entries(row)) { if (k !== '__rowid') p[k] = v }
                        setInsertPrefill(p)
                      } else {
                        setInsertPrefill(null)
                      }
                      setShowInsertDialog(true)
                    }}
                    className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
                  >
                    <CopyPlus size={14} />
                    Duplicate
                  </button>
                  {/* A staged row is not in the database, so "Delete" would
                      be a lie and `deleteSelectedRows` has nothing to act on.
                      It offers the same thing its × does instead. */}
                  {ctxMenu.row?.__pendingInsert === true ? (
                    <button
                      onClick={() => {
                        // Through the same guard as everywhere else: the menu
                        // outlives the row it was opened on — right-click a
                        // staged row, press Ctrl+Z, then choose this.
                        const row = rowIfUsable(ctxMenu?.row)
                        setCtxMenu(null)
                        if (!row) return
                        const tempId = String(row.__rowid)
                        editsRef.current.stage(current => ({
                          ...current,
                          inserts: current.inserts.filter(i => i.tempId !== tempId),
                        }))
                      }}
                      className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:themed-bg-secondary flex items-center gap-2"
                    >
                      <Trash2 size={14} />
                      Remove unsaved row
                    </button>
                  ) : (
                    <button
                      onClick={() => { setCtxMenu(null); deleteSelectedRows() }}
                      className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:themed-bg-secondary flex items-center gap-2"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  )}
                </>
              )}
            </div>,
            document.body,
          )}
        </div>

        {/* Insert row dialog */}
        {showInsertDialog && selectedTable && (
          <InsertRowDialog
            themeClass={themeClass}
            columns={enrichedSchema.find(t => t.name === selectedTable)?.columns || []}
            foreignKeys={enrichedSchema.find(t => t.name === selectedTable)?.foreignKeys}
            prefill={insertPrefill}
            onInsert={async (values, functions) => {
              // Staged, not sent. The row appears at the bottom of the grid in
              // green and reaches the database when the user saves — the same
              // deal the marked-for-deletion rows get, and for the same
              // reason: nothing irreversible happens behind a single click.
              //
              // Async and returning the dialog's result shape because that is
              // its contract; there is simply nothing to await now.
              edits.stage(current => ({
                ...current,
                inserts: [...current.inserts, {
                  tempId: edits.nextTempId(),
                  values,
                  functions,
                }],
              }))
              setShowInsertDialog(false)
              return { ok: true }
            }}
            onClose={() => setShowInsertDialog(false)}
          />
        )}

        {/* Schema context menu */}
        {schemaCtxMenu && createPortal(
          <div
            ref={schemaCtxMenuRef}
            // Portalled onto document.body, so a test cannot reach it through
            // the viewer. Same reason as `data-db-ctx-menu` on the grid's menu.
            data-db-schema-ctx-menu=""
            className={`fixed themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[160px] ${themeClass}`}
            style={{ left: schemaCtxMenu.x, top: schemaCtxMenu.y }}
          >
            <button
              onClick={() => { setSchemaCtxMenu(null); setShowModifyTable(schemaCtxMenu.table) }}
              className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
            >
              <Pencil size={14} />
              Modify Table
            </button>
            <button
              onClick={() => { setSchemaCtxMenu(null); handleDuplicateTable(schemaCtxMenu.table) }}
              className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
            >
              <CopyPlus size={14} />
              Duplicate Table
            </button>
            <div className="my-1 border-t themed-border" />
            <button
              onClick={() => { setSchemaCtxMenu(null); handleDropTable(schemaCtxMenu.table) }}
              className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:themed-bg-secondary flex items-center gap-2"
            >
              <Trash2 size={14} />
              Drop Table
            </button>
          </div>,
          document.body,
        )}

        {/* Create Table dialog */}
        {showCreateTable && (
          <CreateTableDialog
            themeClass={themeClass}
            existingTables={schema.map(t => t.name)}
            onCreate={async (name, columns) => {
              try {
                const res = await adapter.tableCreate(fileId, name, columns)
                if (res.ok) {
                  setShowCreateTable(false); refreshSchema(); setTimeout(() => selectTable(name), 100)
                }
                return res
              } catch (err: any) { return { ok: false, error: err.message } }
            }}
            onClose={() => setShowCreateTable(false)}
          />
        )}

        {/* Modify Table dialog */}
        {showModifyTable && (
          <ModifyTableDialog
            themeClass={themeClass}
            tableName={showModifyTable}
            tableInfo={enrichedSchema.find(t => t.name === showModifyTable)}
            onModify={async (action, opts) => {
              try {
                const res = await adapter.tableModify(fileId, showModifyTable!, action, opts)
                if (res.ok) {
                  // Remember that something actually landed. `onClose` fires
                  // for the backdrop, the X and the Close button alike — the
                  // dialog has no way to tell the parent whether the user
                  // changed anything — so without this a stray click on the
                  // backdrop ran a revalidating load and closed cell editors
                  // for no reason at all.
                  modifiedTableRef.current = showModifyTable!
                  // A lazily loaded table's columns (Firebird) live in this
                  // cache, and `enrichedSchema` prefers it — so without this a
                  // dropped column stayed listed and the #899 reconcile never
                  // saw it go. File events clear the cache; this path may
                  // publish none in time.
                  // Deleted, never refetched in place: a refetch that fails
                  // (or asks for the name a rename just moved away from) keeps
                  // the pre-DDL entry, and nothing would replace it. The
                  // selected table's auto-load re-fetches it; another table's
                  // loads when it is next opened.
                  const modified = showModifyTable!
                  setTableInfoCache(prev => {
                    if (!prev.has(modified)) return prev
                    const m = new Map(prev); m.delete(modified); return m
                  })
                  void refreshSchema()
                  if (action === 'rename_table' && opts.newName) {
                    setShowModifyTable(opts.newName)
                    // The LIVE table, not the one captured when Apply was
                    // clicked. This function awaits the server, and the AI
                    // can move the grid through `papan:db-set-table` while it
                    // waits — a `window` listener with nothing to say a modal
                    // is open. Trusting the captured value then did two wrong
                    // things at once: it yanked the user off the table they
                    // were now on, AND it pre-wrote the scope, so the effect
                    // saw "nothing changed" and kept THAT table's staged
                    // delete marks, cell editors and errors under the renamed
                    // one — marks pointing at another table's rowids, which
                    // is the exact harm this whole mechanism exists to stop.
                    //
                    // Both halves are skipped together when the table has
                    // moved on: the rename still happened on the server and
                    // the sidebar shows it through `refreshSchema`, but
                    // nobody is dragged anywhere.
                    if (selectedTableRef.current === showModifyTable) {
                      // Move the scope to the new name FIRST. The
                      // table-change effect resets on a scope it has not seen
                      // before; writing the new one here means the rename
                      // arrives as "nothing changed", so the open cell
                      // editors, what is typed in them and the staged rows
                      // all stay. Same rows, same rowids — nothing about them
                      // depends on what the table is called.
                      //
                      // The check and the write are one synchronous tick, so
                      // no listener can interleave between them.
                      prevScopeRef.current = `${fileId}:${versionId ?? ''}:${opts.newName}`
                      // And the modified-table mark moves with it: `onClose`
                      // compares it against `selectedTable`, which is about
                      // to become the new name, so leaving the old one meant
                      // a rename never revalidated — and a column dropped
                      // earlier in the same dialog session stayed open,
                      // dirty, rendering nowhere.
                      modifiedTableRef.current = opts.newName
                      setSelectedTable(opts.newName)
                    }
                  }
                }
                return res
              } catch (err: any) { return { ok: false, error: err.message } }
            }}
            // Revalidating, but ONLY if this dialog changed the table being
            // viewed. It can DROP or rename the column an editor is open on,
            // and then the cell has to go — left behind it is dirty, renders
            // nowhere, and Save sends an UPDATE against a column that no
            // longer exists.
            //
            // The two conditions are both load-bearing. `onClose` fires on
            // cancel as much as on apply, and the dialog is opened from the
            // schema sidebar's context menu on ANY table — so revalidating
            // unconditionally meant that opening Modify on some other table
            // and clicking the backdrop threw away a value you had typed into
            // the table you were actually looking at.
            onClose={() => {
              const changedThisTable = modifiedTableRef.current != null && modifiedTableRef.current === selectedTable
              modifiedTableRef.current = null
              setShowModifyTable(null)
              refreshSchema()
              loadTable(undefined, undefined, undefined, changedThisTable ? { revalidate: true } : undefined)
            }}
          />
        )}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// CreateTableDialog
// ---------------------------------------------------------------------------

interface NewColumn {
  name: string; type: string; pk: boolean; notnull: boolean; defaultValue: string; unique: boolean
}

function CreateTableDialog({ themeClass, existingTables, onCreate, onClose }: {
  themeClass: string
  existingTables: string[]
  onCreate: (name: string, columns: NewColumn[]) => Promise<{ ok: boolean; error?: string }>
  onClose: () => void
}) {
  const [tableName, setTableName] = useState('')
  const [columns, setColumns] = useState<NewColumn[]>([
    { name: 'id', type: 'INTEGER', pk: true, notnull: false, defaultValue: '', unique: false },
  ])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const addColumn = () => setColumns(prev => [...prev, { name: '', type: 'TEXT', pk: false, notnull: false, defaultValue: '', unique: false }])
  const removeColumn = (i: number) => setColumns(prev => prev.filter((_, j) => j !== i))
  const updateColumn = (i: number, patch: Partial<NewColumn>) => setColumns(prev => prev.map((c, j) => j === i ? { ...c, ...patch } : c))

  const handleSubmit = async () => {
    if (!tableName.trim()) { setError('Table name required'); return }
    if (existingTables.includes(tableName.trim())) { setError('Table already exists'); return }
    if (columns.length === 0 || columns.every(c => !c.name.trim())) { setError('At least one column required'); return }
    setSaving(true); setError(null)
    const res = await onCreate(tableName.trim(), columns.filter(c => c.name.trim()))
    if (!res.ok) setError(res.error || 'Failed')
    setSaving(false)
  }

  const TYPES = ['INTEGER', 'TEXT', 'REAL', 'BLOB', 'NUMERIC', 'BOOLEAN', 'DATETIME', 'DATE', 'TIME', 'VARCHAR', 'FLOAT']

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative w-[600px] max-h-[80vh] flex flex-col themed-bg-secondary border themed-border shadow-xl rounded-lg animate-scale-in`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">Create Table</h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
          <div>
            <label className="text-xs font-medium themed-text mb-1 block">Table Name</label>
            <input
              type="text" value={tableName} onChange={e => setTableName(e.target.value)}
              placeholder="my_table" disabled={saving}
              className="w-full text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border"
            />
          </div>
          <div>
            <label className="text-xs font-medium themed-text mb-2 block">Columns</label>
            <div className="space-y-2">
              {columns.map((col, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="text" value={col.name} onChange={e => updateColumn(i, { name: e.target.value })}
                    placeholder="column_name" disabled={saving}
                    className="flex-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border" />
                  <select value={col.type} onChange={e => updateColumn(i, { type: e.target.value })} disabled={saving}
                    className="text-xs px-2 py-1.5 rounded themed-input border themed-border w-24">
                    {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <Tooltip always placement="top" className="flex items-center" content="Primary Key">
                    <label className="flex items-center gap-0.5 text-[10px] themed-text-muted cursor-pointer">
                      <input type="checkbox" checked={col.pk} onChange={e => updateColumn(i, { pk: e.target.checked })} disabled={saving} />PK
                    </label>
                  </Tooltip>
                  <Tooltip always placement="top" className="flex items-center" content="Not Null">
                    <label className="flex items-center gap-0.5 text-[10px] themed-text-muted cursor-pointer">
                      <input type="checkbox" checked={col.notnull} onChange={e => updateColumn(i, { notnull: e.target.checked })} disabled={saving} />NN
                    </label>
                  </Tooltip>
                  <Tooltip always placement="top" className="flex items-center" content="Unique">
                    <label className="flex items-center gap-0.5 text-[10px] themed-text-muted cursor-pointer">
                      <input type="checkbox" checked={col.unique} onChange={e => updateColumn(i, { unique: e.target.checked })} disabled={saving} />UQ
                    </label>
                  </Tooltip>
                  <button onClick={() => removeColumn(i)} disabled={saving || columns.length <= 1}
                    className="text-red-400 hover:text-red-300 disabled:opacity-30"><X size={12} /></button>
                </div>
              ))}
            </div>
            <button onClick={addColumn} disabled={saving}
              className="mt-2 flex items-center gap-1 text-xs themed-text-muted hover:themed-text transition-colors">
              <Plus size={12} /> Add column
            </button>
          </div>
        </div>
        {error && <div className="px-4 py-2 text-xs text-red-400 border-t themed-border flex items-center gap-1.5"><AlertTriangle size={12} />{error}</div>}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
          <button onClick={onClose} className="text-xs themed-btn-secondary px-3 py-1.5 rounded">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="text-xs themed-btn-primary px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Loader2 size={12} className="animate-spin" />}Create
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// ModifyTableDialog
// ---------------------------------------------------------------------------

function ModifyTableDialog({ themeClass, tableName, tableInfo, onModify, onClose }: {
  themeClass: string
  tableName: string
  tableInfo?: TableInfo
  onModify: (action: string, opts: { column?: string; newName?: string; type?: string; notnull?: boolean; defaultValue?: string }) => Promise<{ ok: boolean; error?: string }>
  onClose: () => void
}) {
  const confirmDlg = useConfirm()
  const [newTableName, setNewTableName] = useState(tableName)
  const [addColName, setAddColName] = useState('')
  const [addColType, setAddColType] = useState('TEXT')
  const [addColNotNull, setAddColNotNull] = useState(false)
  const [addColDefault, setAddColDefault] = useState('')
  const [renameCol, setRenameCol] = useState('')
  const [renameColNew, setRenameColNew] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const doAction = async (action: string, opts: Parameters<typeof onModify>[1], successMsg: string) => {
    setSaving(true); setError(null); setSuccess(null)
    const res = await onModify(action, opts)
    if (res.ok) { setSuccess(successMsg); setAddColName(''); setAddColDefault(''); setRenameCol(''); setRenameColNew('') }
    else setError(res.error || 'Failed')
    setSaving(false)
  }

  const TYPES = ['INTEGER', 'TEXT', 'REAL', 'BLOB', 'NUMERIC', 'BOOLEAN', 'DATETIME', 'DATE', 'TIME', 'VARCHAR', 'FLOAT']
  const columns = tableInfo?.columns || []

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative w-[520px] max-h-[80vh] flex flex-col themed-bg-secondary border themed-border shadow-xl rounded-lg animate-scale-in`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">Modify: {tableName}</h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 space-y-5">
          {/* Rename table */}
          <div>
            <label className="text-xs font-medium themed-text mb-1 block">Rename Table</label>
            <div className="flex items-center gap-2">
              <input type="text" data-db-rename-table-input="" value={newTableName} onChange={e => setNewTableName(e.target.value)}
                disabled={saving} className="flex-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border" />
              <button onClick={() => doAction('rename_table', { newName: newTableName }, `Renamed to "${newTableName}"`)}
                // This dialog has a Rename for the TABLE and another for a
                // COLUMN, so the label alone matches two buttons.
                data-db-rename-table-submit=""
                disabled={saving || newTableName === tableName || !newTableName.trim()}
                className="text-xs themed-btn-primary px-3 py-1.5 rounded disabled:opacity-50">Rename</button>
            </div>
          </div>

          {/* Add column */}
          <div>
            <label className="text-xs font-medium themed-text mb-1 block">Add Column</label>
            <div className="flex items-center gap-2">
              <input type="text" value={addColName} onChange={e => setAddColName(e.target.value)}
                placeholder="column_name" disabled={saving}
                className="flex-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border" />
              <select value={addColType} onChange={e => setAddColType(e.target.value)} disabled={saving}
                className="text-xs px-2 py-1.5 rounded themed-input border themed-border w-24">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="flex items-center gap-0.5 text-[10px] themed-text-muted cursor-pointer shrink-0">
                <input type="checkbox" checked={addColNotNull} onChange={e => setAddColNotNull(e.target.checked)} disabled={saving} />NN
              </label>
            </div>
            {addColNotNull && (
              <input type="text" value={addColDefault} onChange={e => setAddColDefault(e.target.value)}
                placeholder="Default value (required for NOT NULL)" disabled={saving}
                className="w-full mt-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border" />
            )}
            <button onClick={() => doAction('add_column', { column: addColName, type: addColType, notnull: addColNotNull, defaultValue: addColDefault || undefined }, `Added "${addColName}"`)}
              disabled={saving || !addColName.trim()}
              className="mt-1 text-xs themed-btn-primary px-3 py-1.5 rounded disabled:opacity-50">Add</button>
          </div>

          {/* Rename column */}
          <div>
            <label className="text-xs font-medium themed-text mb-1 block">Rename Column</label>
            <div className="flex items-center gap-2">
              <select value={renameCol} onChange={e => { setRenameCol(e.target.value); setRenameColNew(e.target.value) }} disabled={saving}
                className="text-xs px-2 py-1.5 rounded themed-input border themed-border flex-1">
                <option value="">Select column...</option>
                {columns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
              <span className="text-xs themed-text-muted">→</span>
              <input type="text" value={renameColNew} onChange={e => setRenameColNew(e.target.value)}
                placeholder="new_name" disabled={saving || !renameCol}
                className="flex-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border" />
              <button onClick={() => doAction('rename_column', { column: renameCol, newName: renameColNew }, `Renamed "${renameCol}" → "${renameColNew}"`)}
                disabled={saving || !renameCol || !renameColNew.trim() || renameCol === renameColNew}
                className="text-xs themed-btn-primary px-3 py-1.5 rounded disabled:opacity-50">Rename</button>
            </div>
          </div>

          {/* Drop column */}
          <div>
            <label className="text-xs font-medium themed-text mb-1 block">Drop Column</label>
            <div className="flex flex-wrap gap-1">
              {columns.map(c => (
                <button key={c.name} disabled={saving || c.pk}
                  onClick={async () => {
                    const ok = await confirmDlg({
                      title: 'Drop Column',
                      message: `This will permanently drop column "${c.name}" and all its data from table "${tableName}".`,
                      type: 'danger',
                      confirmText: 'Drop',
                      cancelText: 'Cancel',
                      requiredInput: c.name,
                    })
                    if (!ok) return
                    setSaving(true); setError(null); setSuccess(null)
                    const res = await onModify('drop_column', { column: c.name })
                    if (res.ok) setSuccess(`Dropped "${c.name}"`)
                    else setError(res.error || 'Failed')
                    setSaving(false)
                  }}
                  className={`text-xs px-2 py-1 rounded border themed-border ${c.pk ? 'opacity-30 cursor-not-allowed' : 'hover:bg-red-600/20 hover:text-red-400 hover:border-red-500/30'} themed-text-secondary`}
                  title={c.pk ? 'Cannot drop primary key' : `Drop ${c.name}`}
                >
                  {c.name}
                  {!c.pk && <X size={10} className="inline ml-1" />}
                </button>
              ))}
            </div>
          </div>
        </div>
        {error && <div className="px-4 py-2 text-xs text-red-400 border-t themed-border flex items-center gap-1.5"><AlertTriangle size={12} />{error}</div>}
        {success && <div className="px-4 py-2 text-xs text-green-400 border-t themed-border flex items-center gap-1.5"><Check size={12} />{success}</div>}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
          <button onClick={onClose} className="text-xs themed-btn-secondary px-3 py-1.5 rounded">Close</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// InsertRowDialog — modal for inserting a new row
// ---------------------------------------------------------------------------

// Allowed SQL functions for insert dialog — server validates against this allowlist.
// "custom" is a special key: the server evaluates `SELECT <expr>` and uses the scalar result.
const INSERT_FN_LIST: { key: string; label: string; types: string[] }[] = [
  // Datetime
  { key: 'datetime_now', label: 'Now (UTC)', types: ['DATETIME', 'TIMESTAMP', 'DATE', 'TIME', 'TEXT'] },
  { key: 'date_now', label: 'Today (UTC)', types: ['DATETIME', 'TIMESTAMP', 'DATE', 'TEXT'] },
  { key: 'time_now', label: 'Current time (UTC)', types: ['DATETIME', 'TIMESTAMP', 'TIME', 'TEXT'] },
  { key: 'js_datetime_local', label: 'Now (local)', types: ['DATETIME', 'TIMESTAMP', 'DATE', 'TIME', 'TEXT'] },
  { key: 'js_date_local', label: 'Today (local)', types: ['DATETIME', 'TIMESTAMP', 'DATE', 'TEXT'] },
  { key: 'js_time_local', label: 'Current time (local)', types: ['DATETIME', 'TIMESTAMP', 'TIME', 'TEXT'] },
  { key: 'timestamp_unix', label: 'Unix timestamp', types: ['INTEGER', 'INT', 'REAL', 'NUMERIC'] },
  // IDs & random
  { key: 'uuid_v4', label: 'Random UUID', types: ['TEXT', 'VARCHAR', 'CHAR'] },
  { key: 'random_int', label: 'Random integer', types: ['INTEGER', 'INT', 'REAL', 'NUMERIC'] },
  { key: 'random_hex16', label: 'Random hex (16)', types: ['TEXT', 'VARCHAR', 'CHAR', 'BLOB'] },
  // Boolean
  { key: 'true', label: 'True (1)', types: ['INTEGER', 'INT', 'BOOLEAN', 'NUMERIC'] },
  { key: 'false', label: 'False (0)', types: ['INTEGER', 'INT', 'BOOLEAN', 'NUMERIC'] },
  // Other
  { key: 'zero', label: 'Zero (0)', types: ['INTEGER', 'INT', 'REAL', 'NUMERIC'] },
]

// Map SQLite default expressions to function keys
const DEFAULT_TO_FN: Record<string, string> = {
  "datetime('now')": 'datetime_now',
  "current_timestamp": 'datetime_now',
  "date('now')": 'date_now',
  "current_date": 'date_now',
  "time('now')": 'time_now',
  "current_time": 'time_now',
  "strftime('%s', 'now')": 'timestamp_unix',
  "strftime('%s','now')": 'timestamp_unix',
  "(abs(random()))": 'random_int',
  "abs(random())": 'random_int',
  "0": 'false',
  "1": 'true',
}

function getFunctionsForType(colType: string): { label: string; key: string }[] {
  const t = colType.toUpperCase()
  const results: { label: string; key: string }[] = []
  for (const fn of INSERT_FN_LIST) {
    if (fn.types.some(ft => t.includes(ft))) {
      results.push({ label: fn.label, key: fn.key })
    }
  }
  // Always offer Custom at the end
  results.push({ label: 'Custom expression...', key: 'custom' })
  return results
}

type FieldMode = 'value' | 'null' | 'function'

/** One column's initial entry in the insert dialog's field state. Shared by the
 *  initial seed and by the fallback for a column that arrives after it. */
function seedInsertField(col: ColumnInfo, prefill?: Record<string, unknown> | null): { mode: FieldMode; value: string; fn: string } {
  const fns = getFunctionsForType(col.type)
  const prefillVal = prefill?.[col.name]

  // Prefill from a duplicated row takes priority
  if (prefillVal !== undefined) {
    if (prefillVal === null) return { mode: col.notnull ? 'value' : 'null', value: '', fn: fns[0]?.key || '' }
    return { mode: 'value', value: String(prefillVal), fn: fns[0]?.key || '' }
  }
  if (col.defaultValue != null) {
    // SQLite stores defaults as SQL literals — detect known functions or strip quotes
    const raw = String(col.defaultValue)
    const matchedFnKey = DEFAULT_TO_FN[raw.toLowerCase()]
    if (matchedFnKey && fns.some(f => f.key === matchedFnKey)) return { mode: 'function', value: '', fn: matchedFnKey }
    let defVal = raw
    if ((defVal.startsWith("'") && defVal.endsWith("'")) || (defVal.startsWith('"') && defVal.endsWith('"'))) {
      defVal = defVal.slice(1, -1)
    }
    return { mode: 'value', value: defVal, fn: fns[0]?.key || '' }
  }
  if (!col.notnull) return { mode: 'null', value: '', fn: fns[0]?.key || '' }
  return { mode: 'value', value: '', fn: fns[0]?.key || '' }
}

/** @internal exported for tests */
export function InsertRowDialog({ themeClass, columns, foreignKeys, prefill, onInsert, onClose }: {
  themeClass: string
  columns: ColumnInfo[]
  foreignKeys?: ForeignKey[]
  prefill?: Record<string, unknown> | null
  onInsert: (values: Record<string, unknown>, functions: Record<string, string>) => Promise<{ ok: boolean; error?: string }>
  onClose: () => void
}) {
  const [fields, setFields] = useState<Record<string, { mode: FieldMode; value: string; fn: string }>>(() => {
    const init: Record<string, { mode: FieldMode; value: string; fn: string }> = {}
    for (const col of columns) init[col.name] = seedInsertField(col, prefill)
    return init
  })
  /**
   * The state is seeded ONCE and the render looks every CURRENT column up in
   * it, so a column this dialog has never seen has no entry. Reading `.mode`
   * off that threw, and the throw reaches the tab's error boundary, which does
   * not recover — the grid's staged delete marks, open cell editors and
   * unsaved inserts go with it.
   *
   * Two ways a column arrives late: the TABLE changes under an open dialog
   * (the AI's `papan:db-set-table` moves it with no click, so the backdrop
   * that would otherwise dismiss this never fires), or the same table's column
   * list — fetched per table — lands after Insert Row was clicked.
   *
   * Falling back per column, rather than re-seeding the whole map, keeps what
   * the user has already typed into the columns that are still there.
   */
  const fieldFor = (col: ColumnInfo) => fields[col.name] ?? seedInsertField(col, prefill)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCols, setErrorCols] = useState<Set<string>>(new Set())

  // Parse SQLite error to identify which column(s) failed
  const parseErrorCols = (errMsg: string): Set<string> => {
    const s = new Set<string>()
    // "NOT NULL constraint failed: table.column" or "UNIQUE constraint failed: table.column"
    const colMatch = errMsg.match(/constraint failed: \S+\.(\S+)/)
    if (colMatch) { s.add(colMatch[1]); return s }
    // "Invalid expression for column:" (our custom expression handler)
    const exprMatch = errMsg.match(/Invalid expression for (\S+):/)
    if (exprMatch) { s.add(exprMatch[1]); return s }
    // "FOREIGN KEY constraint failed" — highlight all FK columns
    if (errMsg.includes('FOREIGN KEY')) {
      if (foreignKeys?.length) {
        for (const fk of foreignKeys) s.add(fk.from)
      } else {
        // Fallback: name convention
        for (const col of columns) {
          if (col.name.endsWith('_id')) s.add(col.name)
        }
      }
      return s
    }
    return s
  }

  /**
   * `prev[col.name]` is absent for a column that arrived after the dialog
   * opened — the case `fieldFor` exists for. Spreading `undefined` and the
   * patch leaves ONLY the patch, so one keystroke produced `{ value }` with no
   * `mode`, the render matched neither the value nor the null arm and fell
   * through to the function dropdown: the textarea being typed in vanished
   * mid-keystroke. Submitting then sent nothing for that column, so the row
   * went in with the database's default — or failed a NOT NULL it could see
   * filled in.
   *
   * Reads were made total by `fieldFor`; this is the same seed on the write
   * side, which is what makes the two agree.
   */
  const setField = (col: ColumnInfo, patch: Partial<{ mode: FieldMode; value: string; fn: string }>) => {
    setFields(prev => ({ ...prev, [col.name]: { ...(prev[col.name] ?? seedInsertField(col, prefill)), ...patch } }))
    // Clear error highlight when user changes the field
    if (errorCols.has(col.name)) setErrorCols(prev => { const s = new Set(prev); s.delete(col.name); return s })
  }

  const handleSubmit = async () => {
    setError(null)
    setErrorCols(new Set())
    setSaving(true)
    const vals: Record<string, unknown> = {}
    const fns: Record<string, string> = {}
    for (const col of columns) {
      const isAutoIncPk = col.pk && col.type.toUpperCase() === 'INTEGER' && columns.filter(c => c.pk).length === 1
      if (isAutoIncPk) continue
      const f = fieldFor(col)
      if (f.mode === 'null') { vals[col.name] = null; continue }
      if (f.mode === 'function') {
        // Client-side JS functions — evaluate here, send as values
        if (f.fn === 'js_datetime_local') { vals[col.name] = new Date().toLocaleString('sv-SE').replace(' ', 'T'); continue }
        if (f.fn === 'js_date_local') { vals[col.name] = new Date().toLocaleDateString('sv-SE'); continue }
        if (f.fn === 'js_time_local') { vals[col.name] = new Date().toLocaleTimeString('sv-SE'); continue }
        if (f.fn === 'custom') { fns[col.name] = `custom:${f.value}`; continue }
        // Server-side function
        fns[col.name] = f.fn; continue
      }
      vals[col.name] = f.value
    }
    const res = await onInsert(vals, fns)
    if (!res.ok) {
      const errMsg = res.error || 'Insert failed'
      setError(errMsg)
      setErrorCols(parseErrorCols(errMsg))
    }
    setSaving(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative w-[520px] max-h-[80vh] flex flex-col themed-bg-secondary border themed-border shadow-xl rounded-lg animate-scale-in`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">{prefill ? 'Duplicate Row' : 'Insert New Row'}</h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 space-y-5">
          {columns.map(col => {
            const isAutoIncPk = col.pk && col.type.toUpperCase() === 'INTEGER' && columns.filter(c => c.pk).length === 1
            const f = fieldFor(col)
            const fns = getFunctionsForType(col.type)
            const modes: FieldMode[] = col.notnull ? ['value'] : ['value', 'null']
            if (fns.length > 0) modes.push('function')

            const hasError = errorCols.has(col.name)
            const errorInputClass = hasError ? 'border-red-500/60 ring-1 ring-red-500/30' : ''

            return (
              <div key={col.name}>
                {/* Row 1: name, mode toggle, required, type */}
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-xs font-medium themed-text">{col.name}</label>
                  {!isAutoIncPk && (
                    <div className="mode-pill shrink-0" style={{ padding: '2px' }}>
                      {modes.map(m => (
                        <button
                          key={m}
                          onClick={() => setField(col, { mode: m })}
                          className={`mode-pill-btn ${f.mode === m ? 'active' : ''}`}
                          style={{ padding: '1px 8px', fontSize: '10px' }}
                          disabled={saving}
                        >
                          {m === 'value' ? 'Value' : m === 'null' ? 'NULL' : 'Fn'}
                        </button>
                      ))}
                    </div>
                  )}
                  {col.pk && <Key size={10} className="text-amber-400" />}
                  {hasError && <AlertTriangle size={10} className="text-red-400" />}
                  <div className="flex-1" />
                  <span className="text-[10px] themed-text-muted font-mono">{col.type}</span>
                </div>
                {/* Row 2: input */}
                <div className="pl-4">
                {isAutoIncPk ? (
                  <div className="text-xs themed-text-muted italic px-2 py-1.5">Auto-generated</div>
                ) : f.mode === 'value' ? (
                  <textarea
                    value={f.value}
                    onChange={e => {
                      setField(col, { value: e.target.value })
                      // Auto-expand
                      const ta = e.target
                      ta.style.height = 'auto'
                      ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`
                    }}
                    disabled={saving}
                    rows={1}
                    placeholder={col.defaultValue != null && !DEFAULT_TO_FN[String(col.defaultValue).toLowerCase()] ? `Default: ${String(col.defaultValue).replace(/^['"]|['"]$/g, '')}` : ''}
                    className={`w-full text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border resize-none overflow-hidden ${errorInputClass}`}
                    style={{ minHeight: '28px' }}
                  />
                ) : f.mode === 'null' ? (
                  <input type="text" disabled placeholder="NULL" className={`w-full text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border opacity-40 italic ${errorInputClass}`} />
                ) : (
                  <div className="flex items-center gap-2">
                    <select
                      value={f.fn}
                      onChange={e => setField(col, { fn: e.target.value })}
                      disabled={saving}
                      className={`text-xs px-2 py-1.5 rounded themed-input border themed-border ${f.fn === 'custom' ? '' : 'w-full'} ${errorInputClass}`}
                    >
                      {fns.map(fn => (
                        <option key={fn.key} value={fn.key}>{fn.label}</option>
                      ))}
                    </select>
                    {f.fn === 'custom' && (
                      <input
                        type="text"
                            value={f.value}
                            onChange={e => setField(col, { value: e.target.value })}
                            disabled={saving}
                            placeholder="e.g. abs(random()) % 100"
                            className="flex-1 text-xs font-mono px-2 py-1.5 rounded themed-input border themed-border"
                          />
                        )}
                  </div>
                )}
                </div>
              </div>
            )
          })}
        </div>
        {error && (
          <div className="px-4 py-2 text-xs text-red-400 border-t themed-border flex items-center gap-1.5">
            <AlertTriangle size={12} />
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
          <button onClick={onClose} className="text-xs themed-btn-secondary px-3 py-1.5 rounded">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="text-xs themed-btn-primary px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            Insert
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// EditRowRenderer — standalone component for AG Grid fullWidthCellRenderer
// Defined outside DatabaseTableModifier so AG Grid doesn't re-mount it on parent re-renders.
// Reads all mutable state from context (passed via AG Grid's context prop).
// ---------------------------------------------------------------------------

function EditRowRenderer(params: any) {
  const ctx = params.context
  const sourceRowId = params.data.__sourceRowId
  const currentRow = ctx.result?.rows.find((r: any) => r.__rowid === sourceRowId)
  // Use tableInfo columns if available, fall back to result.columns for lazy-loading adapters (FDB)
  const tableInfoCols = ctx.tableInfo?.columns || []
  const allCols = tableInfoCols.length > 0
    ? tableInfoCols
    : (ctx.result?.columns || []).map((name: string) => ({ name, type: '', notnull: false, pk: false, defaultValue: null }))
  const pkCols = allCols.filter((c: any) => c.pk).map((c: any) => c.name)
  // Only show columns visible in the grid (selectedColumns filter)
  const visibleColNames = ctx.selectedColumns?.length > 0 ? ctx.selectedColumns : allCols.map((c: any) => c.name)
  const cols = allCols.filter((c: any) => visibleColNames.includes(c.name))

  const gridCols = params.api.getColumns?.() || []
  const colWidths = new Map<string, number>()
  for (const gc of gridCols) colWidths.set(gc.getColId(), gc.getActualWidth())

  // Get row number column width for spacer
  const rowNumWidth = colWidths.get('__rownum') || 0

  return (
    <div className="flex items-stretch h-full themed-bg-secondary relative">
      {/* Spacer for pinned row number column */}
      {rowNumWidth > 0 && <div style={{ width: rowNumWidth, minWidth: rowNumWidth }} className="shrink-0 themed-border border-r" />}
      {cols.map((col: any) => {
        const isPk = pkCols.includes(col.name)
        const colType = (col.type || '').toUpperCase()
        const isBinaryBlob = colType === 'BLOB'
        const isBlobText = colType === 'BLOB TEXT'
        const cellKey = editCellKey(sourceRowId, col.name)
        const cell = ctx.editCellsRef.current.get(cellKey)
        const rawVal = currentRow ? currentRow[col.name] : null
        const currentValue = currentRow ? String(rawVal ?? '') : ''
        const width = colWidths.get(col.name)
        // BLOB TEXT: editable if under 10KB, otherwise read-only truncated preview
        const blobTextTooLarge = isBlobText && typeof rawVal === 'string' && rawVal.length > 10240

        // Binary BLOB — non-editable placeholder
        if (isBinaryBlob) return (
          <div key={col.name} data-col={col.name}
            className="shrink-0 px-2 py-1 flex items-center themed-border border-r"
            style={{ width: width || 150 }}
          >
            <span className="text-zinc-500 italic text-[10px]">(BLOB)</span>
          </div>
        )

        // BLOB TEXT too large — read-only truncated preview
        if (blobTextTooLarge) return (
          <div key={col.name} data-col={col.name}
            className="shrink-0 px-2 py-1 flex flex-col themed-border border-r overflow-hidden"
            style={{ width: width || 150 }}
          >
            <span className="text-xs font-mono themed-text-muted truncate">{String(rawVal).slice(0, 200)}</span>
            <span className="text-[9px] text-zinc-500 italic">(truncated — {Math.round(String(rawVal).length / 1024)}KB)</span>
          </div>
        )

        if (isPk && !cell) return (
          <Tooltip key={col.name} always placement="top" className="flex items-center" content="Double-click to edit (⚠ changing PK may break FK references)" tone="warning">
            <div
              data-col={col.name}
              className="shrink-0 pl-3.5 pr-2 py-1 flex items-start themed-border border-r cursor-pointer h-full"
              style={{ width: width || 150 }}
              onDoubleClick={() => ctx.openEditCell(sourceRowId, col.name)}
            >
              <Key size={10} className="text-amber-400 opacity-40" />
            </div>
          </Tooltip>
        )

        if (!cell) return (
          <div
            key={col.name}
            data-col={col.name}
            className="shrink-0 px-2 py-1 cursor-pointer flex items-center themed-border border-r"
            style={{ width: width || 150 }}
            onDoubleClick={() => ctx.openEditCell(sourceRowId, col.name)}
          >
            <span className="text-xs themed-text-muted opacity-20">···</span>
          </div>
        )

        const isSaving = ctx.savingCellsRef.current.has(cellKey)
        const errMsg = ctx.editErrorsRef.current.get(cellKey)
        const isCurrentNull = currentRow ? (currentRow[col.name] === null || currentRow[col.name] === undefined) : false

        return (
          <div key={col.name} data-col={col.name} className="shrink-0 flex flex-col themed-border border-r relative" style={{ width: width || 150 }}>
            {cell.isNull ? (
              // NULL state — show label, click to start editing as string
              <div
                className="flex-1 flex items-center justify-center cursor-pointer mx-1 mb-1 rounded-sm border themed-border"
                style={{ background: '#1e1e22' }}
                onClick={() => {
                  const isDirty = !isCurrentNull // switching from NULL to empty string is dirty if original wasn't NULL
                  ctx.editCellsRef.current = new Map(ctx.editCellsRef.current).set(cellKey, { column: col.name, value: '', dirty: isDirty, isNull: false })
                  ctx.setEditCells(new Map(ctx.editCellsRef.current))
                  if (isDirty && !ctx.hasDirtyRef.current) { ctx.hasDirtyRef.current = true; ctx.onDirtyChange?.(true) }
                  // Redraw to show textarea
                  const api = ctx.gridRef.current?.api
                  const editNode = api?.getRowNode(`edit_${sourceRowId}`)
                  if (editNode) api?.redrawRows({ rowNodes: [editNode] })
                }}
              >
                <span className="text-zinc-500 italic text-xs">NULL</span>
              </div>
            ) : (
              // Non-NULL — show textarea
              <textarea
                defaultValue={cell.value}
                onInput={e => {
                  const ta = e.target as HTMLTextAreaElement
                  const val = ta.value
                  const isDirty = isCurrentNull || val !== currentValue
                  const wasDirty = ctx.editCellsRef.current.get(cellKey)?.dirty ?? false
                  ctx.editCellsRef.current = new Map(ctx.editCellsRef.current).set(cellKey, { column: col.name, value: val, dirty: isDirty, isNull: false })
                  if (isDirty !== wasDirty) {
                    const nowDirty = isDirty || [...ctx.editCellsRef.current.values()].some((c: any) => c.dirty)
                    if (nowDirty !== ctx.hasDirtyRef.current) { ctx.hasDirtyRef.current = nowDirty; ctx.onDirtyChange?.(nowDirty) }
                  }
                  ta.style.borderColor = isDirty ? 'rgba(245, 158, 11, 0.25)' : ''
                  const checkBtn = ta.parentElement?.querySelector('[data-check]') as HTMLButtonElement
                  if (checkBtn) { checkBtn.style.opacity = isDirty ? '1' : '0.3' }
                }}
                key={cellKey}
                className={`flex-1 text-xs font-mono px-2 py-1 outline-none resize-none rounded-sm mx-1 mb-1 border ${isSaving ? 'opacity-50 pointer-events-none themed-border' : errMsg ? 'db-cell-error' : cell.dirty ? 'border-amber-500/25' : 'themed-border'}`}
                style={{ background: '#1e1e22' }}
                disabled={isSaving}
              />
            )}
            <div className="flex items-center gap-2 px-1 py-0.5">
              {isPk && !isSaving && (
                <Tooltip always placement="top" className="flex items-center" content="Changing primary key may break foreign key references" tone="warning">
                  <span className="text-amber-400"><AlertTriangle size={10} /></span>
                </Tooltip>
              )}
              {isSaving ? (
                <Loader2 size={10} className="animate-spin text-blue-400" />
              ) : (
                <>
                <button
                  data-check
                  onClick={() => {
                    const c = ctx.editCellsRef.current.get(cellKey)
                    if (c?.dirty) ctx.saveEditCell(sourceRowId, col.name)
                  }}
                  className="text-green-400 hover:text-green-300"
                  style={{ opacity: cell.dirty ? 1 : 0.3 }}
                >
                  <Check size={10} />
                </button>
                {/* Set NULL button — only show when not already NULL */}
                {!cell.isNull && !isPk && (
                  <button
                    onClick={() => {
                      const isDirty = !isCurrentNull
                      ctx.editCellsRef.current = new Map(ctx.editCellsRef.current).set(cellKey, { column: col.name, value: '', dirty: isDirty, isNull: true })
                      ctx.setEditCells(new Map(ctx.editCellsRef.current))
                      if (isDirty && !ctx.hasDirtyRef.current) { ctx.hasDirtyRef.current = true; ctx.onDirtyChange?.(true) }
                      const api = ctx.gridRef.current?.api
                      const editNode = api?.getRowNode(`edit_${sourceRowId}`)
                      if (editNode) api?.redrawRows({ rowNodes: [editNode] })
                    }}
                    className="text-zinc-500 hover:text-zinc-300 text-[9px] italic"
                    title="Set to NULL"
                  >
                    NULL
                  </button>
                )}
                </>
              )}
              <button
                onClick={() => {
                  // Sync both ref and state
                  const next = new Map(ctx.editCellsRef.current)
                  next.delete(cellKey)
                  ctx.editCellsRef.current = next
                  ctx.setEditCells(new Map(next))
                  ctx.editErrorsRef.current = new Map(ctx.editErrorsRef.current)
                  ctx.editErrorsRef.current.delete(cellKey)
                  const hasRowCells = [...next.keys()].some(k => isEditKeyOfRow(String(k), sourceRowId))
                  if (!hasRowCells) {
                    ;(document.activeElement as HTMLElement)?.blur?.()
                    const api = ctx.gridRef.current?.api
                    api?.clearFocusedCell()
                    ctx.setEditRowIds((prev: Set<number>) => { const s = new Set(prev); s.delete(sourceRowId); return s })
                    ctx.setEditRowHeights((prev: Map<number, number>) => { const next = new Map(prev); next.delete(sourceRowId); return next })
                    setTimeout(() => {
                      const node = api?.getRowNode(String(sourceRowId))
                      if (node) api?.redrawRows({ rowNodes: [node] })
                    }, 50)
                  } else {
                    // Redraw edit row to remove the closed cell
                    const api = ctx.gridRef.current?.api
                    const editNode = api?.getRowNode(`edit_${sourceRowId}`)
                    if (editNode) api?.redrawRows({ rowNodes: [editNode] })
                  }
                }}
                className="text-red-400 hover:text-red-300"
              >
                <X size={10} />
              </button>
              {errMsg && (
                <Tooltip always placement="top" className="flex items-center" content={errMsg} tone="error">
                  <span className="flex items-center gap-1 text-red-400 cursor-help">
                    <AlertTriangle size={10} />
                  </span>
                </Tooltip>
              )}
            </div>
            {/* Corner resize handle */}
            <div
              className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize opacity-40 hover:opacity-100 transition-opacity"
              onMouseDown={e => {
                e.preventDefault()
                e.stopPropagation()
                const startX = e.clientX
                const startY = e.clientY
                // Read current width from grid API (not stale render-time `width`)
                const gridCol = params.api.getColumns?.()?.find((gc: any) => gc.getColId() === col.name)
                const startW = gridCol?.getActualWidth() || width || 100
                const container = e.currentTarget.closest('.ag-full-width-row') as HTMLElement
                // Read current height from DOM (not stale render-time context)
                const startH = container?.getBoundingClientRect().height || ctx.editRowHeights.get(sourceRowId) || 80
                const cellDiv = e.currentTarget.parentElement as HTMLElement
                ctx.cornerDraggingRef.current = true

                const onMove = (ev: MouseEvent) => {
                  const newW = Math.max(60, startW + ev.clientX - startX)
                  const newH = Math.max(50, Math.min(400, startH + ev.clientY - startY))
                  params.api.setColumnWidths([{ key: col.name, newWidth: newW }])
                  if (cellDiv) cellDiv.style.width = `${newW}px`
                  if (container) container.style.height = `${newH}px`
                }
                const onUp = (ev: MouseEvent) => {
                  document.removeEventListener('mousemove', onMove)
                  document.removeEventListener('mouseup', onUp)
                  ctx.cornerDraggingRef.current = false
                  // Finalize width — set the last drag position via API and store in ref
                  const finalW = Math.max(60, startW + ev.clientX - startX)
                  params.api.setColumnWidths([{ key: col.name, newWidth: finalW }])
                  if (cellDiv) cellDiv.style.width = `${finalW}px`
                  // Store all column widths and bump version so columnDefs picks them up
                  const allCols = params.api.getColumns?.() || []
                  for (const gc of allCols) {
                    const id = gc.getColId()
                    if (id !== '__rownum') ctx.columnWidthsRef.current.set(id, gc.getActualWidth())
                  }
                  ctx.setColWidthsVersion((v: number) => v + 1)
                  const newH = Math.max(50, Math.min(400, startH + ev.clientY - startY))
                  if (container) container.style.height = ''
                  ctx.setEditRowHeights((prev: Map<number, number>) => new Map(prev).set(sourceRowId, newH))
                  const node = params.api.getRowNode(`edit_${sourceRowId}`)
                  if (node) { node.setRowHeight(newH); params.api.onRowHeightChanged() }
                  // Don't redraw — it destroys the textarea and loses input
                }
                document.addEventListener('mousemove', onMove)
                document.addEventListener('mouseup', onUp)
              }}
            >
              <svg width="12" height="12" className="text-zinc-500">
                <path d="M10 2 L2 10 M10 6 L6 10" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// DatabaseSqlModifier — SQL query mode (own state, no live refresh)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SQL History — device-local and per identity; see ./sql-history.ts.
//
// It used to be localStorage PLUS a fire-and-forget PUT to
// `/api/settings/sql-history`, which wrote a file into the user's own Drive.
// That write is one of the two issue #369 removes so an identity-only tenant
// needs no drive writes at all. The key is now namespaced by the acting
// identity, because localStorage is per-DEVICE: two people on one browser were
// reading each other's queries, which carry table names, column names and
// literal values from a drive the other may not be able to open.
// ---------------------------------------------------------------------------

export interface DatabaseSqlHandle {
  setSql: (sql: string) => void
}

interface DatabaseSqlModifierProps {
  fileId: string
  fileName?: string
  schema: TableInfo[]
  versionId?: string
  parentId?: string | null
  canCopy?: boolean
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  adapter: DbAdapter
  /** Declarative AI/user SQL apply (see DatabaseViewer.pushAiSql).
   *  Applied via an effect keyed on `n` — no sqlRef timing race. */
  aiSql?: { sql: string; n: number; autoRun: boolean } | null
}

const DatabaseSqlModifier = forwardRef<DatabaseSqlHandle, DatabaseSqlModifierProps>(function DatabaseSqlModifier({ fileId, fileName, schema, versionId, parentId, canCopy = true, onExportToDrive, adapter, aiSql }, ref) {
  const [sql, setSql] = useState('')
  const { settings: themeSettings } = useTheme()
  const sqlThemeClass = getThemeClass(themeSettings.theme.workspace)

  useImperativeHandle(ref, () => ({ setSql }), [])
  const [result, setResult] = useState<QueryResult | null>(null)
  const [queryLoading, setQueryLoading] = useState(false)
  const [statusMsg, setStatusMsg] = useState('')
  const [pendingAutoRun, setPendingAutoRun] = useState(false)
  // Device-local only. There is no server load any more — and no merge, which
  // is what the removed round-trip was really doing: reconciling two copies of
  // a scratch pad.
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory())
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const highlightRef = useRef<HTMLPreElement>(null)
  const displayName = fileName || fileId

  /** Insert text at cursor position in the textarea — supports undo (Ctrl+Z) */
  const insertAtCursor = useCallback((text: string) => {
    const ta = textareaRef.current
    if (!ta) { setSql(text); return }
    ta.focus()
    if (ta.selectionStart === 0 && ta.selectionEnd === 0 && ta.value.length > 0) {
      ta.selectionStart = ta.selectionEnd = ta.value.length
    }
    document.execCommand('insertText', false, text)
  }, [])

  /** Get the query to run: selection > statement at cursor > full text */
  const getActiveQuery = useCallback((): string => {
    const ta = textareaRef.current
    if (!ta) return sql.replace(/;\s*$/, '').trim()
    // If there's a selection, run that
    if (ta.selectionStart !== ta.selectionEnd) {
      return ta.value.slice(ta.selectionStart, ta.selectionEnd).replace(/;\s*$/, '').trim()
    }
    // Split by semicolons, find which statement the cursor is in.
    // Walk backwards from cursor to find the nearest non-empty statement.
    const pos = ta.selectionStart
    const parts = ta.value.split(';')
    let offset = 0
    let lastNonEmpty = ''
    for (const part of parts) {
      const end = offset + part.length
      const trimmed = part.trim()
      if (trimmed) lastNonEmpty = trimmed
      if (pos <= end) {
        // Cursor is in this segment — use it if non-empty, otherwise use the previous one
        return trimmed || lastNonEmpty
      }
      offset = end + 1
    }
    return lastNonEmpty
  }, [sql])

  const runQuery = useCallback(async () => {
    const query = getActiveQuery()
    if (!query) return
    setQueryLoading(true)
    setStatusMsg('')
    setHistory(prev => addToHistory(prev, { sql: query, fileName: displayName, fileId, timestamp: Date.now() }))
    try {
      // Running SQL does NOT touch the grid's staged changes: the grid stages
      // locally and Ctrl+Z reaches only what has not been sent, so nothing a
      // statement does here can invalidate it.
      //
      // What a statement CAN invalidate is a staged row's id — a DELETE here
      // removes a row the grid has marked, and the next Save asks for it
      // again. The Save reports the refusal and leaves the mark for the user
      // to drop; see applyPendingDeletes.
      const res = await adapter.query(fileId, query, { versionId })


      if (res.error) {
        setResult({ columns: [], rows: [], error: res.error })
        setStatusMsg('')
      } else {
        setResult(res)
        if (res.changes != null && res.changes > 0) {
          setStatusMsg(`${res.changes} row(s) affected in ${res.duration}ms`)
        } else if ((res as any).truncated) {
          setStatusMsg(`first ${(res.rowCount ?? 0).toLocaleString()} row(s) in ${res.duration}ms — add a LIMIT/WHERE for more`)
        } else {
          setStatusMsg(`${res.rowCount} row(s) in ${res.duration}ms`)
        }
      }
    } catch (err: any) {
      // #1316: a dropped answer (its session ended) shows nothing — the console keeps its last result
      const text = errorText(err)
      if (text !== null) {
        setResult({ columns: [], rows: [], error: text })
        setStatusMsg('')
      }
    }
    setQueryLoading(false)
  }, [fileId, sql, versionId])

  // Apply an AI/user-requested SQL (declarative — see
  // DatabaseViewer.pushAiSql). Keyed on the nonce so identical SQL
  // re-applies and unrelated re-renders never clobber the user's edits.
  // Effects run after THIS component mounts too, so a value set before
  // it mounted (the cold post-reload reload-recovery case) still lands —
  // no dependence on WHEN the imperative handle committed, the race the
  // old 50 ms setTimeout lost.
  useEffect(() => {
    if (!aiSql) return
    setSql(aiSql.sql)
    setPendingAutoRun(aiSql.autoRun)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSql?.n])
  // Autorun only AFTER the new SQL has committed to `sql` state
  // (deterministic — no timer; runQuery reads `sql`, so gating on it
  // guarantees the new query runs, not the previous one).
  useEffect(() => {
    if (!pendingAutoRun || !sql.trim()) return
    setPendingAutoRun(false)
    void runQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoRun, sql])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      runQuery()
    }
  }, [runQuery])

  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }, [])

  const sqlGridRef = useRef<AgGridReact>(null)

  // Range selection state (same model as DB table viewer)
  const sqlSelectionRef = useRef<{ startRow: number; startCol: number; endRow: number; endCol: number } | null>(null)
  const [sqlSelVer, setSqlSelVer] = useState(0)

  const getSqlCellRange = useCallback(() => {
    const sel = sqlSelectionRef.current
    if (!sel) return null
    return {
      rowStart: Math.min(sel.startRow, sel.endRow),
      rowEnd: Math.max(sel.startRow, sel.endRow),
      colStart: Math.min(sel.startCol, sel.endCol),
      colEnd: Math.max(sel.startCol, sel.endCol),
    }
  }, [sqlSelVer]) // eslint-disable-line react-hooks/exhaustive-deps

  // Context menu for SQL results
  const [sqlCtxMenu, setSqlCtxMenu] = useState<{ x: number; y: number; value: string; colId: string } | null>(null)
  const sqlCtxMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sqlCtxMenu) return
    const onMouseDown = (e: MouseEvent) => {
      if (sqlCtxMenuRef.current && !sqlCtxMenuRef.current.contains(e.target as Node)) setSqlCtxMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSqlCtxMenu(null) }
    window.addEventListener('mousedown', onMouseDown, true)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onMouseDown, true); window.removeEventListener('keydown', onKey) }
  }, [sqlCtxMenu])

  useEffect(() => {
    if (!sqlCtxMenu || !sqlCtxMenuRef.current) return
    const el = sqlCtxMenuRef.current
    const rect = el.getBoundingClientRect()
    let { x, y } = sqlCtxMenu
    if (x + rect.width > window.innerWidth - 4) x = window.innerWidth - rect.width - 4
    if (y + rect.height > window.innerHeight - 4) y = window.innerHeight - rect.height - 4
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }, [sqlCtxMenu])

  const copySqlSelection = useCallback(() => {
    const range = getSqlCellRange()
    if (!range || !result) return false
    const cols = result.columns.slice(range.colStart, range.colEnd + 1)
    const rows = result.rows.slice(range.rowStart, range.rowEnd + 1)
    if (rows.length === 0 || cols.length === 0) return false
    const text = rows.map(row => cols.map(col => {
      const val = row[col]
      if (val === null || val === undefined) return ''
      return String(val)
    }).join('\t')).join('\n')
    navigator.clipboard.writeText(text)
    return true
  }, [getSqlCellRange, result])

  const columnDefs: ColDef[] = useMemo(() => {
    const resultCols = result?.columns || []
    const maxRowNum = result?.rows.length ?? 0
    const digits = Math.max(2, String(maxRowNum).length)
    const rowNumWidth = digits * 8 + 10
    const rowNumCol: ColDef = {
      colId: '__rownum',
      headerName: '',
      width: rowNumWidth,
      minWidth: rowNumWidth,
      maxWidth: rowNumWidth,
      sortable: false,
      resizable: false,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
      cellStyle: { textAlign: 'center', cursor: 'pointer', userSelect: 'none', overflow: 'visible', textOverflow: 'clip', padding: '0 4px' },
      headerClass: 'ag-header-row-num',
      valueGetter: (params: any) => params.node?.rowIndex != null ? params.node.rowIndex + 1 : '',
      cellRenderer: (params: any) => {
        const val = params.value
        return val ? <span className="text-[10px] themed-text-muted">{val}</span> : null
      },
    }
    const dataCols: ColDef[] = resultCols.map(col => ({
      field: col,
      headerName: col,
      sortable: true,
      resizable: true,
      minWidth: 80,
      flex: 1,
      cellRenderer: (params: any) => {
        const val = params.value
        if (val === null) return <span className="text-zinc-500 italic">NULL</span>
        if (typeof val === 'object') return JSON.stringify(val)
        return String(val)
      },
    }))
    return [rowNumCol, ...dataCols]
  }, [result?.columns, result?.rows])

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* History sidebar + table reference */}
      <div data-sidebar className="w-56 shrink-0 themed-bg-secondary themed-border border-r flex flex-col overflow-hidden">
        <div className="h-9 flex items-center px-3 text-xs font-medium themed-text-muted border-b themed-border shrink-0">
          <History size={12} className="mr-1.5" /> History ({history.length})
        </div>
        <div className="flex-1 overflow-auto">
          {history.length === 0 && (
            <div className="px-3 py-2 text-xs themed-text-muted">No queries yet</div>
          )}
          {(() => {
            // Group: current file first, then other files
            const currentFile = history.filter(e => e.fileId === fileId)
            const otherFiles = history.filter(e => e.fileId !== fileId)
            // Group other files by fileName
            const groups = new Map<string, HistoryEntry[]>()
            for (const e of otherFiles) {
              const key = e.fileName || e.fileId
              if (!groups.has(key)) groups.set(key, [])
              groups.get(key)!.push(e)
            }

            return (
              <>
                {currentFile.map((e, i) => (
                  <Tooltip key={`cur-${i}`} always placement="top" className="flex items-center" content={e.sql}>
                    <button
                      onClick={() => { setSql(e.sql); setTimeout(() => textareaRef.current?.focus(), 0) }}
                      className="w-full text-left px-3 py-1.5 text-xs font-mono truncate hover:themed-bg-primary themed-text transition-colors border-l-2 border-l-blue-500"
                    >
                      {e.sql}
                    </button>
                  </Tooltip>
                ))}
                {currentFile.length > 0 && groups.size > 0 && (
                  <div className="border-t themed-border my-1" />
                )}
                {[...groups.entries()].map(([name, entries]) => (
                  <div key={name}>
                    <div className="px-3 py-1 text-[10px] themed-text-muted truncate">{name}</div>
                    {entries.map((e, i) => (
                      <Tooltip key={i} always placement="top" className="flex items-center" content={e.sql}>
                        <button
                          onClick={() => { setSql(e.sql); setTimeout(() => textareaRef.current?.focus(), 0) }}
                          className="w-full text-left px-3 py-1 text-xs font-mono truncate hover:themed-bg-primary themed-text-muted opacity-60 hover:opacity-100 transition-all"
                        >
                          {e.sql}
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                ))}
              </>
            )
          })()}
        </div>
        <div className="border-t themed-border px-3 py-2 overflow-auto max-h-40">
          <div className="text-xs themed-text-muted mb-1">Tables</div>
          {schema.map(t => (
            <button
              key={t.name}
              onClick={() => {
                const query = adapter.type === 'firebird' ? `SELECT FIRST 100 * FROM "${t.name}";` : `SELECT * FROM "${t.name}" LIMIT 100;`
                insertAtCursor(sql.trim() ? `\n${query}` : query)
              }}
              className="block w-full text-left text-sm font-mono px-1 py-0.5 truncate hover:themed-bg-primary themed-text-muted"
              title={t.columns.map(c => `${c.name} ${c.type}`).join('\n')}
            >
              {t.name} <span className="opacity-40">({t.rowCount < 0 ? '…' : t.rowCount})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="h-9 flex items-center gap-2 px-3 themed-bg-secondary themed-border border-b shrink-0">
          <button
            data-sql-run
            onClick={runQuery}
            disabled={queryLoading || !sql.trim()}
            className="flex items-center gap-1 text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
          >
            <Play size={12} />
            Run
          </button>
          {sql.trim() && (
            <button
              onClick={() => { setSql(''); setResult(null); setStatusMsg(''); textareaRef.current?.focus() }}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded themed-btn-secondary"
            >
              <Eraser size={12} />
              Clear
            </button>
          )}
          <div className="flex-1" />

          {/* Export */}
          {canCopy && result && result.rows.length > 0 && (
            <div className="flex items-center gap-1">
              <Tooltip always placement="top" className="flex items-center" content={`Copy selection (${formatShortcut({ key: 'c', mod: true })})`}>
                <button
                  onClick={copySqlSelection}
                  disabled={!sqlSelectionRef.current}
                  className="themed-btn-secondary text-xs px-1.5 py-0.5 rounded disabled:opacity-30"
                >
                  <Copy size={13} />
                </button>
              </Tooltip>
              <ExportDropdown columns={result.columns} rows={result.rows} types={result.types} onExportToDrive={onExportToDrive} parentId={parentId} baseName={`${fileName?.replace(/\.[^.]+$/, '') || 'db'}_query`} />
            </div>
          )}

          {statusMsg && <span className="text-xs themed-text-muted truncate max-w-48">{statusMsg}</span>}
        </div>

        {/* SQL editor */}
        <div className="flex flex-col gap-1 p-2 themed-bg-secondary themed-border border-b">
          <div className="relative">
            <pre
              ref={highlightRef}
              className="absolute inset-0 text-sm font-mono p-2 overflow-hidden pointer-events-none whitespace-pre-wrap break-words"
              aria-hidden
              dangerouslySetInnerHTML={{ __html: highlightSQL(sql) + '\n' }}
            />
            <textarea
              ref={textareaRef}
              value={sql}
              onChange={e => setSql(e.target.value)}
              onInput={e => setSql((e.target as HTMLTextAreaElement).value)}
              onKeyDown={handleKeyDown}
              onScroll={syncScroll}
              placeholder={adapter.type === 'firebird' ? "SELECT FIRST 100 * FROM table_name" : "SELECT * FROM table_name LIMIT 100"}
              className="w-full h-24 text-sm font-mono themed-text p-2 resize-y outline-none bg-transparent caret-white relative z-10"
              style={{ color: 'transparent', caretColor: '#e4e4e7' }}
              spellCheck={false}
            />
          </div>
          <span className="text-[10px] themed-text-muted">{formatShortcut({ key: 'Enter', mod: true })} to run</span>
        </div>

        {/* Results grid */}
        <div className="flex-1 relative">
          {queryLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}

          {result?.error ? (
            <div className="p-4 text-red-400 text-sm font-mono whitespace-pre-wrap">{result.error}</div>
          ) : result && result.rows.length > 0 ? (
            <AgGridReact
              ref={sqlGridRef}
              theme={readonlyTheme}
              columnDefs={columnDefs}
              rowData={result.rows}
              headerHeight={32}
              rowHeight={28}
              suppressMovableColumns
              suppressCellFocus
              preventDefaultOnContextMenu
              onCellContextMenu={(e) => {
                const me = e.event as MouseEvent
                me.preventDefault()
                const value = String(e.value ?? '')
                const colId = e.column?.getColId() || ''
                setSqlCtxMenu({ x: me.clientX, y: me.clientY, value, colId })
              }}
              onCellMouseDown={(e) => {
                if ((e.event as MouseEvent)?.button === 2) return
                const rowIdx = e.node.rowIndex ?? -1
                const colId = e.column.getColId()
                const isShift = (e.event as MouseEvent)?.shiftKey
                const cols = result.columns
                // Click on row number → select entire row
                if (colId === '__rownum' || !cols.includes(colId)) {
                  if (rowIdx < 0) return
                  if (isShift && sqlSelectionRef.current) {
                    sqlSelectionRef.current = { ...sqlSelectionRef.current, startCol: 0, endRow: rowIdx, endCol: cols.length - 1 }
                  } else {
                    sqlSelectionRef.current = { startRow: rowIdx, startCol: 0, endRow: rowIdx, endCol: cols.length - 1 }
                  }
                  setSqlSelVer(v => v + 1)
                  return
                }
                const colIdx = cols.indexOf(colId)
                if (rowIdx < 0 || colIdx < 0) return
                if (isShift && sqlSelectionRef.current) {
                  sqlSelectionRef.current = { ...sqlSelectionRef.current, endRow: rowIdx, endCol: colIdx }
                } else {
                  sqlSelectionRef.current = { startRow: rowIdx, startCol: colIdx, endRow: rowIdx, endCol: colIdx }
                }
                setSqlSelVer(v => v + 1)
              }}
              onCellMouseOver={(e) => {
                if (!sqlSelectionRef.current || !(e.event as MouseEvent)?.buttons) return
                const rowIdx = e.node.rowIndex ?? -1
                if (rowIdx < 0) return
                const colId = e.column.getColId()
                const cols = result.columns
                if (colId === '__rownum' || !cols.includes(colId)) {
                  sqlSelectionRef.current = { ...sqlSelectionRef.current, startCol: 0, endRow: rowIdx, endCol: cols.length - 1 }
                } else {
                  const colIdx = cols.indexOf(colId)
                  if (colIdx < 0) return
                  sqlSelectionRef.current = { ...sqlSelectionRef.current, endRow: rowIdx, endCol: colIdx }
                }
                setSqlSelVer(v => v + 1)
              }}
              defaultColDef={{
                cellClass: (params: any) => {
                  const range = getSqlCellRange()
                  if (!range || !result) return ''
                  const rowIdx = params.node?.rowIndex ?? -1
                  const colIdx = result.columns.indexOf(params.column.getColId())
                  if (rowIdx >= range.rowStart && rowIdx <= range.rowEnd && colIdx >= range.colStart && colIdx <= range.colEnd) {
                    return 'db-cell-selected'
                  }
                  return ''
                },
              }}
            />
          ) : result ? (
            <div className="p-4 themed-text-muted text-sm">
              {result.changes != null && result.changes > 0
                ? `${result.changes} row(s) affected`
                : 'No rows returned'}
            </div>
          ) : (
            <div className="p-4 themed-text-muted text-sm">Write a query and press Run</div>
          )}
        </div>

        {/* SQL result context menu */}
        {sqlCtxMenu && createPortal(
          <div
            ref={sqlCtxMenuRef}
            className={`fixed themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[160px] ${sqlThemeClass}`}
            style={{ left: sqlCtxMenu.x, top: sqlCtxMenu.y }}
          >
            {canCopy && (
              <button
                onClick={() => { copySqlSelection(); setSqlCtxMenu(null) }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                <Copy size={14} />
                Copy
                <span className="ml-auto text-[11px] themed-text-muted">{formatShortcut({ key: 'c', mod: true })}</span>
              </button>
            )}
          </div>,
          document.body,
        )}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// DatabaseEditor — wrapper
// ---------------------------------------------------------------------------

interface DatabaseEditorProps {
  fileId: string
  fileName?: string
  parentId?: string | null
  versionId?: string
  readOnly?: boolean
  canCopy?: boolean
  isActive?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.RefObject<(() => void) | null>
  onDiscardRef?: React.RefObject<(() => void) | null>
  /** Claim the app's undo scope so Ctrl+Z can't fall through to the
   *  drive's file-operation stack. See useUndoScopeGuard. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  onExportToDrive?: (blob: Blob, suggestedName: string, defaultFolderId?: string | null, title?: string) => void
  viewSync?: import('./viewSync.js').ViewSyncRef
  adapter?: DbAdapter
  /** #897 — where this editor is open (`ws:<workspace>`, `single`, `chat`), so
   *  its unsaved work (typed cells, staged rows, Edit mode) survives a
   *  phone/desktop layout swap. Absent = not kept (the share view, previews). */
  draftScope?: string
}

/** Credential dialog for databases that require authentication */
function DbCredentialDialog({ onSubmit, onCancel, error, loading }: {
  onSubmit: (user: string, password: string, rememberMe: boolean) => void
  onCancel?: () => void
  error?: string | null
  loading?: boolean
}) {
  const [user, setUser] = useState('SYSDBA')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (user.trim() && password) onSubmit(user.trim(), password, rememberMe)
  }

  return (
    <div className="flex-1 flex items-center justify-center">
      <form onSubmit={handleSubmit} className="themed-bg-secondary border themed-border rounded-lg p-6 w-80 shadow-xl">
        <h3 className="text-sm font-medium themed-text mb-4 flex items-center gap-2">
          <Lock size={14} />
          Database Login
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase themed-text-muted block mb-1">Username</label>
            <input
              type="text"
              value={user}
              onChange={e => setUser(e.target.value)}
              autoComplete="username"
              className="w-full text-sm themed-bg-tertiary themed-text rounded px-2.5 py-1.5 border themed-border outline-none focus:border-brand-500"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase themed-text-muted block mb-1">Password</label>
            <PasswordInput
              ref={passwordRef}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full text-sm themed-bg-tertiary themed-text rounded px-2.5 py-1.5 border themed-border outline-none focus:border-brand-500"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="rounded" />
            <span className="text-xs themed-text-secondary">Remember me</span>
          </label>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            {onCancel && (
              <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded">
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={!user.trim() || !password || loading}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded disabled:opacity-50 flex items-center gap-1.5"
            >
              {loading && <Loader2 size={12} className="animate-spin" />}
              Connect
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

/**
 * I14: tell the person when a change landed in the db but its VERSION could not be recorded —
 * the adapter's background retries gave up (every retry used, or refused, e.g. a full drive).
 * The change is safe; only its history entry is missing until the db's next recorded save carries
 * it (each version is the whole file — E7 PR 2 §3.2, D4). One warning per burst: a bulk edit's writes report together.
 *
 * #1101: also the SERVER's word that a background db operation of this person's ended (`DB_OPERATION_ENDED_EVENT`, a
 * per-user realtime frame — a large db's version save that recorded nothing): the change is in the db and NO version of
 * it was saved. Same burst rule; the bell keeps a notification for someone who has left the editor.
 */
export function useVersionUnsavedNotice(fileId: string): void {
  const toast = useToastOptional()
  const { t } = useTranslation('collab')
  const lastShownAt = useRef(0)
  useEffect(() => {
    const show = (key: string, tone: 'success' | 'warning' = 'warning') => {
      const now = Date.now()
      if (now - lastShownAt.current < 10_000) return
      lastShownAt.current = now
      if (tone === 'success') toast?.success(t(key), 10_000)
      else toast?.warning(t(key), 10_000)
    }
    const onUnsaved = (e: Event) => {
      const detail = (e as CustomEvent<{ fileId?: string; reason?: DbVersionUnsavedReason }>).detail
      const target = detail?.fileId
      if (target && target !== fileId) return
      // H15 W6: a db that stayed in use (possibly a sandbox's lock) is not "the drive may be full".
      // E7 PR 2 slice 2: superseded by a newer drive version — the change is NOT kept for a later save (#1101's copy).
      show(detail?.reason === 'in-use' ? 'database.versionUnsaved.inUse'
        : detail?.reason === 'superseded' ? 'database.operationEnded.versionSave.notSaved.superseded'
        : 'database.versionUnsaved.generic')
    }
    const onOperationEnded = (e: Event) => {
      const op = (e as CustomEvent<DbOperationEnded>).detail
      if (!op || op.fileId !== fileId) return
      const key = dbOperationNoticeKey(op)
      if (key) show(key, dbOperationNoticeTone(op))
    }
    window.addEventListener(DB_VERSION_UNSAVED_EVENT, onUnsaved)
    window.addEventListener(DB_OPERATION_ENDED_EVENT, onOperationEnded)
    return () => {
      window.removeEventListener(DB_VERSION_UNSAVED_EVENT, onUnsaved)
      window.removeEventListener(DB_OPERATION_ENDED_EVENT, onOperationEnded)
    }
  }, [fileId, toast, t])
}

export const DatabaseEditor = forwardRef<EditorEditHandle, DatabaseEditorProps>(function DatabaseEditor({ fileId, fileName, parentId, versionId, readOnly, canCopy = true, isActive, onDirtyChange, onSaveRef, onDiscardRef, registerUndoRedo, onExportToDrive, viewSync, adapter = sqliteAdapter, draftScope }, ref) {
  useVersionUnsavedNotice(fileId)
  // Ctrl+Z and the Edit menu, wired to the grid's STAGED changes.
  //
  // Registered HERE rather than in the grid so that flipping to SQL mode
  // cannot tear it down and drop the keystroke back onto the drive's file
  // history — which is the bug the parent branch exists to fix.
  //
  // `editable: true` claims the keystroke even when there is nothing staged.
  // An editor that registers nothing reads as a viewer, and Ctrl+Z over an
  // open database would then restore a file deleted in the drive minutes ago.
  // With nothing staged it simply does nothing and the menu greys out.


  // #897 — the editor's unsaved work is kept across a phone/desktop layout swap
  // under its place + file + version: the mode it is in, the staged rows (with
  // their undo history), and — in the grid — the typed cells.
  const hostScope = useDraftScope()
  const place = draftScope ?? hostScope
  const draftKey = place && !readOnly ? `db:${place}:${fileId}:${versionId ?? ''}` : null
  const [mode, setMode] = useDraftState<'view' | 'edit' | 'sql'>(draftKey && `${draftKey}:mode`, 'view')

  const edits = useGridEdits(draftKey && `${draftKey}:edits`)

  useEffect(() => {
    if (readOnly || !registerUndoRedo) return
    return registerUndoRedo({
      // Claims the keystroke even with nothing staged. An editor that
      // registers nothing reads as a viewer, and Ctrl+Z over an open database
      // would restore a file deleted in the drive minutes ago. With nothing
      // staged it does nothing and the Edit menu greys out.
      editable: true,
      // SQL mode has no staged set of its own and the grid behind it is
      // hidden, so answering there would move something nobody can see.
      undo: async () => mode !== 'sql' && edits.undo(),
      redo: async () => mode !== 'sql' && edits.redo(),
      onStateChange: (cb) => {
        cb({
          canUndo: mode !== 'sql' && edits.canUndo,
          canRedo: mode !== 'sql' && edits.canRedo,
        })
        return () => {}
      },
    })
  }, [readOnly, registerUndoRedo, edits, mode])

  // Auth state — for adapters that require authentication (e.g., Firebird)
  // Cookie-based: server sets httpOnly cookie on authenticate, cleared on logout
  const [authenticated, setAuthenticated] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(false)
  const needsAuth = adapter.requiresAuth && !authenticated

  const handleAuth = useCallback(async (user: string, password: string, rememberMe: boolean) => {
    if (!adapter.authenticate) return
    setAuthLoading(true)
    setAuthError(null)
    try {
      await adapter.authenticate(fileId, user, password, rememberMe)
      setAuthenticated(true)
    } catch (err: any) {
      const text = errorText(err, 'Authentication failed')
      if (text !== null) setAuthError(text)
    } finally {
      setAuthLoading(false)
    }
  }, [adapter, fileId])

  const handleLogout = useCallback(async () => {
    if (!adapter.logout) return
    await adapter.logout(fileId)
    setAuthenticated(false)
  }, [adapter, fileId])

  // On mount, try fetching schema to check if cookie session is still valid
  const [cookieChecked, setCookieChecked] = useState(!adapter.requiresAuth)
  useEffect(() => {
    if (!adapter.requiresAuth || cookieChecked) return
    setCookieChecked(true)
    // Try a lightweight request — if cookie is valid, it'll succeed
    adapter.getSchema(fileId).then(() => setAuthenticated(true)).catch(() => {})
  }, [adapter, fileId, cookieChecked])

  const { schema, loading, error, refreshSchema, initialLoading } = useDbSchema(fileId, adapter, versionId, authenticated)
  const sqlRef = useRef<DatabaseSqlHandle>(null)
  // Declarative AI/user SQL apply. set_sql / navigate / streamed
  // preview / "send to SQL" set this; DatabaseSqlModifier applies it via
  // an effect keyed on `n`. Replaces a 50 ms setTimeout race on
  // sqlRef.current — which is null on a cold post-reload render (the
  // db_editor reload-recovery re-run fires before the SQL sub-editor's
  // imperative handle has committed), so the SQL was silently dropped
  // while the tool reported success. No timing dependency now: the
  // modifier is always mounted (when !versionId) and its effect runs
  // whenever `n` changes — including on its own mount with the latest
  // value if it mounted after this was set.
  const aiSqlNonce = useRef(0)
  const [aiSql, setAiSql] = useState<{ sql: string; n: number; autoRun: boolean } | null>(null)
  const pushAiSql = useCallback((sql: string, autoRun = false) => {
    aiSqlNonce.current += 1
    setAiSql({ sql, n: aiSqlNonce.current, autoRun })
  }, [])
  const tableRef = useRef<DatabaseTableHandle>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [advancedFilter, setAdvancedFilter] = useState(false)

  // Forward EditorEditHandle to the active table modifier
  useImperativeHandle(ref, () => ({
    copy: () => tableRef.current?.copy(),
    cut: () => {},
    paste: () => {},
    delete: () => tableRef.current?.delete(),
  }), [])

  // Set editor:db:grid scope immediately on mount (overrides TabContent's 'editor').
  // Also re-set on mousedown for when user switches between explorer and editor.
  const [dbEditorEl, setDbEditorEl] = useState<HTMLDivElement | null>(null)
  // This viewer's key in the agent's editor registry (#352) — see the registration below.
  const registryKey = `dbe:${useId()}`
  const dbEditorCallbackRef = useCallback((el: HTMLDivElement | null) => setDbEditorEl(el), [])
  const cmdRegistryForScope = useCommandRegistryOptional()
  useEffect(() => {
    if (!cmdRegistryForScope) return
    // Set scope when this DB tab becomes active.
    // Use requestAnimationFrame to fire AFTER TabContent's useEffect
    // which sets 'editor' — we need to override it with the DB-specific scope.
    if (isActive) {
      const raf = requestAnimationFrame(() => {
        console.log('[DB EDITOR] setActiveScope for', fileId, 'isActive:', isActive)
        cmdRegistryForScope.setActiveScope(dbGridScope(fileId, versionId).scopeId)
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [isActive, fileId, versionId, cmdRegistryForScope])
  useEffect(() => {
    if (!dbEditorEl || !cmdRegistryForScope) return
    const handler = () => cmdRegistryForScope.setActiveScope(dbGridScope(fileId, versionId).scopeId)
    dbEditorEl.addEventListener('mousedown', handler, true)
    return () => dbEditorEl.removeEventListener('mousedown', handler, true)
  }, [dbEditorEl, cmdRegistryForScope, fileId, versionId])

  const handleSendToSql = useCallback((sql: string) => {
    setMode('sql')
    pushAiSql(sql)
  }, [pushAiSql])

  // Listen for AI-dispatched editor actions
  useEffect(() => {
    /** Resolve table schema columns — used for validation */
    const getTableColumns = (tableName?: string) => {
      if (!tableName || !schema) return []
      const t = schema.find(s => s.name === tableName)
      return t?.columns ?? []
    }

    /** Validate column names against schema. Returns invalid names or empty array. */
    const validateColumns = (cols: string[], tableName?: string): string[] => {
      const valid = new Set(getTableColumns(tableName).map(c => c.name))
      if (valid.size === 0) return [] // no schema info — skip validation
      return cols.filter(c => !valid.has(c))
    }

    // Editor mutation + result, NO transport. Called by the
    // `papan:client-tool-request` handler below; the result is delivered
    // back via the AG-UI tool-result re-POST owned by the runtime.
    const applyEditorAction = (
      action: string,
      payload: Record<string, unknown>,
    ): { success: boolean; data?: Record<string, unknown>; error?: string } => {
      let success = false
      let data: Record<string, unknown> | undefined
      let error: string | undefined

      try {
        switch (action) {
          case 'inspect': {
            const tableState = tableRef.current?.getState?.()
            const currentTable = tableState?.table
            const tableCols = getTableColumns(currentTable)
            success = true
            data = {
              mode,
              table: currentTable ?? null,
              tableColumns: tableCols.map(c => ({ name: c.name, type: c.type, pk: c.pk })),
              visibleColumns: tableState?.columns ?? [],
              where: tableState?.where ?? '',
              limit: tableState?.limit ?? 100,
              page: tableState?.page ?? 0,
              selectedRange: tableState?.selectedRange ?? null,
              // What is on screen but NOT in the database yet. Without it an
              // agent describes the table as the server has it while the user
              // is looking at rows struck through for deletion and rows that
              // do not exist there at all.
              staged: tableState?.staged ?? { deleteRowIds: [], insertCount: 0 },
              tables: schema?.map(t => t.name) ?? [],
              readOnly: readOnly || !!versionId,
            }
            break
          }

          case 'navigate': {
            const m = payload.mode as string | undefined
            const table = payload.table as string | undefined
            const cols = payload.columns as string[] | undefined
            const where = payload.where as string | undefined
            const sql = payload.sql as string | undefined

            // Validate mode
            if (m && !['view', 'edit', 'sql'].includes(m)) { error = `Invalid mode "${m}". Use: view, edit, sql`; break }
            if (m === 'edit' && (readOnly || versionId)) { error = 'Cannot switch to edit mode — read-only permission'; break }

            // Validate table exists
            if (table && !schema?.some(t => t.name === table)) {
              const available = schema?.map(t => t.name).join(', ') ?? 'none'
              error = `Table "${table}" not found. Available tables: ${available}`
              break
            }

            // Validate columns exist in target table
            const targetTableName = table || tableRef.current?.getState?.()?.table
            if (cols && cols.length > 0) {
              const invalid = validateColumns(cols, targetTableName)
              if (invalid.length > 0) {
                const validCols = getTableColumns(targetTableName).map(c => c.name).join(', ')
                error = `Invalid column(s): ${invalid.join(', ')}. Available: ${validCols}`
                break
              }
            }

            // Apply changes
            if (m) setMode(m as 'view' | 'edit' | 'sql')
            if (table) window.dispatchEvent(new CustomEvent('papan:db-set-table', { detail: { fileId, table } }))
            if (cols) window.dispatchEvent(new CustomEvent('papan:db-set-columns', { detail: { fileId, columns: cols } }))
            if (where !== undefined) {
              window.dispatchEvent(new CustomEvent('papan:db-set-filter', { detail: { fileId, filter: where } }))
              // Open the filter panel in the parent so user sees the applied filter
              if (where) { setFilterOpen(true); setAdvancedFilter(true) }
            }
            if ((m === 'sql' || (!m && mode === 'sql')) && sql) {
              pushAiSql(sql, !!payload.autoRun)
            }

            success = true
            const tableCols = getTableColumns(targetTableName).map(c => c.name)
            data = { mode: m || mode, table: targetTableName, columns: cols, where, tableColumns: tableCols }
            break
          }

          case 'set_columns': {
            const cols = payload.columns as string[] ?? []
            const currentTable = tableRef.current?.getState?.()?.table
            if (cols.length > 0) {
              const invalid = validateColumns(cols, currentTable)
              if (invalid.length > 0) {
                const validCols = getTableColumns(currentTable).map(c => c.name).join(', ')
                error = `Invalid column(s): ${invalid.join(', ')}. Available: ${validCols}`
                break
              }
            }
            window.dispatchEvent(new CustomEvent('papan:db-set-columns', { detail: { fileId, columns: cols } }))
            success = true
            data = { columns: cols }
            break
          }

          case 'set_filter': {
            const where = payload.where as string ?? ''
            window.dispatchEvent(new CustomEvent('papan:db-set-filter', { detail: { fileId, filter: where } }))
            if (where) { setFilterOpen(true); setAdvancedFilter(true) }
            success = true
            data = { where }
            break
          }

          case 'set_sql': {
            const sql = payload.sql as string
            if (!sql) { error = 'Missing sql parameter'; break }
            setMode('sql')
            pushAiSql(sql, !!payload.autoRun)
            success = true
            data = { mode: 'sql', sql }
            break
          }

          default:
            error = `Unknown action "${action}". Available: inspect, navigate, set_columns, set_filter, set_sql`
        }
      } catch (err: any) {
        error = `Editor action error: ${err?.message || 'unexpected failure'}`
        console.error('[DB Editor] AI action failed:', err)
      }

      return { success, data, error }
    }

    // Client-executed path (Design note I step 4). db_editor is a real
    // model tool the BROWSER runs: the registered handler in
    // features/agent agentTools.ts dispatches this request and awaits
    // the matching result; we apply + return the editor state and the
    // runtime re-POSTs it as an AG-UI tool-result. Exactly ONE viewer
    // answers: the one the request NAMES (`editor` — its workspace and
    // file, chosen by the handler from the turn the worker stamped, #352).
    // "Whichever is active" answered for another session: every open
    // workspace stays mounted, each with an active tab, and the recovery
    // path runs after an await. A request naming no editor (an older agent
    // bundle) keeps the old active-tab rule.
    const clientToolHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { requestId?: string; toolName?: string; args?: Record<string, unknown>; editor?: { key?: string } }
        | undefined
      if (!d?.requestId || d.toolName !== 'db_editor') return
      if (!editorActionIsMine(d, registryKey, !!isActive)) return
      const args = d.args ?? {}
      const action = typeof args.action === 'string' ? args.action : ''
      const result = applyEditorAction(action, args)
      window.dispatchEvent(
        new CustomEvent('papan:client-tool-result', { detail: { requestId: d.requestId, result } }),
      )
    }

    // Registry of OPEN db/fdb editors — EVERY mounted instance (inactive
    // tabs and hidden workspaces stay mounted), each with its file, active
    // flag and element. The client-executed db_editor handler picks the ONE
    // its call is bound to from it (agent `pickDbEditor`), or answers with
    // an accurate error (a deep link to switch to, or "not open"). Window
    // Map — decoupled cross-feature, same idiom as the events here.
    // Keyed per VIEWER instance, carrying its element and file: the same
    // file can be open twice at once (two workspaces, or a workspace's deck
    // and chat-home's side editor for that project) — keyed by file, one
    // overwrote the other, either's unmount removed both, and a request
    // naming the file would be answered by both.
    // The element, not a workspace id read now: the stack it sits in can be
    // re-marked without remounting the editor (chat-home's side editor
    // follows the conversation picked in the rail), so the reader resolves
    // the workspace when it asks (agentTools `dbEditorEntries`).
    type DbEntry = { fileId: string; fileName: string; active: boolean; el: HTMLElement | null }
    const w = window as unknown as { __papanDbEditors?: Map<string, DbEntry> }
    const dbEditors = (w.__papanDbEditors ??= new Map<string, DbEntry>())
    dbEditors.set(registryKey, { fileId, fileName: fileName ?? fileId, active: !!isActive, el: dbEditorEl })

    window.addEventListener('papan:client-tool-request', clientToolHandler)
    return () => {
      dbEditors.delete(registryKey)
      window.removeEventListener('papan:client-tool-request', clientToolHandler)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps — mode read from closure is fine (stale mode in inspect is non-critical)
  }, [isActive, fileId, schema, readOnly, versionId, dbEditorEl])

  // Live SQL preview: the agent's in-progress db_editor `set_sql`/`navigate`
  // args streamed in BEFORE the tool executes (AG-UI streamed tool args, the
  // same a CopilotKit renderer consumes). Transient — switch to SQL mode and
  // mirror the growing query into the editor for the "watch it type" effect.
  // NO reply, NO autoRun, NO validation: the authoritative papan:editor-action
  // (from tool execute, with id) still does the definitive apply + autoRun +
  // reply. Mirrors the authoritative set_sql idiom (mode → ref after mount).
  // Transient streamed-args preview — distinct from the authoritative
  // apply (pushAiSql/aiSql). It must render PROGRESSIVELY (one paint per
  // streamed delta = the "watch it type" effect), so it can't go through
  // the coalesced aiSql state (a burst of deltas in one SSE read task
  // would batch into a single render → no typewriter). The SQL modifier
  // is always mounted during a live stream (no cold-mount race here —
  // that race only affects the authoritative reload-recovery path), so
  // write straight to it via sqlRef, rAF-throttled: ≤1 setSql per frame,
  // always the latest value → a smooth typewriter with no magic
  // millisecond (rAF is the render-paced primitive for an animation).
  useEffect(() => {
    let raf = 0
    let latest: string | null = null
    const flush = () => {
      raf = 0
      if (latest != null) sqlRef.current?.setSql(latest)
    }
    const onPreview = (e: Event) => {
      // Bound like the executed call (#352): only the streaming session's
      // workspace (every open workspace stays mounted with its own active
      // tab), and — when the turn's chip named a database — only that file,
      // active tab or not; else that workspace's active database tab.
      const d = (e as CustomEvent).detail
      if (!streamPreviewIsMine(d ?? {}, { workspaceId: workspaceIdOf(dbEditorEl), fileId, active: !!isActive })) return
      const sql = (e as CustomEvent).detail?.payload?.sql
      if (typeof sql !== 'string' || !sql) return
      setMode('sql')
      latest = sql
      if (!raf) raf = requestAnimationFrame(flush)
    }
    window.addEventListener('papan:editor-action-preview', onPreview)
    return () => {
      window.removeEventListener('papan:editor-action-preview', onPreview)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [isActive, dbEditorEl, fileId])

  // Content state — auth, loading, error shown inside the layout (toolbar stays visible)
  const activeMode = versionId ? 'view' : readOnly ? (mode === 'sql' ? 'sql' : 'view') : mode

  const dbStatusLabel = needsAuth && !authLoading
    ? 'Not connected'
    : needsAuth || loading
      ? 'Connecting…'
      : error ? `Error — ${error}` : 'Connected'

  return (
    <div ref={dbEditorCallbackRef} className="flex-1 flex flex-col overflow-hidden">
      {/* Mode Toggle Bar */}
      <EditorToolbar
        mode={
          versionId ? (
            <span className="text-xs themed-text-muted flex items-center gap-1">
              <Lock size={14} /> Version preview
            </span>
          ) : (
            <ModeToggle
              options={[
                { value: 'view', label: 'View', icon: <Eye size={10} /> },
                ...(!readOnly ? [{ value: 'edit', label: 'Edit', icon: <Pencil size={10} /> } satisfies ModeOption<typeof mode>] : []),
                { value: 'sql', label: 'SQL', icon: <Play size={10} /> },
              ]}
              value={mode}
              onChange={setMode}
            />
          )
        }
        actions={activeMode !== 'sql' ? [
          {
            key: 'filter',
            node: (
              <>
                <ToolToggle
                  on={filterOpen && !advancedFilter}
                  icon={<Filter size={12} />}
                  onClick={() => {
                    if (filterOpen && !advancedFilter) setFilterOpen(false)
                    else { setFilterOpen(true); setAdvancedFilter(false) }
                  }}
                >
                  Filter
                </ToolToggle>
                <ToolToggle
                  on={filterOpen && advancedFilter}
                  icon={<Braces size={12} />}
                  className="font-mono"
                  onClick={() => {
                    if (filterOpen && advancedFilter) setFilterOpen(false)
                    else { setFilterOpen(true); setAdvancedFilter(true) }
                  }}
                >
                  WHERE
                </ToolToggle>
              </>
            ),
          },
        ] : []}
        status={
          <>
            {adapter.requiresAuth && authenticated && (
              <ToolButton variant="danger" onClick={handleLogout} data-tooltip="Disconnect">
                Logout
              </ToolButton>
            )}
            {/* Icon-only DB connection state — the hint carries the words */}
            <div className="flex items-center themed-text-muted" data-tooltip={dbStatusLabel} aria-label={dbStatusLabel}>
              {needsAuth && !authLoading ? (
                <WifiOff size={14} className="themed-text-muted" />
              ) : needsAuth || loading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : error ? (
                <WifiOff size={14} className="text-red-500" />
              ) : (
                <Wifi size={14} className="text-green-500" />
              )}
            </div>
          </>
        }
      />

      {/* Auth / Loading / Error — shown inside layout so toolbar stays visible */}
      {needsAuth ? (
        authLoading && !authError ? (
          <div className="flex-1 flex items-center justify-center gap-2 themed-text-muted">
            <Loader2 className="animate-spin" size={18} />
            <span>Checking credentials...</span>
          </div>
        ) : (
          <DbCredentialDialog onSubmit={handleAuth} error={authError} loading={authLoading} />
        )
      ) : initialLoading ? (
        <div className="flex-1 flex items-center justify-center gap-2 themed-text-muted">
          <Loader2 className="animate-spin" size={18} />
          <span>Loading database...</span>
        </div>
      ) : !schema ? (
        // Only when there is nothing to show. A refresh that FAILS while a
        // schema is already loaded used to land here and tear the editor down
        // for the same reason the spinner did — and an offline blip is
        // precisely when a refresh fails. The error is on the connection dot.
        <div className="flex-1 flex items-center justify-center gap-2 text-red-400">
          <AlertTriangle size={18} />
          <span>{error || 'Failed to load database'}</span>
        </div>
      ) : <>

      {/* Table modifier (View/Edit) — hidden but mounted when in SQL mode */}
      <div className={`flex-1 flex flex-col overflow-hidden ${activeMode === 'sql' ? 'hidden' : ''}`}>
        <DatabaseTableModifier
          ref={tableRef}
          edits={edits}
          fileId={fileId}
          fileName={fileName}
          schema={schema}
          editable={activeMode === 'edit'}
          sqlMode={activeMode === 'sql'}
          versionId={versionId}
          refreshSchema={refreshSchema}
          onSendToSql={handleSendToSql}
          filterOpen={filterOpen}
          advancedFilter={advancedFilter}
          onDirtyChange={onDirtyChange}
          onSaveRef={onSaveRef}
          onDiscardRef={onDiscardRef}
          parentId={parentId}
          canCopy={canCopy}
          onExportToDrive={onExportToDrive}
          viewSync={viewSync}
          viewSyncId={viewSync ? `${fileId}:db` : undefined}
          adapter={adapter}
          onRequestEdit={!readOnly && !versionId ? () => setMode('edit') : undefined}
          draftKey={draftKey}
          isActive={isActive !== false}
          keyboardRegion={dbEditorEl}
        />
      </div>

      {/* SQL modifier — hidden but mounted when in View/Edit mode */}
      {!versionId && (
        <div className={`flex-1 flex flex-col overflow-hidden ${activeMode !== 'sql' ? 'hidden' : ''}`}>
          <DatabaseSqlModifier
            ref={sqlRef}
            fileId={fileId}
            fileName={fileName}
            schema={schema}
            versionId={versionId}
            parentId={parentId}
            canCopy={canCopy}
            onExportToDrive={onExportToDrive}
            adapter={adapter}
            aiSql={aiSql}
          />
        </div>
      )}
      </>}
    </div>
  )
})

// Export DatabaseModifier for preview registration (readonly table view)
export function DatabaseModifier({ fileId, fileName: _fileName, versionId, viewSync, adapter = sqliteAdapter }: { fileId: string; fileName?: string; versionId?: string; viewSync?: import('./viewSync.js').ViewSyncRef; adapter?: DbAdapter }) {
  const { schema, error, refreshSchema, initialLoading } = useDbSchema(fileId, adapter, versionId)
  // Read-only preview: nothing can be staged here, but the grid takes the
  // handle unconditionally rather than defending against its absence at every
  // read.
  const edits = useGridEdits()

  if (initialLoading) {
    return (
      <div className="flex-1 flex items-center justify-center gap-2 themed-text-muted">
        <Loader2 className="animate-spin" size={18} />
        <span>Loading database...</span>
      </div>
    )
  }

  // Only when there is nothing to show — see the main editor's gate.
  if (!schema) {
    return (
      <div className="flex-1 flex items-center justify-center gap-2 text-red-400">
        <AlertTriangle size={18} />
        <span>{error || 'Failed to load database'}</span>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* A refresh that FAILS no longer replaces the preview — it keeps
          showing the schema it already has, which is the fix. But this
          surface has no connection dot and no toolbar, so without this strip
          a failed refresh would be completely silent here. */}
      {error && (
        <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-amber-400 bg-amber-950/40 border-b border-amber-900/50 shrink-0">
          <AlertTriangle size={12} className="shrink-0" />
          <span className="truncate">Could not refresh — showing the last loaded version. {error}</span>
        </div>
      )}
      <DatabaseTableModifier
        fileId={fileId}
        schema={schema}
        editable={false}
        versionId={versionId}
        refreshSchema={refreshSchema}
        edits={edits}
        viewSync={viewSync}
        viewSyncId={viewSync ? `${fileId}:db` : undefined}
        adapter={adapter}
      />
    </div>
  )
}
